import type { Application, Container, Texture } from 'pixi.js';
import {
  Container as PixiContainer,
  Graphics,
  RenderLayer,
  type Sprite,
} from 'pixi.js';
import { COMBAT_STANCE_CLIP_IDS, isGhostBody } from '../sim/content';
import { TILE_HALF, Z_STEP, tileToScreen, type ScreenPoint } from '../sim/projection';
import { Layer, sortByDepthInPlace, type Renderable } from '../sim/depth';
import { Direction } from '../sim/direction';
import type { MobileState } from '../sim/mobile';
import type { Entity, EntityId } from '../sim/entity';
import { doorArtId, staticTileName, doorDrawCell } from '../sim/doorModel';
import type { World } from '../sim/world';
import { PlayerSprite, NAME_COLOR_CRIMINAL, type SpriteLayer } from './playerSprite';
import { HordeSprite } from './hordeSprite';
import { DiceSprite } from './diceSprite';
import { MoongateSprite } from './moongateSprite';
import { ProjectileSprite, projectileArtRotation } from './projectileSprite';
import { XpOrbLayer, type XpOrbCadence, type XpOrbSprite } from './xpOrbSprite';
import { HealthGlobeSprite } from './healthGlobeSprite';
import { VacuumSprite } from './vacuumSprite';
import { type BodyFrames, type EquipFrames, DEFAULT_BODY } from './playerArt';
import { type SpriteAtlasStore, type FramesLoadListener } from './spriteAtlasStore';
import {
  buildPlayerLayers,
  type PlayerLook,
  type RiderHeightOverride,
} from './buildPlayerLayers';
import type { HueTable } from './hueTable';
import { configureUoHueBatch, UoHueSprite } from './uoHueBatch';
import {
  StaticScene,
  type StaticBillboard,
  type StaticPlacement,
} from './staticsLayer';
import type { StaticTextures } from './staticsArt';
import type { LightTextures } from './lightArt';
import { roofStructures, isEntityOnHiddenRoof, type RoofStructure } from './roofHiding';
import { OUTSIDE, regionIndex } from './regions';
import { inBounds, terrainCullMargin, visibleTileBounds, type TileBounds } from './viewportCull';
import { pickGroundTile } from './groundPick';
import type {
  ProceduralTerrain,
  TerrainTile,
  TerrainTileInfo,
  WindowedTerrain,
} from './terrainLayer';
import { AutowalkOverlay } from './autowalkOverlay';
import { TelegraphOverlay } from './telegraphOverlay';
import { DungeonTelegraphOverlay } from './dungeonTelegraphOverlay';
import type { FootprintAnchor } from './abilityFootprint';
import type { AbilityShape } from '../sim/combat/abilityCatalog';
import { BloodLayer, type SplatRoll } from './bloodLayer';
import type { BloodTextures } from './bloodArt';
import { ArenaFxOverlay, BOSS_BURST_PARTICLES } from './arenaFx';
import { aggregateDamageFloats, planVolleyFx, prioritizePlannedFx } from './volleyFx';
import { combatFloatSeed, type CombatFloatPunch } from './combatText';
import type { EffectArt } from './effectsArt';
import type { ClientPerformanceMonitor } from '../performance/clientPerformance';
import type {
  PresentationQualityTier,
  PresentationWorkload,
} from '../performance/clientPerformance';
import {
  admitArenaFxEvents,
  presentationBudgetFor,
} from '../performance/arenaPresentationBudget';
import { isArenaBossBody, type ArenaFxEvent } from '../sim/arena';
import { HitFlash, hitFlashLevel, HIT_FLASH_LEVELS } from './hitFlash';
import { DungeonLighting, type DungeonStaticLight } from './dungeonLighting';
import type { EffectId, GroundEffectId, ImpactEffectId, ProjectileEffectId } from '../sim/content';
import { projectileRemovalWasContact } from '../sim/projectile';
import { arenaMagnetRadius } from '../sim/xpGem';
import type { DungeonChargeContact } from '../sim/combat/abilityShapes';
import {
  DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
  DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES,
  DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
  dungeonRushProgress,
  sweepDungeonDisplacement,
} from '../sim/dungeonMovement';
import { OVERLAY_Z } from './tilePinnedOverlay';

/**
 * Sound seams into one volley's staged FX show (ADR-0187): the renderer owns the
 * *instants* (it already schedules every flight/impact/float on the overlay's
 * scheduler), the caller owns the *sound decisions* and their throttles - the
 * observer/cue split. `onEffect` fires per projectile launch and per
 * explosion/flame-strike landing with the effect id; `onHit` fires per struck
 * entity at its damage float's instant. The renderer-owned hit reactions (the
 * white {@link HitFlash} + stagger hold, PRD #430 Slice A) fire at the same
 * staged instant but internally - they act on renderer-owned objects, so they
 * are not part of this caller seam.
 */
export interface VolleyFxHooks {
  onEffect?: (effect: EffectId) => void;
  /** Fired per struck entity at its damage float. `viaEffect` names the
   * projectile that struck when it should sound a physical hit (the archer's
   * arrow/bolt thwack, ADR-0237); absent for splash/zone/magic hits. */
  onHit?: (target: EntityId, viaEffect?: EffectId) => void;
}
import { ScreenShake } from './screenShake';
import { ContinuousPositionAnimator } from './continuousPositionAnimator';
import { trainingLoopSchedule } from './trainingLoopSchedule';
import { StepAnimator, STAGGER_MS, type ObservedPose } from './stepAnimator';
import { straddledTiles } from './straddle';

/**
 * zIndex given to the ground layer. The terrain is one monolithic container that
 * always paints behind every static and every mobile: they stand *on* the
 * ground, so it never needs to interleave with them. The sorted renderables
 * (statics + mobiles) take zIndex 0..n above this.
 */
const TERRAIN_Z = -1;

/** Batched ground gems paint over floor decals and below every depth-ranked entity. */
const XP_GROUND_Z = -0.25;

/** Collected gems fly over the battlefield on their way into the runner. */
const XP_ABSORB_Z = 998_000;

/** Visual-only forward travel for the warrior's auto-attack. */
const DUNGEON_LUNGE_PX = 12;

/** The lunge completes with the crescent sweep and never changes sim position. */
const DUNGEON_LUNGE_TTL_MS = 260;

interface DungeonLunge {
  unitX: number;
  unitY: number;
  ageMs: number;
}

const DUNGEON_WHIRLWIND_FACING_MS = 48;
const DUNGEON_WHIRLWIND_TTL_MS = DUNGEON_WHIRLWIND_FACING_MS * 8;

interface DungeonWhirlwind {
  startFacing: Direction;
  ageMs: number;
}

/** Charge presentation tuning, kept together for rapid feel iteration. */
export const DUNGEON_CHARGE_AFTERIMAGE_COUNT = 5;
export const DUNGEON_CHARGE_AFTERIMAGE_TTL_MS = 260;
export const DUNGEON_CHARGE_DUST_INTERVAL_MS = 45;
export const DUNGEON_CHARGE_TRAVEL_DUST_PARTICLES = 4;
export const DUNGEON_CHARGE_IMPACT_DUST_PARTICLES = 18;
export const DUNGEON_CHARGE_KNOCK_SLIDE_MS = 150;
export const DUNGEON_CHARGE_LAUNCH_SHAKE = 0.18;
export const DUNGEON_CHARGE_CONTACT_SHAKE = 0.55;
export const DUNGEON_CHARGE_PREVIEW_COLOR = 0xffd36a;
export const DUNGEON_CHARGE_PREVIEW_ARROW_LENGTH_PX = 12;
export const DUNGEON_CHARGE_PREVIEW_ARROW_HALF_WIDTH_PX = 4;
export const DUNGEON_CHARGE_PREVIEW_LINE_WIDTH_PX = 2;
export const DUNGEON_CHARGE_PREVIEW_LINE_ALPHA = 0.55;

interface DungeonChargeVisual {
  ageMs: number;
  durationMs: number;
  nextAfterimageMs: number;
  nextDustMs: number;
  afterimagesSpawned: number;
  contacts: {
    contact: DungeonChargeContact;
    progress: number;
    presented: boolean;
  }[];
}

interface DungeonAfterimage {
  sprite: Container;
  ageMs: number;
}

/**
 * Camera zoom clamp. The world container is scaled uniformly between these
 * bounds; 1 is the authored 1:1 view. Below `MIN_ZOOM` the dimetric art gets too
 * small to read, above `MAX_ZOOM` it turns to mush, so the wheel input saturates
 * here rather than zooming to either extreme (issue #67).
 */
const MIN_ZOOM = 0.5;
const MAX_ZOOM = 3;

/**
 * Per-wheel-notch zoom multiplier: one notch in multiplies the target zoom by
 * this, one notch out divides by it, so zoom is geometric (each notch feels the
 * same at any level) and clamped to [{@link MIN_ZOOM}, {@link MAX_ZOOM}].
 */
const ZOOM_STEP = 1.1;

/**
 * Exponential-smoothing rate (per millisecond) for the camera zoom lerp. Each
 * frame the live `zoom` eases toward `targetZoom` by `1 - exp(-rate * dt)`, which
 * is frame-rate independent: the same wall-clock easing whether the client runs
 * at 30 or 144 fps. ~0.015/ms is a ~67ms time constant - snappy but visibly
 * smooth, never the steppy jump of snapping straight to the target.
 */
const ZOOM_SMOOTH_PER_MS = 0.015;

/** Below this gap the zoom snaps to its target, ending the asymptotic crawl. */
const ZOOM_SNAP_EPSILON = 1e-3;

/**
 * Extra tiles kept on every side of the visible window when viewport-culling
 * (see {@link visibleTileBounds}). The ground-plane inverse alone would clip a
 * tall static whose foot tile sits just off-screen but whose art still reaches
 * into view: a static at `z=20` draws `20 * Z_STEP = 80px ~= 3.6` tiles up the
 * screen, so a margin of 6 covers the tallest authored statics plus a safety band
 * against rounding, while still pruning the vast majority of the 182x183 map.
 */
const CULL_MARGIN = 6;
/** Maximum classic light-mask reach expressed as dimetric tile steps. */
const LIGHT_CULL_MARGIN = 8;

/** ClassicUO centers a placed static's light mask on its projected tile anchor. */
export function positionStaticLight(
  out: ScreenPoint,
  tileX: number,
  tileY: number,
  tileZ: number,
): ScreenPoint {
  out.x = (tileX - tileY) * TILE_HALF;
  out.y = (tileX + tileY) * TILE_HALF - tileZ * Z_STEP;
  return out;
}

/** A depth-sortable scene item: its tile/layer plus how to stamp its resolved zIndex. */
type Sortable = Renderable & { apply: (z: number) => void };

/**
 * Whether two viewport-cull windows are identical (or both absent). The statics
 * membership diff, the per-static/per-terrain `inBounds` tests, and the terrain
 * `.visible` loop depend solely on the {@link TileBounds}; when it is unchanged
 * frame-to-frame the on-screen set and terrain visibility are already correct, so
 * the renderer can skip re-culling entirely and reuse the cached static sortables.
 */
function sameBounds(a: TileBounds | undefined, b: TileBounds | undefined): boolean {
  if (a === b) return true; // same reference, or both undefined (no camera)
  if (!a || !b) return false;
  return a.minX === b.minX && a.maxX === b.maxX && a.minY === b.minY && a.maxY === b.maxY;
}

/**
 * The length of one server tick in ms (the sim's fixed 50ms `STEP_MS`), used to turn a
 * broadcast's tick countdown into a wall-clock render duration - here the storm
 * telegraph's `ticksUntil` into the sparkle's lifetime (ADR-0152). Mirrors
 * `ui/eventBoard.EVENT_TICK_MS`; kept local so the render layer takes no board dependency.
 */
const SERVER_TICK_MS = 50;

/**
 * The classic UO grey notoriety hue (Rat Swarm Slice E, PRD #222 stories 6/7):
 * hue 0x3B2 is the grey the original client paints attackable/criminal mobiles
 * in, so a hovered rat floods this tone through the shared highlight filter and
 * the interaction reads as authentic UO targeting.
 */
export const GREY_NOTORIETY_HUE = 0x3b2;

/**
 * The blue duel-target hue (Player duels, PRD #252 story 3, ADR-0130): the duel
 * cursor tints a hovered target **blue** rather than the grey rat-notoriety tone,
 * so a duel call-out reads distinctly from a war-mode rat hunt while reusing the
 * exact same shared hue-filter seam. Hue 398 is the validated blue the chat
 * text-colour palette already uses (`src/chat.ts`), sampled from the same baked
 * ramp.
 */
export const BLUE_DUEL_HUE = 398;

/**
 * The **blaze** hue (UO 1161) the Scorched Earth trail's molten residue wears
 * (PRD #419 slice B, arena). The splat draws the red blood static art recoloured
 * through this hue's ramp - a real luminance->palette remap, the exact
 * {@link GREY_NOTORIETY_HUE}/{@link BLUE_DUEL_HUE} highlight-filter seam - rather
 * than the flat Pixi tint it used to wear. A tint *multiplies*, so it could only
 * darken the already-red art (it read as "still just blood"); the ramp actually
 * pushes it to fire, the way `bodyHue` recolours a body.
 */
export const BLAZE_HUE = 1161;

/**
 * The King's Justice smoke poof (PRD #361, ADR-0194): the one-shot impact FX's
 * footprint and tint. The radius sizes the baked `0x3728` strip to roughly one
 * tile (a body-height puff); the pale-smoke colour only matters for the particle
 * burst the FX degrades to when the effect is unbaked.
 */
const POOF_RADIUS_TILES = 0.9;
const POOF_COLOR = 0xbfc7cf;

/** The keystone-unlock flourish (PRD #378): golden, and long enough to savour. */
const KEYSTONE_FANFARE_MS = 1500;
const KEYSTONE_FANFARE_COLOR = 0xffd35c;

/**
 * How long a slain arena horde enemy's corpse lies on the ground, **client-side**
 * (ADR-0304). The server despawns the enemy entity right after its die clip
 * (`ARENA_ENEMY_DESPAWN_TICKS`); the renderer keeps the die-held sprite behind as
 * an inert corpse for this long - the VS-style carpet of bodies at zero server
 * cost (the old 10s server linger re-diffed hundreds of static corpse entities
 * into every arena delta at 10Hz). Matches the old server linger, so the arena
 * reads exactly as before.
 */
export const ARENA_CORPSE_TTL_MS = 10_000;
/** The tail of {@link ARENA_CORPSE_TTL_MS} over which an arena corpse fades out. */
const ARENA_CORPSE_FADE_MS = 1_000;

/**
 * How many spent horde sprites the renderer keeps warm (PRD #826).
 *
 * The pool only bridges the gap between one enemy's corpse expiring and the next
 * enemy spawning, so at a steady wave population it stays near empty; this bound
 * is the backstop for a wave that ends all at once, keeping a wipe from leaving
 * a whole wave's worth of sprites resident for the rest of the run.
 */
const HORDE_SPRITE_POOL_MAX = 64;

/**
 * How hard the XP gems animate at each presentation tier (PRD #826).
 *
 * A gem is decorative, and there can be well over a hundred of them lying on the
 * field at once, so they are the first per-frame cost to give up when the
 * governor sees frame pressure: the full tier keeps the drop, bob, and glow, the
 * reduced tier steps the same animation coarsely, and the constrained tier draws
 * them as still piles. The gem never changes what it is worth or when it is
 * collected - only how it moves.
 */
function xpOrbCadenceFor(tier: PresentationQualityTier): XpOrbCadence {
  if (tier === 'full') return 'animated';
  return tier === 'reduced' ? 'coarse' : 'static';
}

/**
 * The archer's ground-effect tints (PRD #446 Slice 1, ADR-0237): only the
 * *unbaked* Graphics degrade reads these (a baked strip paints its own art) -
 * poison a toxic green, caltrops a cold steel. Absent = the arena dealt colour
 * (fire fields keep their orange). Keyed by {@link GroundEffectId}.
 */
const ARENA_GROUND_FX_COLOR: Partial<Record<GroundEffectId, number>> = {
  'poison-field-ew': 0x5fbf3f,
  'poison-field-ns': 0x5fbf3f,
  caltrop: 0x9fb0c0,
};

/** The archer's status-tick blip colours (ADR-0237): a bleed spurts blood-red,
 * a poison tick sickly green, a slow a cold blue. */
const ARENA_STATUS_MARK_COLOR: Record<'bleed' | 'poison' | 'slow', number> = {
  bleed: 0xc01818,
  poison: 0x5fbf3f,
  slow: 0x7fa8d8,
};

/**
 * The level-up flourish (ADR-0218): the gold of the top XP-orb tier (a level-up
 * is what those orbs were for), radiating as staggered pulse rings off the
 * runner - one ring per fanfare beat, paced with the plink arpeggio's climb.
 */
const LEVEL_UP_FLOURISH_COLOR = 0xffd24d;
const LEVEL_UP_RING_COUNT = 3;
const LEVEL_UP_RING_STAGGER_MS = 110;
const LEVEL_UP_RING_RADIUS_TILES = 1.7;

/**
 * A basic straight shot's **impact** flash (PRD #399 Slice D): the traveling
 * projectile entity renders from its live position, and when it is removed from
 * the snapshot on a contact ({@link projectileRemovalWasContact}) this one-shot
 * `explosion` strip plays at its last-seen point, reusing the pooled impact
 * machinery verbatim. A max-range expiry fires nothing - the bolt just fades.
 * The radius sizes the strip to roughly one tile; the warm colour only shows in
 * the particle burst the effect degrades to when the strip is unbaked.
 */
const PROJECTILE_IMPACT_EFFECT: ImpactEffectId = 'explosion';
const PROJECTILE_IMPACT_RADIUS_TILES = 1;
const PROJECTILE_IMPACT_COLOR = 0xffaa33;

/**
 * How long an XP orb cluster's suck-in flight takes, ground point to runner.
 * Ease-in cubic: the first half reads as "attracted", the back half as the
 * suck - the whole dopamine beat lives in this curve.
 */
const XP_ABSORB_MS = 420;
/** Per-cluster stagger when a pile absorbs the same tick, so it pours in. */
const XP_ABSORB_STAGGER_MS = 45;
/** Cap on the pour stagger - a huge pile still finishes inside ~a third of a second. */
const XP_ABSORB_STAGGER_MAX_MS = 240;
/**
 * Slack (tiles) added to the effective magnet radius when classifying a gem
 * removal as suck-in vs cap-merge fade: generous because sim tiles are ~44px
 * isometrically and the runner keeps moving between snapshots. The screen-px
 * threshold is derived per frame from the runner's *effective* magnet radius
 * ({@link GameRenderer.xpAbsorbMaxPx}, which grows with the client's observed
 * `magnet` picks, PRD #431 Slice A) - a wrong guess is a cosmetic mislabel,
 * never wrong XP (ADR-0215).
 */
const XP_ABSORB_SLACK_TILES = 1.5;
/** How long a far (cap-merge) removal takes to fade out in place. */
const XP_FADE_MS = 250;
/**
 * The whole-field Vacuum inrush (PRD #431 Slice C): a far gem removed in the
 * same sweep as a claimed vacuum flies anyway (the one sanctioned exception to
 * ADR-0215's fade-far rule), its flight *lengthened* with start distance up to
 * this cap so the far edge of the field arrives as a sustained pour, not one
 * frame of chaos. The nearest gems keep the base {@link XP_ABSORB_MS}.
 */
const XP_VACUUM_ABSORB_MAX_MS = XP_ABSORB_MS * 2;
/** Screen distance (px) at which a vacuum-swept gem hits the full flight time /
 * stagger - beyond it, both clamp. ~a generous field radius on screen. */
const XP_VACUUM_DISTANCE_REF_PX = 520;
/** Per-px launch stagger for the vacuum inrush (nearest first), so 120 clusters
 * pour in as a spiral rather than all launching on one frame. */
const XP_VACUUM_STAGGER_MAX_MS = 700;
/** Sparkle particles per orb-cluster arrival (via the pooled arenaFx burst). */
const XP_ABSORB_BURST_PARTICLES = 5;

function clampZoom(z: number): number {
  return Math.min(MAX_ZOOM, Math.max(MIN_ZOOM, z));
}

/** A fractional tile position used for interpolation and depth keys. */
interface Tile {
  x: number;
  y: number;
}

/**
 * The local player's prediction-driven render pose (see
 * {@link GameRenderer.setLocalPose}): fractional tile + level + facing + gait,
 * everything {@link GameRenderer.sync} needs to draw the own sprite without
 * consulting its snapshot mobile.
 */
export interface LocalPose {
  x: number;
  y: number;
  z: number;
  facing: Direction;
  isMoving: boolean;
  isRunning: boolean;
}

/**
 * Owns the Pixi scene graph and projects simulation state onto it each frame.
 * It reads from the {@link World} (never the reverse): {@link sync} walks the
 * world's renderable entities, draws each at its interpolated tile, and
 * depth-sorts the whole set.
 *
 * It is a *reconciling* renderer: it keeps a sprite pool keyed by
 * {@link EntityId} and, each frame, creates a sprite for every newly appeared
 * renderable entity and destroys the sprite of any that has left, so adding or
 * removing an entity in the sim (a player joining or leaving) is reflected in the
 * scene with no bespoke wiring. The scene is a single sortable world container
 * holding the ground terrain, the statics, and the pooled mobile sprites; each
 * frame {@link sync} re-derives every renderable's `zIndex` from the pure depth
 * rule ({@link sortByDepthInPlace}) applied to the full set, so a mobile sorts live
 * against the statics and against the other mobiles as it walks. The ground stays
 * pinned behind everything at {@link TERRAIN_Z}. Position comes from
 * `tileToScreen`; the depth rule owns only draw *order*.
 *
 * The caller supplies the ground `terrain` container and the `statics`
 * billboards (both built from the map + baked art) plus the shared mobile
 * `playerFrames` and the baked `hueTable`; the renderer owns the terrain, the
 * statics, the mobile sprite pool, and the sort.
 */
/**
 * How long a hurt dungeon monster keeps its overhead bar after its last hit,
 * and how many may be lit at once (PRD #793).
 */
export const DUNGEON_HEALTH_BAR_MS = 3_000;
export const DUNGEON_HEALTH_BAR_BUDGET = 12;

/**
 * What each rarity's ground label is drawn in (PRD #795).
 *
 * The parchment default stays Common, so nothing about an ordinary drop
 * changes; Magic and Rare are the two colours a player learns once. The word is
 * on the tooltip as well as in the colour, so the readability does not depend
 * on distinguishing blue from yellow.
 */
const DUNGEON_LOOT_RARITY_COLORS: Readonly<Record<string, number>> = {
  common: 0xe8d9a6,
  magic: 0x6fa8ff,
  rare: 0xffd34d,
};

export class GameRenderer {
  readonly world = new PixiContainer();
  /**
   * Always-on-top layer for overhead name + speech labels. The labels stay logical
   * children of their {@link PlayerSprite} (so all their positioning, fade, and
   * line-of-sight visibility logic is untouched), but a Pixi `RenderLayer` draws
   * them in its own pass after the whole depth-sorted world - so a name or speech
   * line never clips behind a wall it happens to sort under (a single sorted
   * `world` ranks each sprite *and its labels* as one unit, so a wall sorting above
   * the speaker would otherwise occlude the text). Sits above {@link world} on the
   * stage; the same render group, so attached labels still transform through their
   * sprite. {@link PlayerSprite} attaches its labels when handed this layer.
   */
  private readonly overheadLayer = new RenderLayer();
  /**
   * Touch tap-to-walk feedback (issue #163): the ping + persistent destination
   * marker + can't-get-there signal. A child of {@link world}, so a tile-pinned
   * marker pans/zooms with the camera; aged each frame in {@link sync}. `main.ts`
   * drives it through the {@link pingDestination}/{@link markDestination}/
   * {@link clearDestination}/{@link signalBlocked} pass-throughs.
   */
  private readonly autowalkOverlay = new AutowalkOverlay();
  /**
   * The Blackthorn's Storm spawn-telegraph sparkle layer (Slice E, ADR-0152): a
   * child of the camera-transformed `world` so each sparkle pins to its ground tile;
   * aged each frame in {@link sync}. `main.ts` drives it from `onStormTelegraph` via
   * the {@link showStormTelegraph} pass-through.
   */
  private readonly telegraphOverlay = new TelegraphOverlay();
  /**
   * The announced-area layer for telegraphed dungeon abilities (PRD #793): the
   * exact footprint a monster's cast committed to, drawn for its whole warning.
   * A child of the camera-transformed `world` like the storm sparkles, aged each
   * frame in {@link sync}, driven from `onDungeonTelegraph` through the
   * {@link showDungeonTelegraph} pass-through.
   */
  private readonly dungeonTelegraphOverlay = new DungeonTelegraphOverlay();
  /** Cursor-driven Charge path and landing marker, visible only while targeting. */
  private readonly dungeonChargePreview = new Graphics();
  /**
   * The blood-splatter decal layer (Blood splatter, PRD #372, Slice A): pooled,
   * capped, self-fading ground decals. A child of the camera-transformed `world`
   * like the telegraph overlay, but sorted **below** the depth-ranked sprites (just
   * above terrain), so blood reads as a floor decal underfoot. Public so `main.ts`
   * (and headless checks / the dev hook) can drive its {@link splatBlood} primitive
   * and read its live count; aged each frame in {@link sync}.
   */
  readonly bloodLayer = new BloodLayer();
  /**
   * The Arena juice FX layer (Slice F, ADR-0178): pooled death bursts, projectile
   * tracers, and aura pulses. A child of the camera-transformed `world` like the
   * telegraph overlay, so every effect pins to its battlefield spot; aged each
   * frame in {@link sync}. Public so `main.ts` (and the headless e2e checks) can
   * read its live counts; spawning goes through the entity-addressed
   * pass-throughs below.
   */
  readonly arenaFx = new ArenaFxOverlay();
  private readonly dungeonLunges = new Map<EntityId, DungeonLunge>();
  private readonly dungeonWhirlwinds = new Map<EntityId, DungeonWhirlwind>();
  private readonly dungeonCharges = new Map<EntityId, DungeonChargeVisual>();
  private dungeonAfterimages: DungeonAfterimage[] = [];
  /**
   * The screen-shake kick (Slice F, ADR-0178): a tiny, capped, decaying offset
   * added to the camera translation in {@link sync}. It rides `world.position` -
   * the same live transform Pixi's event boundary hit-tests through and
   * {@link screenToGroundTile} inverts through - so visuals and picking can never
   * disagree during a shake (see the module doc for the full argument).
   */
  private readonly shake = new ScreenShake();
  private readonly statics: readonly (StaticBillboard | StaticPlacement)[];
  /** Reused screen-space light records, updated in place once per frame. */
  private readonly staticLightScreen: DungeonStaticLight[] = [];
  private readonly staticLightPool: DungeonStaticLight[] = [];
  private readonly staticScene?: StaticScene;
  private readonly staticActivators = new Map<string, Array<(sprite: Sprite) => void>>();
  private readonly bodyFrames: BodyFrames;
  private readonly equipFrames: EquipFrames;
  private readonly hueTable?: HueTable;
  /**
   * The lazy sprite-atlas store, when the live client streams body/equipment atlases
   * on first sighting (ADR-0247 / issue #474). Absent in the tests/tools that hand the
   * renderer fully-populated frame maps up front, in which case the streaming machinery
   * below is inert and the renderer behaves exactly as before.
   */
  private readonly atlasStore?: SpriteAtlasStore;
  /** Our subscription to {@link atlasStore}, detached in {@link destroy}. */
  private readonly onFramesLoaded?: FramesLoadListener;
  /**
   * Bumped once per atlas-ready event from the store. Each pending sprite records the
   * generation it was last (re)built at; a pending sprite is rebuilt at most once per
   * new load, swapping in each atlas as it arrives without rebuilding every frame.
   */
  private frameLoadGen = 0;
  /** Sprites whose look still has an atlas streaming in (grounded fallback showing). */
  private readonly pendingFrames = new Set<EntityId>();
  /** Each pending sprite's {@link frameLoadGen} at its last (re)build. */
  private readonly pendingFrameGen = new Map<EntityId, number>();
  /** Baked static textures, used to (re)texture door sprites by art name. */
  private readonly staticTextures?: StaticTextures;

  /** Mobile sprites pooled by the id of the entity they draw. */
  private readonly sprites = new Map<EntityId, PlayerSprite>();
  private mobileEntities: Entity[] = [];
  private doorEntities: Entity[] = [];
  private diceEntities: Entity[] = [];
  private moongateEntities: Entity[] = [];
  private chestEntities: Entity[] = [];
  private dungeonLootEntities: Entity[] = [];
  private dungeonGlobeEntities: Entity[] = [];
  private projectileEntities: Entity[] = [];
  private xpGemEntities: Entity[] = [];
  private pickupEntities: Entity[] = [];
  private doorEntityIds = new Set<EntityId>();
  private diceEntityIds = new Set<EntityId>();
  private moongateEntityIds = new Set<EntityId>();
  private chestEntityIds = new Set<EntityId>();
  private dungeonLootEntityIds = new Set<EntityId>();
  private dungeonGlobeEntityIds = new Set<EntityId>();
  private projectileEntityIds = new Set<EntityId>();
  private xpGemEntityIds = new Set<EntityId>();
  private pickupEntityIds = new Set<EntityId>();
  private presentationIndexDirty = true;
  private corpseCreations = 0;
  private effectOperationsPlanned = 0;
  private effectOperationsScheduled = 0;
  private largeEffectCascades = 0;
  /** Owner-only arena vitals, pushed outside the replicated world snapshot. */
  private arenaRunnerHealth?: { id: EntityId; current: number; max: number };
  /**
   * Transient overhead health for hurt dungeon monsters (PRD #793).
   *
   * A monster shows a bar because you hurt it and stops showing it when the
   * fight moves on, which is the readable middle between no feedback at all and
   * a room permanently full of bars. Held here rather than on the wire because
   * every input is already replicated: the last health this client saw, and how
   * long ago it dropped.
   */
  private readonly dungeonHealthBars = new Map<
    EntityId,
    { current: number; max: number; remainingMs: number }
  >();
  /** Owner-only Sphere Duel vitals for the two isolated arena sprites. */
  private readonly sphereVitals = new Map<
    EntityId,
    { health: number; mana?: number; poisoned: boolean }
  >();
  /**
   * The render-look signature each pooled sprite was last built for (body + hue +
   * worn equipment), so {@link reconcile} can detect when a character's outfit
   * changes on the snapshot stream and rebuild that sprite's layer stack live (a
   * dressing change shows immediately on every client, no reconnect - PRD #101).
   */
  private readonly spriteLooks = new Map<EntityId, string>();
  /** Structured counterpart to {@link spriteLooks}, used by on-demand clip loads. */
  private readonly spriteFrameLooks = new Map<EntityId, PlayerLook>();
  /** Last async action request per entity, so a late asset response cannot replay stale input. */
  private readonly actionLoadGeneration = new Map<EntityId, number>();
  /**
   * Recently presented prediction ids. The authoritative echo carries the same
   * id, so network latency cannot turn one input into two action clips.
   */
  private readonly presentedActionIds = new Map<string, number>();
  /** In-flight or settled action preloads, keyed by entity look and clip. */
  private readonly actionPreloads = new Map<string, Promise<void>>();
  /** Death-only generation, invalidated by resurrection and bulk corpse clearing. */
  private readonly corpseLoadGeneration = new Map<EntityId, number>();
  /** Look signature whose duel clips are already being prepared for each fighter. */
  private readonly combatPrefetchLooks = new Map<EntityId, string>();
  /**
   * Door sprites pooled by entity id, with the static name of the art currently
   * shown so a state toggle re-textures the sprite only when it actually changed.
   * A door is a billboard like a static, but its art id and draw cell change at
   * runtime (open/closed + swing), so it cannot be a fixed map static - it is
   * drawn from the {@link DoorComponent} each frame (Slice 4, ADR-0014).
   */
  private readonly doorSprites = new Map<EntityId, { sprite: Sprite; name: string }>();
  /** Names already warned about as missing a door texture, so we warn once each. */
  private readonly missingDoorArt = new Set<string>();
  /**
   * Dice-cup sprites pooled by entity id (dice-and-cup, ADR-0083). A dice prop is a
   * fixed billboard like a static, but - unlike a map static - it must be
   * double-clickable and carry an overhead emote surface, so it is drawn from the
   * {@link DiceComponent} each frame as its own {@link DiceSprite}, the same way a
   * door is drawn from its component.
   */
  private readonly diceSprites = new Map<EntityId, DiceSprite>();
  /** Names already warned about as missing a dice texture, so we warn once each. */
  private readonly missingDiceArt = new Set<string>();
  /**
   * Moongate sprites pooled by entity id (The Wandering Moongate, PRD #362
   * Slice A): an animated, double-clickable billboard drawn from the entity's
   * {@link MoongateComponent} each frame via the baked `moongate` effect strip -
   * the dice-cup pattern on the effects art path. Gates spawn and despawn at
   * runtime (the event consumes them), so the pool's removal sweep is live, not
   * just symmetry.
   */
  private readonly moongateSprites = new Map<EntityId, MoongateSprite>();
  /**
   * Treasure-chest sprites pooled by entity id (PRD #362 Slice B): a chest is a
   * fixed, double-clickable billboard exactly like the dice cup, so it reuses
   * {@link DiceSprite} whole (double-click + overhead emote surface) in its own
   * pool; only the routed handler differs ({@link onChestUse}).
   */
  private readonly chestSprites = new Map<EntityId, DiceSprite>();
  /** Names already warned about as missing a chest texture, so we warn once each. */
  private readonly missingChestArt = new Set<string>();
  /** Private dungeon ground items, rendered as named double-clickable statics. */
  private readonly dungeonLootSprites = new Map<EntityId, DiceSprite>();
  private readonly missingDungeonLootArt = new Set<string>();
  /** Contact-collected dungeon health globes (PRD #794), one orb per entity. */
  private readonly dungeonGlobeSprites = new Map<EntityId, HealthGlobeSprite>();
  /**
   * Flying projectile sprites pooled by entity id (bullet-hell rework, PRD #399
   * Slice A): a projectile is a non-interactive, centered billboard drawn
   * straight from its replicated position with **no gait smoothing** (unlike a
   * mobile's {@link StepAnimator}). Spawned and reaped by the run, so the pool's
   * removal sweep is live.
   */
  private readonly projectileSprites = new Map<EntityId, ProjectileSprite>();
  /**
   * Last-seen state of every live projectile, for the impact-on-removal cue (PRD
   * #399 Slice D): its last replicated `ttlTicks` (the contact-vs-expiry
   * discriminator, {@link projectileRemovalWasContact}) and last-drawn screen
   * point (where the impact flash lands). Kept in step with
   * {@link projectileSprites} and swept the same frame a bolt leaves the world.
   */
  private readonly projectileImpactState = new Map<
    EntityId,
    { ttlTicks: number; screen: { x: number; y: number } }
  >();
  /**
   * The XP orb clusters (Arena XP gems, VS-style): one pooled lightweight
   * {@link XpOrbSprite} handle per replicated `xpGem` entity writes into the
   * stable ground particle layer.
   * Like the projectiles, gems are spawned and reaped by the run, so the
   * removal sweep is live.
   * A removal near the runner transfers into {@link absorbingOrbs} instead of
   * vanishing.
   */
  private readonly xpGemSprites = new Map<EntityId, XpOrbSprite>();
  private readonly xpGemLayer = new XpOrbLayer(XP_GROUND_Z);
  private readonly xpAbsorbLayer = new XpOrbLayer(XP_ABSORB_Z);
  /** Each live gem's last-drawn screen point, for the removal-time suck-in origin. */
  private readonly xpGemLastScreen = new Map<EntityId, { x: number; y: number }>();
  /**
   * Orb clusters currently flying into the runner (their gem entity is already
   * gone from the world): eased from their ground point to the *live* player
   * sprite each frame, contracting/fading via {@link XpOrbSprite.setAbsorb},
   * finished with a sparkle burst + {@link onXpAbsorb}. `delayMs` staggers a
   * same-tick pile so the group pours in instead of arriving as one blob.
   */
  private absorbingOrbs: {
    sprite: XpOrbSprite;
    from: { x: number; y: number };
    ageMs: number;
    delayMs: number;
    /** Flight duration (ms); the base {@link XP_ABSORB_MS} normally, lengthened
     * with start distance for a Vacuum inrush cluster (PRD #431 Slice C). */
    durationMs: number;
    mode: 'absorb' | 'fade';
  }[] = [];
  /**
   * Fired once per render frame in which at least one absorbing orb cluster
   * reached the runner, with how many arrived that frame - the shell plays the
   * pickup plink off it (pitch-varied per call, so a pouring pile sparkles).
   */
  onXpAbsorb?: (count: number) => void;
  /**
   * The Vacuum pickup sprites (PRD #431), one pooled {@link VacuumSprite} per
   * replicated `pickup` entity - the gem-orb pool's sibling, drawn with a slow
   * distinct pulse so it reads "special" across the field. Its removal near the
   * runner is the grab: it fires {@link onVacuumGrab} and flags the frame so
   * every gem removed in the same sweep flies (the whole-field inrush).
   */
  private readonly pickupSprites = new Map<EntityId, VacuumSprite>();
  /** Each live pickup's last-drawn screen point, for the grab-time burst origin. */
  private readonly pickupLastScreen = new Map<EntityId, { x: number; y: number }>();
  /**
   * Fired once when a vacuum pickup is claimed near the runner (its entity left
   * the world within grab reach) - the shell plays the one-shot whoosh cue. The
   * per-gem arrival plinks still ride {@link onXpAbsorb} as the field pours in.
   */
  onVacuumGrab?: () => void;
  /**
   * The client's mirror of the runner's `magnet` stat level (PRD #431 Slice A):
   * counted from its own accepted draft picks (main.ts), it grows the effective
   * pickup radius the removal sweep classifies suck-in-vs-fade against. Pure
   * presentation - a wrong guess is a cosmetic mislabel, never wrong XP.
   */
  private magnetLevel = 0;
  /**
   * The moongate sprites drawn this frame with their tile + elevation, so the
   * per-viewer roof-hiding pass (ADR-0155) can cull a roof gate with the same
   * `hiddenStructure` decision the mobiles use - tiles, occupants, and gates can
   * never disagree.
   */
  private readonly moongateOverhead: {
    sprite: MoongateSprite;
    tile: { x: number; y: number };
    elevation: number;
  }[] = [];
  /** Late-bound baked effect strips (gate + summon flash); empty until {@link setEffectArt}. */
  private effectArt: EffectArt = {};
  /** True after the one deferred effect-atlas attempt settles, including failure. */
  private effectArtLoadFinished = false;
  /** Requests the deferred baked FX atlas on arena entry or first town-side use. */
  onEffectArtNeeded?: () => Promise<EffectArt> | void;
  /**
   * Per-entity extrapolation anchors for the banishment overhead countdown
   * (PRD #362 Slice D): the last replicated `remainingTicks` and the render-clock
   * instant it arrived, so the mm:ss ticks smoothly between the server's ~1/s
   * pushes (the storm-board deadline pattern, per entity).
   */
  private readonly banishAnchors = new Map<EntityId, { ticks: number; atMs: number }>();
  /**
   * One {@link StepAnimator} per grid-stepped remote mobile, keyed by id: the
   * UO-style step-queue that turns discrete snapshot poses into tile-by-tile
   * animated motion. Fractional dungeon enemies use `continuousAnimators`.
   * Both are fed and aged in {@link sync} and dropped with the sprite in
   * {@link reconcile}. The prediction-driven local player bypasses both via
   * {@link setLocalPose}.
   */
  private readonly animators = new Map<EntityId, StepAnimator>();
  /** Fractional snapshot followers used only by marked private dungeon enemies. */
  private readonly continuousAnimators = new Map<
    EntityId,
    ContinuousPositionAnimator
  >();
  /**
   * One pooled {@link HitFlash} machine per enemy the Arena volleys have ever
   * struck (PRD #430 Slice A, ADR-0225), keyed by id: triggered at each hit's
   * damage-float instant, aged in {@link sync}, reused across retriggers (zero
   * allocation per hit), swept with the sprite in {@link reconcile}. An idle
   * machine is skipped (its flash mix cleared) - never a per-frame cost.
   */
  private readonly hitFlashes = new Map<EntityId, HitFlash>();
  /**
   * Each live entity's replicated body id (from its `renderable`), refreshed in
   * {@link reconcile}: {@link flashHit} reads it at the hit instant to halve the
   * stagger hold for a boss ({@link isArenaBossBody}) - by then only the id is
   * at hand, not the entity.
   */
  private readonly entityBodies = new Map<EntityId, number | undefined>();
  /**
   * The fallen **corpse** sprite of a dead duelist, pooled by entity id (Duel
   * corpse/ghost split, ADR-0140). On death the player *entity* itself becomes the
   * standing grey hooded shroud ghost (the body-swap look in {@link buildPlayerLayers});
   * the corpse is a **separate** figure - the body + clothes it wore in life, frozen
   * in the fallen die pose on the ground - raised beside it by {@link raiseCorpse} and
   * cleared on resurrection/despawn by {@link removeCorpse}. This mirrors ClassicUO's
   * ghost-mobile + corpse-item split (a corpse is its own object, not the mobile).
   */
  private readonly corpses = new Map<EntityId, PlayerSprite>();
  /**
   * Remaining lifetime (ms) of each **arena horde corpse** in {@link corpses}
   * (ADR-0304): a slain horde enemy's die-held sprite survives its entity's
   * despawn as a client-owned corpse and expires after {@link ARENA_CORPSE_TTL_MS}
   * (fading over its last second). A duel corpse has no entry here - it persists
   * until resurrection/despawn/`ClearCorpses`, exactly as ADR-0140 shipped it.
   */
  private readonly corpseTtl = new Map<EntityId, number>();
  /**
   * The live entities carrying the replicated `horde` marker (arena enemies),
   * refreshed in {@link reconcile}: the despawn pass reads it to tell a slain
   * horde enemy - converted to a client-side TTL corpse (ADR-0304) - from any
   * other despawn, which destroys the sprite as before.
   */
  private readonly hordeIds = new Set<EntityId>();
  /**
   * The last **living** render-look each entity was drawn from (body + hue + worn
   * equipment), so a death can raise its corpse (ADR-0140) from the outfit it wore
   * in life - by the time the renderer sees the ghost body the living look is gone
   * from the snapshot. Cached each reconcile while the entity is alive.
   */
  private readonly livingLooks = new Map<EntityId, PlayerLook>();
  /**
   * Client-local admin tuning keyed by mount body.
   * These values never enter replication and disappear with this renderer.
   */
  private readonly mountRiderHeightOverrides = new Map<number, RiderHeightOverride>();

  /**
   * Residency pins (issue #625): one release handle per visible entity, pinning the
   * atlases its current look renders from so the byte-budget eviction can never
   * destroy a texture a pooled sprite is displaying. Taken at sprite creation and on
   * every live look change (the old look's pin is released so a departed outfit
   * becomes evictable), released at despawn. The local player is a visible entity,
   * so its own look is always pinned through this map.
   */
  private readonly lookPins = new Map<EntityId, () => void>();

  /**
   * The corpse-lifetime residency pins (issue #625): a raised duel corpse renders the
   * cached living look ({@link livingLooks}) long after the entity's own sprite pin
   * moved on to the shroud ghost - so the corpse takes its own pin on the living look,
   * held until {@link removeCorpse}. An arena horde corpse inherits the despawned
   * entity's sprite pin for its client-side TTL the same way.
   */
  private readonly corpsePins = new Map<EntityId, () => void>();

  /**
   * The entities whose mobile is currently inside the viewport window. A sprite
   * scrolling into view reveals its name once on the entering frame (issue #85),
   * not every frame it lingers on screen, so membership is tracked here and the
   * reveal fires only on the false->true transition. Entries are dropped when an
   * entity leaves the window or leaves the world (see {@link reconcile}).
   */
  private readonly inViewport = new Set<EntityId>();

  /**
   * While true (the "show all names" hotkey is held, issue #184), every character's
   * name is re-revealed each {@link sync} so the 4s reveal timer never expires and
   * all nearby names stay lit at once. The per-frame line-of-sight pass still gates
   * them, so names across the bank wall stay dark. Cleared on release, after which
   * the names fade out on their own. See {@link setForceNamesVisible}.
   */
  private forceNamesVisible = false;

  /** The entity the camera centres on (the player), if set. */
  private cameraTarget?: EntityId;

  /**
   * The prediction-driven pose for the **local player** (the camera target),
   * set each frame by `main.ts` from the {@link PredictedWalker}. While present,
   * {@link sync} draws the own entity at this pose - fractional tile, level,
   * facing, gait - instead of its snapshot mobile, and skips the prev/curr
   * interpolation recording for it (prediction owns its motion; the snapshot
   * lags by RTT). Cleared (undefined) to fall back to snapshot-driven drawing.
   */
  private localPose?: LocalPose;

  /**
   * A fixed world tile the camera centres on when there is **no** {@link cameraTarget}
   * entity - the spectator backdrop (the login screen) has no local player to follow,
   * so it pins the view over the player spawn tile instead (issue #193). Cleared the moment an entity
   * target is set, so the player view always wins once the visitor enters.
   */
  private fixedCameraTile?: Tile;

  /**
   * Camera zoom: `zoom` is the value actually applied to {@link world}'s scale
   * this frame, `targetZoom` is where the wheel input wants it. {@link sync} eases
   * `zoom` toward `targetZoom` each frame so a wheel notch glides rather than
   * jumps (issue #67). Both default to 1 (the authored 1:1 view) until the player
   * scrolls.
   */
  private zoom = 1;
  private targetZoom = 1;

  /** The local viewer's dungeon-membership target. */
  private dungeonLightingEnabled = false;
  /** The run identity that keys the accumulated dungeon sight mask. */
  private dungeonSightRunKey?: string;
  /** Lazily built because Pixi filter construction touches the DOM. */
  private dungeonLighting?: DungeonLighting;
  /** A failed construction or render degrades this scene to no lighting. */
  private dungeonLightingUnavailable = false;
  /** Authored underground maps stay lit independently of procedural run state. */
  private authoredDungeonLighting = false;

  setAuthoredDungeonLighting(on: boolean): void {
    this.authoredDungeonLighting = on;
    this.setDungeonLighting(false, { initialAmount: on ? 1 : 0 });
  }

  /**
   * Set whether the local viewer is inside a dungeon lighting volume.
   * Repeated level-driven calls are idempotent.
   */
  setDungeonLighting(
    on: boolean,
    options: { initialAmount?: number; sightRunKey?: string } = {},
  ): void {
    const initialAmount = options.initialAmount ?? 0;
    const enabled = on || this.authoredDungeonLighting;
    this.dungeonLightingEnabled = enabled;
    this.dungeonSightRunKey = on ? options.sightRunKey : undefined;
    if ((!enabled && initialAmount <= 0) || this.dungeonLighting || this.dungeonLightingUnavailable) {
      return;
    }
    try {
      this.dungeonLighting = new DungeonLighting(
        this.app,
        this.world,
        this.lightTextures,
        initialAmount,
      );
    } catch {
      this.dungeonLightingUnavailable = true;
    }
  }

  /**
   * Called when the local player clicks a door sprite, with that door's entity
   * id. `main.ts` wires it to enqueue a `UseDoor` command up the socket; input
   * never mutates the door directly (the server is authoritative). Absent when no
   * handler is attached (e.g. headless renderer tests), in which case a click is
   * a harmless no-op.
   */
  onDoorUse?: (door: EntityId) => void;

  /**
   * Called when the local player **double-clicks** the dice-and-cup, with the dice
   * entity's id (dice-and-cup, ADR-0083). `main.ts` wires it to send a `RollDice`
   * command up the socket; the cup never rolls locally (the server is
   * authoritative and enforces the per-player cooldown). Absent when no handler is
   * attached (e.g. headless renderer tests), in which case a double-click is a
   * harmless no-op.
   */
  onDiceUse?: (dice: EntityId) => void;

  /**
   * Called when the local player **double-clicks** a moongate, with the gate
   * entity's id (PRD #362 Slice A). `main.ts` wires it to send an `EnterMoongate`
   * command up the socket; the gate never teleports locally (the server is
   * authoritative and rules on dead/busy/fate). Absent when no handler is
   * attached (headless tests), in which case a double-click is a harmless no-op.
   */
  onMoongateUse?: (gate: EntityId) => void;

  /**
   * Called when the local player **double-clicks** a treasure chest, with the
   * chest entity's id (PRD #362 Slice B). `main.ts` wires it to send an
   * `OpenChest` command up the socket; the chest never opens locally (the owning
   * event resolves the purse server-side). Absent when no handler is attached
   * (headless tests), in which case a double-click is a harmless no-op.
   */
  onChestUse?: (chest: EntityId) => void;

  /** Called when the local player double-clicks private dungeon ground loot. */
  onDungeonLootUse?: (loot: EntityId) => void;

  /**
   * Called when the local player double-clicks a **player character** - their own
   * (the {@link cameraTarget}, the renderer's notion of "self") or any entity the
   * server classified as a player via the replicated interaction role
   * (`'duel-target'`/`'dueling'`, ADR-0266) - with that entity's id. The paperdoll
   * installer wires it to open the read-only paperdoll gump (PRD #629, superseding
   * the own-character-only wardrobe shortcut of ADR-0073). NPC scenery, rats
   * (`'war-target'`), and the marker-keyed vendors (whose own `onDoubleClick`
   * overrides below keep precedence) never fire it. Absent when no handler is
   * attached (e.g. headless renderer tests), in which case a double-click is a
   * harmless no-op beyond the usual name reveal.
   */
  onCharacterDoubleClick?: (id: EntityId) => void;

  /**
   * Whether the current left-click should be **suppressed** because it was really a
   * long-press that already opened the in-world context menu (ADR-0169, replacing the
   * old shift+left-click of ADR-0161). `main.ts` wires it to the long-press latch: while
   * a hold has opened the menu, the sprite `click` handlers below (rat pursuit /
   * duel-target pick) early-return so the hold never *also* fires the normal action.
   * Absent (headless tests) or returning `false` means the click acts as usual.
   */
  suppressClick?: () => boolean;

  /**
   * Called when the local player **left-clicks** a giant-rat sprite while war
   * mode is on (Rat Swarm Slice E, ADR-0120), with the rat's entity id.
   * `main.ts` wires it to the pursuit brain (`src/input/pursuit.ts`): the click
   * commits a chase that stomps on arrival - never a local kill (the server is
   * authoritative, ADR-0119). Absent when unwired (headless tests): a click is
   * a harmless no-op. Peace-mode clicks never reach it (see {@link setWarMode}).
   */
  onRatClick?: (rat: EntityId) => void;

  /**
   * Whether a rat may currently be targeted (Rat Swarm Slice E). `main.ts`
   * wires it to "not a lingering corpse": a killed rat's entity stays ~1s so its
   * `die` clip can play, and hovering/clicking that corpse must not read as a
   * target (PRD story 34). Unwired (or `true`) means every live rat is fair game.
   */
  ratTargetable?: (rat: EntityId) => boolean;

  /**
   * War mode is a client-local interaction gate (PRD #222): while off, rat
   * hover/click wiring is inert and no highlight shows. Toggled by `main.ts`
   * from the Tab action via {@link setWarMode}.
   */
  private warMode = false;

  /** The pooled sprites that are giant rats, so the war-off sweep can clear their highlights. */
  private readonly ratIds = new Set<EntityId>();

  /**
   * The pooled sprites that are King's Justice **thieves** (PRD #361, ADR-0194), so
   * the double-click loot and the die-pose exception in {@link playActionAnimation}
   * (his entity IS the corpse - no raiseCorpse split) route by the replicated
   * `justice` marker rather than a body/name check.
   */
  private readonly justiceThieves = new Set<EntityId>();

  /**
   * The justice thieves whose sprite has already been dropped into the held prone
   * death pose (ADR-0194). The pose is derived from the replicated `thiefCorpse`
   * marker in {@link reconcile} (snapshot-driven - the ephemeral die broadcast can
   * race the entity's first snapshot and is missed by late joiners); this latch
   * keeps the 10Hz stream from restarting the one-shot every frame.
   */
  private readonly justiceCorpsePosed = new Set<EntityId>();

  /**
   * When each Bone Knight Wall trainer's looping pose replays next, in
   * renderer clock ms (PRD #837 slice 3). The pose is **derived from the
   * replicated `trainingLoop` component on reconcile**, never from one-shot
   * cues: a cue fired at spawn races the first snapshot and a late joiner
   * misses it, while this schedule starts the moment the sprite exists.
   */
  private readonly trainingLoopNextAt = new Map<EntityId, number>();

  /**
   * Called when the local player **double-clicks** the King's Justice thief's
   * corpse (PRD #361, ADR-0194). `main.ts` wires it to send the `LootCorpse`
   * command; the server rules authoritatively (event phase, corpse marker,
   * Chebyshev range, the first-loot race), so a premature or out-of-range
   * double-click fizzles silently. Absent when unwired (headless tests).
   */
  onCorpseLoot?: (corpse: EntityId) => void;

  /**
   * Called when the local player **double-clicks** the Bank Escort (PRD #371).
   * `main.ts` wires it to send the `EscortAsk` command - the double-click is the
   * "Ask destination" affordance (the long-press menu carries Accept/Abandon);
   * the server rules authoritatively (phase, marker, Chebyshev range), so an
   * out-of-range double-click fizzles silently. Absent when unwired (headless tests).
   */
  onEscortAsk?: (npc: EntityId) => void;

  /**
   * Called when the local player **double-clicks** the Arena Master NPC (PRD #334,
   * ADR-0245). `main.ts` wires it to open the Arena Master gump (leaderboard + Join
   * + shop) - the double-click is an *additional* affordance beside the long-press
   * "Join the arena" menu (the Bank Escort precedent: one NPC, both a double-click
   * action and a long-press menu). Absent when unwired (headless tests).
   */
  onArenaMasterUse?: (npc: EntityId) => void;

  /**
   * Called when the local player **double-clicks** the Bone Knight Wall's
   * Soulstone keeper (PRD #837 slice 3), keyed off the replicated
   * `soulstoneKeeper` marker. `installDungeon` wires it to open the Soulstone
   * dialog; the long-press menu carries the same "Speak" affordance (the Bank
   * Escort precedent). Absent when unwired (headless tests).
   */
  onSoulstoneKeeperUse?: (npc: EntityId) => void;

  /**
   * Called when the local player **left-clicks** a player/NPC sprite while the
   * duel target cursor is armed (Player duels, PRD #252, ADR-0130), with the
   * target's entity id. `main.ts` wires it to open the challenger's wager prompt.
   * The server makes the authoritative ruling (self / rat / NPC / ghost / range /
   * busy); an invalid pick fizzles silently. Absent when unwired (headless tests).
   */
  onDuelTargetClick?: (target: EntityId) => void;

  /**
   * Whether a mobile may currently be a duel target (Player duels, PRD #252).
   * `main.ts` wires it to the pure `isDuelTargetCandidate` (not self, not a rat,
   * within {@link DUEL_CHALLENGE_RANGE}); unwired (or `true`) means every mobile is
   * a candidate. A best-effort client filter - the server rules authoritatively.
   */
  duelTargetable?: (target: EntityId) => boolean;

  /**
   * Whether a **scenery-role** mobile may be picked by the armed `/ignore`
   * cursor (ADR-0185, preserved across the D5 role flip - ADR-0266). Roster
   * NPCs classify as scenery now (no duel wiring), but NPC bark squelching is
   * the `/ignore` command's whole point, so scenery mobiles carry hover/click
   * handlers gated on this predicate at event time. `main.ts` wires it to
   * "ignore purpose armed AND the pre-D5 pickable set" (named, not self, not an
   * event body / marker NPC); **unwired it is `false`**, so scenery stays fully
   * inert by default (the fail-safe) - the opposite polarity of
   * {@link duelTargetable}'s permissive default, deliberately.
   */
  ignoreTargetable?: (target: EntityId) => boolean;

  /**
   * The duel target cursor is armed (Player duels, PRD #252): while off, the
   * player/NPC hover/click wiring is inert and no blue highlight shows - the mirror
   * of {@link warMode} for duels. Armed by `main.ts` when the player speaks the
   * duel phrase; disarmed after a pick or on cancel via {@link setDuelTargeting}.
   */
  private duelMode = false;

  /** The pooled player/NPC sprites, so disarming the duel cursor can clear their highlights. */
  private readonly duelTargetIds = new Set<EntityId>();

  /**
   * The pooled scenery-role mobiles carrying the `/ignore`-cursor wiring (see
   * {@link ignoreTargetable}), so disarming the cursor sweeps their blue
   * highlight exactly as it sweeps the duel targets'.
   */
  private readonly ignoreWiredIds = new Set<EntityId>();

  /** The local player's last discrete tile, exposed for the dev coord HUD. */
  private lastPlayerTile?: Tile;

  /**
   * The map's connected roof structures, computed once and reused every frame.
   * `this.statics` is immutable for the renderer's lifetime (statics never move,
   * join, or leave - only mobiles and doors do), so their roof structures - and
   * the inside/outside regions that derive from them - never change either.
   * Flood-filling them every frame cost ~4ms on the extended map for no benefit;
   * we compute them lazily on first use and cache them here (see {@link sync}).
   */
  private structures?: RoofStructure<StaticBillboard | StaticPlacement>[];

  /**
   * The sprite-owning subset of the supplied terrain tiles, for viewport-
   * culling the ground (toggling each one's `visible` by the camera window).
   * On the classic sprite path this is every tile; on the sloped-mesh path
   * (PRD #536 Slice E) the supplied tiles are bare {@link TerrainTileInfo}s
   * (no per-tile display object) so this is empty and {@link meshTerrain}
   * culls per chunk instead. Empty too when the caller supplies no tiles
   * (e.g. the headless renderer tests): terrain culling is simply a no-op.
   */
  private readonly terrainSprites: TerrainTile[];
  private readonly terrainPlacementCount: number;

  /**
   * Terraced land (PRD #536 Slice C): the map's height budget - the largest
   * `|landZ|` over the supplied terrain tiles - and, when non-zero, a per-tile
   * elevation lookup for height-aware ground picking. Both derive once from
   * `terrainTiles` at construction; on a flat map the budget is 0, the lookup
   * is absent, and every path below degenerates byte-identically to the flat
   * behavior.
   */
  private readonly maxAbsLandZ: number;
  private readonly landZByTile?: Map<string, number>;

  /**
   * The viewport-cull margin actually used: {@link CULL_MARGIN} widened by the
   * map's terrain height budget ({@link terrainCullMargin}), so a tall hill's
   * tiles - and everything standing on them - never pop at the screen edges.
   */
  private readonly cullMargin: number;

  /**
   * The windowed procedural ground of an open-plain map (PRD #387 Slice C),
   * late-bound by the scene builder when the map is plain (`undefined` on grid
   * maps). Driven from the same cull-bounds change the static/terrain sweep
   * keys off, so a still camera repaints nothing.
   */
  proceduralTerrain: ProceduralTerrain | undefined;

  /** Bounded-map pooled terrain, driven by the same stable cull window. */
  windowedTerrain: WindowedTerrain | undefined;

  /**
   * The chunked sloped-mesh ground of a heighted map (PRD #536 Slice E),
   * late-bound by the scene builder when the render-path toggle picks the mesh
   * path (`undefined` otherwise). The ground still lives in the one terrain
   * container at TERRAIN_Z; only culling differs - per chunk via this hook
   * instead of per tile sprite, driven from the same cull-bounds change.
   */
  meshTerrain: { cull(bounds: TileBounds | undefined): void } | undefined;

  /**
   * The statics whose sprites are currently children of {@link world}. The
   * constructor adds them all (so the depth-integration tests that never set a
   * camera target keep every static present), and the first culling {@link sync}
   * prunes this down to the camera window by diffing entering/leaving statics
   * against this set - the membership half of viewport culling.
   */
  private readonly addedStatics: Set<StaticBillboard>;

  /**
   * The viewport-cull window used for the last re-cull, and whether one has
   * happened yet. When {@link sync} finds the freshly computed window
   * {@link sameBounds} as this one, it skips the whole-map statics and terrain
   * loops and reuses {@link cachedStaticSortables} unchanged - the biggest
   * per-frame win, since a still camera (the common case: the player standing, or
   * moving fractionally within one tile) does no full-world work at all. Reset by
   * every genuine camera move or zoom step (which changes the window).
   */
  private lastCullBounds: TileBounds | undefined;
  private hasCulled = false;

  /**
   * The depth-sortables for the statics currently on screen, rebuilt only on a
   * re-cull and re-pushed into the per-frame sort each frame (statics never move,
   * so their `x/y/z/layer/roof` and zIndex-stamping closure are stable for the
   * whole time they stay in the window). Reused verbatim while the camera window
   * holds still, so a static frame allocates none of them.
   */
  private cachedStaticSortables: Sortable[] = [];

  /**
   * How many times {@link sync} has run the full-map re-cull (rebuilt the visible
   * static set + terrain visibility). Exposed for verification: on a still camera
   * this stops incrementing while the frame counter keeps climbing, which is the
   * observable proof the per-frame whole-world culling work is being skipped.
   */
  cullRecomputes = 0;

  /**
   * How many depth ranks {@link stampDepth} has actually written. The depth pass
   * resolves a rank for every sortable every frame, but only a rank that moved is
   * written, because a `zIndex` write marks the world's render group
   * `structureDidChange` and re-sorts `world.children`. On a settled scene this
   * stops climbing while the frame counter keeps going, which is the observable
   * proof that an unchanged depth order rebuilds no instructions (PRD #826).
   */
  depthStampWrites = 0;

  /**
   * Spent horde sprites kept warm for the next spawn (PRD #826).
   *
   * An Arena wave is a treadmill: enemies spawn, die into TTL corpses, and their
   * corpses expire, all at a roughly constant population. Building a
   * {@link HordeSprite} per enemy and destroying it on corpse expiry therefore
   * churned display objects continuously for no change in what is on screen.
   * Released here at every corpse exit and re-dressed in {@link createSprite}.
   */
  private readonly freeHordeSprites: HordeSprite[] = [];

  /** How many horde spawns have been served from the pool rather than built. */
  hordeSpriteReuses = 0;
  private performanceMonitor?: ClientPerformanceMonitor;
  private detailedWorkloadSampling = false;
  private replicaDrivenReconciliation = false;
  private presentationQuality: PresentationQualityTier = 'full';
  onPresentationInvalidated?: () => void;

  constructor(
    private readonly app: Application,
    terrain: Container,
    statics: StaticBillboard[] | StaticScene,
    bodyFrames: BodyFrames,
    hueTable?: HueTable,
    equipFrames: EquipFrames = {},
    staticTextures?: StaticTextures,
    terrainTiles: TerrainTileInfo[] = [],
    atlasStore?: SpriteAtlasStore,
    private readonly lightTextures: LightTextures = new Map(),
  ) {
    if (statics instanceof StaticScene) {
      this.staticScene = statics;
      this.statics = statics.placements;
      this.addedStatics = new Set();
    } else {
      this.statics = statics;
      this.addedStatics = new Set(statics);
    }
    this.bodyFrames = bodyFrames;
    this.equipFrames = equipFrames;
    this.hueTable = hueTable;
    if (hueTable) configureUoHueBatch(hueTable);
    // When lazy streaming is wired (the live client), subscribe to atlas-ready events:
    // each bumps the load generation so `reconcile` swaps the grounded fallback for the
    // real frames on the pending sprites that use the newly-arrived body/equipment id.
    // In production `bodyFrames`/`equipFrames` ARE the store's own records, so a load
    // mutates the very maps `buildPlayerLayers` reads. Detached in `destroy`.
    if (atlasStore) {
      this.atlasStore = atlasStore;
      const listener: FramesLoadListener = () => {
        this.frameLoadGen++;
        this.onPresentationInvalidated?.();
      };
      this.onFramesLoaded = listener;
      atlasStore.addLoadListener(listener);
    }
    this.staticTextures = staticTextures;
    this.terrainSprites = terrainTiles.filter(
      (t): t is TerrainTile => (t as TerrainTile).sprite !== undefined,
    );
    this.terrainPlacementCount = terrainTiles.length;
    // Terraced land (PRD #536 Slice C): derive the height budget + picking
    // lookup once. A flat map leaves the budget 0 and the lookup absent.
    let maxAbsLandZ = 0;
    for (const t of terrainTiles) {
      const abs = Math.abs(t.z);
      if (abs > maxAbsLandZ) maxAbsLandZ = abs;
    }
    this.maxAbsLandZ = maxAbsLandZ;
    if (maxAbsLandZ > 0) {
      const lookup = new Map<string, number>();
      for (const t of terrainTiles) {
        if (t.z !== 0) lookup.set(`${t.x},${t.y}`, t.z);
      }
      this.landZByTile = lookup;
      // Ground-pinned overlays draw at the terrain under their tile too (the
      // tap-to-walk reticle/ping/X and blood decals); left at their flat
      // defaults on a flat map, so today's positions are untouched.
      const landZ = (x: number, y: number): number => lookup.get(`${x},${y}`) ?? 0;
      this.autowalkOverlay.landZ = landZ;
      this.bloodLayer.landZ = landZ;
      // An announced area sits on the terrace it will damage, not below it.
      this.dungeonTelegraphOverlay.landZ = landZ;
    }
    this.cullMargin = terrainCullMargin(CULL_MARGIN, maxAbsLandZ);
    // One sortable world: ground pinned behind, then statics + mobiles sorted by
    // the depth rule each frame. Mobile sprites are added on demand in sync().
    this.world.sortableChildren = true;
    terrain.zIndex = TERRAIN_Z;
    // The ground is its own Pixi render group (PRD #826). Every entity spawn,
    // corpse, gem, and depth reorder marks the world's render group
    // `structureDidChange`, which rebuilds its instruction set and re-packs every
    // quad it owns. Terrain is by far the largest quad population in the scene
    // (the Arena plain's window alone is thousands of tiles) and it never
    // interleaves with the depth-sorted sprites, so isolating it means entity
    // churn re-packs only the entities. The container keeps its TERRAIN_Z place
    // in the world's sort, so the ground still paints behind everything.
    terrain.enableRenderGroup();
    this.world.addChild(terrain);
    if (Array.isArray(statics)) {
      for (const s of statics) this.world.addChild(s.sprite);
    }
    // The tap-to-walk feedback overlay rides inside the world so its tile-pinned
    // marker tracks the camera; its high zIndex keeps the ping/marker above the
    // depth-ranked sprites (issue #163).
    this.world.addChild(this.autowalkOverlay);
    // The storm spawn-telegraph sparkles ride inside the world the same way, so each
    // danger cue pins to its ground tile and tracks the camera (ADR-0152).
    this.world.addChild(this.telegraphOverlay);
    // Announced dungeon areas pin to the ground the same way (PRD #793).
    this.world.addChild(this.dungeonTelegraphOverlay);
    this.dungeonChargePreview.eventMode = 'none';
    this.dungeonChargePreview.zIndex = OVERLAY_Z;
    this.world.addChild(this.dungeonChargePreview);
    // The blood decals ride inside the world too, but their low zIndex sorts them
    // below every sprite (just above terrain) - a floor decal, not an on-top FX
    // (Blood splatter, PRD #372, Slice A).
    this.world.addChild(this.bloodLayer);
    // Ground XP gems share one particle batch below every mobile. Collected
    // gems transfer into the second batch above the crowd for their suck-in.
    this.world.addChild(this.xpGemLayer);
    this.world.addChild(this.xpAbsorbLayer);
    // The Arena juice FX layer rides inside the world too, so bursts/tracers/pulses
    // pin to their battlefield spots and pan/zoom with the camera (Slice F, ADR-0178).
    this.world.addChild(this.arenaFx);

    app.stage.addChild(this.world);
    // The overhead layer sits above the world on the stage so its attached labels
    // always paint last (over every wall/roof/mobile), while still transforming
    // with the camera through their sprite's place in the world.
    app.stage.addChild(this.overheadLayer);
  }

  setPerformanceMonitor(monitor: ClientPerformanceMonitor | undefined): void {
    this.performanceMonitor = monitor;
  }

  /**
   * Configure matching static sprites whenever their placement enters the
   * viewport.
   * Pool reuse clears event listeners before this callback runs again.
   */
  onStaticActivated(name: string, configure: (sprite: Sprite) => void): void {
    const callbacks = this.staticActivators.get(name);
    if (callbacks) callbacks.push(configure);
    else this.staticActivators.set(name, [configure]);
    for (const billboard of this.staticScene?.activeBillboards ?? []) {
      if (billboard.name === name) configure(billboard.sprite);
    }
  }

  setPresentationQuality(tier: PresentationQualityTier): void {
    this.presentationQuality = tier;
    const budget = presentationBudgetFor(tier);
    this.arenaFx.setPendingLimit(budget.pendingEffects);
    this.enforceCorpseBudget();
    const cadence = xpOrbCadenceFor(tier);
    for (const sprite of this.xpGemSprites.values()) sprite.setCadence(cadence);
  }

  /**
   * Turn the scene-walking half of {@link presentationWorkload} on (PRD #826).
   *
   * The sample runs once a second for every player, but only the benchmarks and
   * the dev diagnostic read the counts that require walking the display tree,
   * every sprite's layer stack, and the whole terrain placement list. Off, the
   * sample stays proportional to the entity count and those fields are omitted
   * rather than reported as a misleading zero; the counters the presentation
   * governor keys off are always present.
   */
  setDetailedWorkloadSampling(on: boolean): void {
    this.detailedWorkloadSampling = on;
  }

  presentationWorkload(): PresentationWorkload {
    const residency = this.atlasStore?.residencyStats();
    let liveEnemies = 0;
    let materializedEnemies = 0;
    for (const entity of this.mobileEntities) {
      if (entity.horde !== true) continue;
      liveEnemies += 1;
      if ((this.sprites.get(entity.id)?.visualLayerCount ?? 0) > 0) {
        materializedEnemies += 1;
      }
    }
    const detail = this.detailedWorkloadSampling ? this.workloadDetail() : undefined;
    return {
      liveEnemies,
      materializedEnemies,
      xpGems: this.xpGemEntities.length,
      corpses: this.corpseTtl.size,
      corpseCreations: this.corpseCreations,
      pendingEffects: this.arenaFx.pendingCount,
      activeEffects: this.arenaFx.activeCount,
      effectOperationsPlanned: this.effectOperationsPlanned,
      effectOperationsScheduled: this.effectOperationsScheduled,
      largeEffectCascades: this.largeEffectCascades,
      ...detail,
      decodedAssets: residency?.residentCount ?? 0,
      decodedAssetBytes: residency?.residentBytes ?? 0,
      staticPlacements: this.statics.length,
      activeStaticSprites: this.staticScene?.activeBillboards.length ?? this.addedStatics.size,
      terrainWindowUpdates: this.cullRecomputes,
    };
  }

  /**
   * The workload fields that cost a walk of the scene. Sampled only while
   * {@link setDetailedWorkloadSampling} is on, because nothing in the live client
   * reads them: the presentation governor keys off frame timings and the entity
   * counters, while these answer "what is the scene made of" for the benchmarks
   * and the dev diagnostic.
   */
  private workloadDetail(): Partial<PresentationWorkload> {
    let mobileLayers = 0;
    let huedLayers = 0;
    for (const sprite of this.sprites.values()) {
      mobileLayers += sprite.visualLayerCount;
      huedLayers += sprite.huedLayerCount;
    }
    for (const sprite of this.corpses.values()) {
      mobileLayers += sprite.visualLayerCount;
      huedLayers += sprite.huedLayerCount;
    }
    return {
      displayObjects:
        this.countDisplayObjects(this.world) + this.countDisplayObjects(this.overheadLayer),
      mobileLayers,
      huedLayers,
      terrainPlacements: this.terrainPlacementCount,
      activeTerrainSprites: this.countActiveTerrainSprites(),
      terrainCoverageTiles: this.countTerrainCoverageTiles(),
    };
  }

  /** Visible ground sprites, counted without materialising a filtered array. */
  private countActiveTerrainSprites(): number {
    if (this.windowedTerrain) return this.windowedTerrain.activeCount;
    let count = 0;
    if (this.proceduralTerrain) {
      for (const child of this.proceduralTerrain.container.children) if (child.visible) count += 1;
      return count;
    }
    for (const tile of this.terrainSprites) if (tile.sprite.visible) count += 1;
    return count;
  }

  /** Logical ground tiles covered by the visible terrain representation. */
  private countTerrainCoverageTiles(): number {
    return this.proceduralTerrain?.activeCount ?? this.countActiveTerrainSprites();
  }

  /** Whether the current scene needs full-rate presentation on the next frame. */
  hasActivePresentation(): boolean {
    if (this.localPose?.isMoving) return true;
    if (this.zoom !== this.targetZoom || this.shake.active) return true;
    if (this.arenaFx.activeCount > 0 || this.arenaFx.pendingCount > 0) return true;
    if (
      this.autowalkOverlay.requiresActivePresentation ||
      this.telegraphOverlay.activeCount > 0 ||
      this.dungeonTelegraphOverlay.activeCount > 0 ||
      this.bloodLayer.requiresActivePresentation ||
      this.moongateSprites.size > 0 ||
      this.projectileSprites.size > 0 ||
      this.xpGemSprites.size > 0 ||
      this.absorbingOrbs.length > 0
    ) {
      return true;
    }
    for (const sprite of this.sprites.values()) {
      if (sprite.requiresActivePresentation) return true;
    }
    for (const corpse of this.corpses.values()) {
      if (corpse.requiresActivePresentation) return true;
    }
    for (const ttl of this.corpseTtl.values()) {
      if (ttl < ARENA_CORPSE_FADE_MS) return true;
    }
    for (const sprite of this.diceSprites.values()) {
      if (sprite.requiresActivePresentation) return true;
    }
    for (const sprite of this.chestSprites.values()) {
      if (sprite.requiresActivePresentation) return true;
    }
    for (const sprite of this.dungeonLootSprites.values()) {
      if (sprite.requiresActivePresentation) return true;
    }
    if (this.dungeonGlobeSprites.size > 0) return true;
    for (const flash of this.hitFlashes.values()) {
      if (!flash.idle) return true;
    }
    for (const animator of this.animators.values()) {
      if (animator.queueLength > 0 || animator.staggered) return true;
    }
    return false;
  }

  private countDisplayObjects(container: Container): number {
    let count = 1;
    for (const child of container.children) {
      count += child instanceof PixiContainer ? this.countDisplayObjects(child) : 1;
    }
    return count;
  }

  private enforceCorpseBudget(): void {
    const limit = presentationBudgetFor(this.presentationQuality).corpses;
    while (this.corpseTtl.size > limit) {
      const oldest = this.corpseTtl.keys().next().value as EntityId | undefined;
      if (oldest === undefined) break;
      this.removeCorpse(oldest);
    }
  }

  applyReplica(world: World): void {
    this.replicaDrivenReconciliation = true;
    this.rebuildPresentationIndex(world);
    if (this.performanceMonitor) {
      this.performanceMonitor.measure('scene-reconciliation', () => this.reconcile());
    } else {
      this.reconcile();
    }
  }

  private rebuildPresentationIndex(world: World): void {
    const mobileEntities: Entity[] = [];
    const doorEntities: Entity[] = [];
    const diceEntities: Entity[] = [];
    const moongateEntities: Entity[] = [];
    const chestEntities: Entity[] = [];
    const dungeonLootEntities: Entity[] = [];
    const dungeonGlobeEntities: Entity[] = [];
    const projectileEntities: Entity[] = [];
    const xpGemEntities: Entity[] = [];
    const pickupEntities: Entity[] = [];
    for (const entity of world.all()) {
      if (entity.mobile) mobileEntities.push(entity);
      if (entity.door && entity.position) doorEntities.push(entity);
      if (entity.dice && entity.position) diceEntities.push(entity);
      if (entity.moongate && entity.position) moongateEntities.push(entity);
      if (entity.chest && entity.position) chestEntities.push(entity);
      if (entity.dungeonLoot && entity.position) dungeonLootEntities.push(entity);
      if (entity.dungeonGlobe && entity.position) dungeonGlobeEntities.push(entity);
      if (entity.projectile && entity.position) projectileEntities.push(entity);
      if (entity.xpGem && entity.position) xpGemEntities.push(entity);
      if (entity.pickup && entity.position) pickupEntities.push(entity);
    }
    this.mobileEntities = mobileEntities;
    this.doorEntities = doorEntities;
    this.diceEntities = diceEntities;
    this.moongateEntities = moongateEntities;
    this.chestEntities = chestEntities;
    this.dungeonLootEntities = dungeonLootEntities;
    this.dungeonGlobeEntities = dungeonGlobeEntities;
    this.projectileEntities = projectileEntities;
    this.xpGemEntities = xpGemEntities;
    this.pickupEntities = pickupEntities;
    this.doorEntityIds = new Set(doorEntities.map((entity) => entity.id));
    this.diceEntityIds = new Set(diceEntities.map((entity) => entity.id));
    this.moongateEntityIds = new Set(moongateEntities.map((entity) => entity.id));
    this.chestEntityIds = new Set(chestEntities.map((entity) => entity.id));
    this.dungeonLootEntityIds = new Set(dungeonLootEntities.map((entity) => entity.id));
    this.dungeonGlobeEntityIds = new Set(dungeonGlobeEntities.map((entity) => entity.id));
    this.projectileEntityIds = new Set(projectileEntities.map((entity) => entity.id));
    this.xpGemEntityIds = new Set(xpGemEntities.map((entity) => entity.id));
    this.pickupEntityIds = new Set(pickupEntities.map((entity) => entity.id));
    this.presentationIndexDirty = true;
  }

  /**
   * Tear the whole scene down: remove this renderer's containers from the stage and
   * destroy them (children included - pooled sprites, labels, overlays; shared
   * textures are untouched). Used by the mid-session **scene swap** (Arena Slice E,
   * ADR-0177): a map-bearing snapshot with a *different* map (arena entry/exit)
   * cannot be drawn on this renderer's terrain/statics, so `main.ts` destroys it and
   * builds a fresh renderer from the new world's map. Not called on an ordinary
   * reconnect (the town map content is unchanged, see `sameMapContent`).
   */
  destroy(): void {
    const dungeonLighting = this.dungeonLighting;
    this.dungeonLighting = undefined;
    try {
      dungeonLighting?.destroy();
    } catch {
      // A lost renderer must not stop the rest of the scene from tearing down.
    }
    this.app.stage.removeChild(this.world);
    this.app.stage.removeChild(this.overheadLayer);
    // Pooled horde sprites are out of the scene graph, so the world teardown
    // below cannot reach them; a scene swap must not leak the warm pool.
    for (const sprite of this.freeHordeSprites) sprite.destroy({ children: true });
    this.freeHordeSprites.length = 0;
    this.world.destroy({ children: true });
    this.overheadLayer.destroy({ children: true });
    this.sprites.clear();
    this.hitFlashes.clear();
    this.entityBodies.clear();
    this.diceSprites.clear();
    this.moongateSprites.clear();
    this.chestSprites.clear();
    this.dungeonLootSprites.clear();
    this.dungeonGlobeSprites.clear();
    this.projectileSprites.clear();
    this.projectileImpactState.clear();
    this.xpGemSprites.clear();
    this.xpGemLastScreen.clear();
    this.absorbingOrbs = [];
    this.pickupSprites.clear();
    this.pickupLastScreen.clear();
    this.doorSprites.clear();
    // Detach from the shared, page-scoped atlas store so a torn-down renderer (a scene
    // swap builds a fresh one, ADR-0177) stops bumping its load generation.
    if (this.onFramesLoaded) this.atlasStore?.removeLoadListener(this.onFramesLoaded);
    // Release every residency pin this scene held (a scene swap builds a fresh
    // renderer whose reconcile re-pins what the new world shows; issue #625).
    for (const release of this.lookPins.values()) release();
    this.lookPins.clear();
    for (const release of this.corpsePins.values()) release();
    this.corpsePins.clear();
    this.pendingFrames.clear();
    this.pendingFrameGen.clear();
    this.spriteFrameLooks.clear();
    this.actionLoadGeneration.clear();
    this.corpseLoadGeneration.clear();
    this.combatPrefetchLooks.clear();
  }

  /** Centre the camera on the given entity each frame (e.g. the player). */
  setCameraTarget(id: EntityId): void {
    this.cameraTarget = id;
    // An entity to follow supersedes any fixed spectator view.
    this.fixedCameraTile = undefined;
  }

  /** Surface the local arena runner's compact underfoot health bar. */
  setArenaRunnerHealth(id: EntityId, current: number, max: number, active: boolean): void {
    this.arenaRunnerHealth = active ? { id, current, max } : undefined;
  }

  /** Surface the private Sphere Duel health bars and the local player's mana bar. */
  setSphereVitals(state:
    | { active: false }
    | {
      active: true;
      local: { id: EntityId; health: number; mana: number; poisoned: boolean };
      opponent: { id: EntityId; health: number; poisoned: boolean };
    }): void {
    this.sphereVitals.clear();
    if (!state.active) return;
    this.sphereVitals.set(state.local.id, {
      health: state.local.health,
      mana: state.local.mana,
      poisoned: state.local.poisoned,
    });
    this.sphereVitals.set(state.opponent.id, {
      health: state.opponent.health,
      poisoned: state.opponent.poisoned,
    });
  }

  /**
   * Provide (or clear) the prediction-driven pose the **local player** draws at
   * this frame. Set from the {@link PredictedWalker} before each {@link sync};
   * while present the own sprite ignores its snapshot mobile entirely - position,
   * level, facing, and gait all come from prediction, and the camera follows the
   * same sprite, so the view is prediction-driven too. `undefined` restores the
   * snapshot-driven path (spectators and the pre-Welcome state never set one).
   */
  setLocalPose(pose: LocalPose | undefined): void {
    this.localPose = pose;
  }

  /**
   * Pin the camera on a fixed world tile, used when there is no entity to follow -
   * the spectator backdrop (login screen) frames the player spawn tile from here. Ignored
   * once {@link setCameraTarget} sets an entity to centre on.
   */
  setCameraTile(tile: Tile): void {
    this.cameraTarget = undefined;
    this.fixedCameraTile = tile;
  }

  /** Current discrete camera tile for deterministic browser performance travel. */
  cameraTile(): Tile | undefined {
    return this.lastPlayerTile ? { ...this.lastPlayerTile } : undefined;
  }

  /**
   * Set the desired camera zoom, clamped to [{@link MIN_ZOOM}, {@link MAX_ZOOM}].
   * This moves only the *target*; {@link sync} eases the applied zoom toward it
   * each frame, so the change is smooth rather than instant.
   */
  setZoom(z: number): void {
    this.targetZoom = clampZoom(z);
  }

  /**
   * Nudge the zoom target by a signed number of wheel notches: positive zooms in
   * (closer), negative zooms out, each notch a geometric {@link ZOOM_STEP} step so
   * the feel is uniform across the range. Wired to the canvas wheel in `main.ts`.
   */
  adjustZoom(deltaNotches: number): void {
    this.setZoom(this.targetZoom * Math.pow(ZOOM_STEP, deltaNotches));
  }

  /** The applied camera zoom this frame (the value on `world.scale`). */
  zoomLevel(): number {
    return this.zoom;
  }

  /** The zoom the wheel input is easing toward (post-clamp). */
  zoomTarget(): number {
    return this.targetZoom;
  }

  /** The local player's current discrete tile, if known (dev coord HUD). */
  playerTile(): Tile | undefined {
    return this.lastPlayerTile;
  }

  /**
   * The local player's on-screen position in canvas pixels: the viewport centre
   * the camera locks the camera-target entity to each frame (see {@link sync}),
   * or `undefined` before the player is known. ClassicUO's right-click movement
   * measures the cursor's offset from exactly this point (its `Camera.Bounds`
   * centre) to derive an angle -> 8-way facing and a distance -> walk/run/turn,
   * so the mouse-movement input layer reads the player's screen position here
   * rather than re-projecting its tile.
   */
  playerCenter(): ScreenPoint | undefined {
    if (this.lastPlayerTile === undefined) return undefined;
    return { x: this.app.screen.width / 2, y: this.app.screen.height / 2 };
  }

  /**
   * Invert the dimetric projection for a point in the canvas, returning the
   * **ground-plane** (`z=0`) tile under it. Used by the dev coord HUD to read off
   * the tile beneath the cursor; an authored static sitting at a positive `z`
   * draws shifted up the screen, so its art appears a few tiles "north" of the
   * ground tile this returns - place from the player's own tile when in doubt.
   *
   * Zoom-aware: the world container is both translated (`world.position`) and
   * scaled (`world.scale`, the camera zoom), so a canvas point inverts to a
   * world-local point by subtracting the translation *and* dividing out the zoom
   * before inverting the dimetric projection. Without the zoom divide this
   * mis-reads whenever the player has scrolled away from 1:1 (issue #67).
   */
  screenToGroundTile(screenX: number, screenY: number): Tile {
    const zoom = this.world.scale.x || 1;
    const sx = (screenX - this.world.position.x) / zoom;
    const sy = (screenY - this.world.position.y) / zoom;
    // Terraced land (PRD #536 Slice C): on a heighted map, resolve the click
    // against the tile actually painted there (its own z-plane), not the flat
    // ground plane - a tap on a tall terrace must walk to the tile clicked,
    // not the tile behind it. Flat map: `pickGroundTile` with budget 0 IS the
    // old inverse, byte-identical.
    const lookup = this.landZByTile;
    return pickGroundTile(
      sx,
      sy,
      lookup ? (x, y) => lookup.get(`${x},${y}`) ?? 0 : () => 0,
      this.maxAbsLandZ,
    );
  }

  /**
   * The targetable giant rat whose sprite sits under a canvas point, or
   * `undefined` (Rat Swarm mobile support). Touch has no hover-then-click, so a
   * tap must pick a rat the way a desktop click does - through Pixi's own hit
   * test against the sprite (respecting its bounds and z-order), *not* a
   * ground-tile guess: a rat's art stands well above its foot tile, so the tile
   * under the tap is not the rat's tile. War-mode gated and corpse-filtered
   * exactly like the pointer {@link onRatClick} path, so tap and click agree.
   * The coords are canvas pixels (the same space {@link screenToGroundTile}
   * takes); the renderer runs at resolution 1, so that is Pixi's event space too.
   */
  pickRat(screenX: number, screenY: number): EntityId | undefined {
    if (!this.warMode) return undefined;
    const hit = this.app.renderer.events.rootBoundary.hitTest(screenX, screenY);
    if (!hit) return undefined;
    for (const id of this.ratIds) {
      if (!(this.ratTargetable?.(id) ?? true)) continue;
      const sprite = this.sprites.get(id);
      // The hit may be the sprite itself or one of its children (name label,
      // highlight), so match against the sprite or any of its ancestors.
      for (let node: Container | null = hit; node; node = node.parent) {
        if (node === sprite) return id;
      }
    }
    return undefined;
  }

  /**
   * The mobile (player or NPC) sprite sitting under a canvas point, or `undefined`
   * (the client-side ignore list, per-viewer squelch). Reuses Pixi's own hit test
   * exactly like {@link pickRat} - respecting sprite bounds and z-order, matching the
   * hit against the sprite or any of its children (name label, highlight) - so a
   * long-press of left-click resolves the character under the cursor to its entity id, which the
   * caller maps to its display name to offer "Ignore". Unlike `pickRat` this is not
   * war-mode gated and spans every pooled character, since ignoring is not combat.
   * The coords are canvas pixels (the space {@link screenToGroundTile} takes); the
   * renderer runs at resolution 1, so that is Pixi's event space too.
   */
  pickEntity(screenX: number, screenY: number): EntityId | undefined {
    const hit = this.app.renderer.events.rootBoundary.hitTest(screenX, screenY);
    if (!hit) return undefined;
    for (const [id, sprite] of this.sprites) {
      for (let node: Container | null = hit; node; node = node.parent) {
        if (node === sprite) return id;
      }
    }
    return undefined;
  }

  /**
   * The dice-and-cup entity under a canvas point, or `undefined` (Dice Wager, ADR-0154).
   * The dice analogue of {@link pickEntity}: it hit-tests the pooled {@link DiceSprite}s
   * instead of the character sprites, so a long-press of left-click resolves the cup under the cursor
   * to its entity id, which `main.ts` maps to the "Roll the dice" wager-starting menu.
   * The dice pool is separate from the character pool (a cup is never a mobile), so this
   * is checked before {@link pickEntity} on the menu-open path. Same coord space and
   * hit-test mechanism as {@link pickEntity}.
   */
  pickDice(screenX: number, screenY: number): EntityId | undefined {
    const hit = this.app.renderer.events.rootBoundary.hitTest(screenX, screenY);
    if (!hit) return undefined;
    for (const [id, sprite] of this.diceSprites) {
      for (let node: Container | null = hit; node; node = node.parent) {
        if (node === sprite) return id;
      }
    }
    return undefined;
  }

  /**
   * The moongate entity under a canvas point, or `undefined` (PRD #362 Slice A) -
   * the {@link pickDice} mechanism over the moongate pool, so a long-press on a
   * gate resolves to its entity id for the "Enter the moongate" context-menu row.
   */
  pickMoongate(screenX: number, screenY: number): EntityId | undefined {
    const hit = this.app.renderer.events.rootBoundary.hitTest(screenX, screenY);
    if (!hit) return undefined;
    for (const [id, sprite] of this.moongateSprites) {
      for (let node: Container | null = hit; node; node = node.parent) {
        if (node === sprite) return id;
      }
    }
    return undefined;
  }

  /**
   * The treasure-chest entity under a canvas point, or `undefined` (PRD #362
   * Slice B) - the {@link pickDice} mechanism over the chest pool, for the
   * long-press "Open the chest" context-menu row.
   */
  pickChest(screenX: number, screenY: number): EntityId | undefined {
    const hit = this.app.renderer.events.rootBoundary.hitTest(screenX, screenY);
    if (!hit) return undefined;
    for (const [id, sprite] of this.chestSprites) {
      for (let node: Container | null = hit; node; node = node.parent) {
        if (node === sprite) return id;
      }
    }
    return undefined;
  }

  /**
   * Touch tap-to-walk feedback (issue #163, PRD #159). These four pass-throughs let
   * `main.ts` drive the {@link AutowalkOverlay} off the same tap-commit / arrival /
   * cancel events the pure tracer already produces - the renderer owns the Pixi
   * layer and the per-frame ageing; the glue owns *when* each fires.
   */
  /** Play one expanding confirmation ping on the resolved destination tile. */
  pingDestination(tile: Tile): void {
    this.autowalkOverlay.pingDestination(tile);
  }

  /** Show / move the persistent destination marker to the resolved tile. */
  markDestination(tile: Tile): void {
    this.autowalkOverlay.markDestination(tile);
  }

  /** Remove the persistent destination marker (arrival / cancel / retarget-to-stop). */
  clearDestination(): void {
    this.autowalkOverlay.clearDestination();
  }

  /** Show the "can't get there" signal when a destination is unreachable: a tap that resolved to no reachable target, or an in-progress walk that gave up (re-path cap / no route, #164). */
  signalBlocked(tile: Tile): void {
    this.autowalkOverlay.signalBlocked(tile);
  }

  /** Draw a compact arrow at Charge's exact planned landing point. */
  showDungeonChargePreview(
    start: { x: number; y: number },
    end: { x: number; y: number },
  ): void {
    const from = tileToScreen(start.x, start.y, 0);
    const to = tileToScreen(end.x, end.y, 0);
    const dx = to.x - from.x;
    const dy = to.y - from.y;
    const length = Math.hypot(dx, dy);
    this.dungeonChargePreview.clear();
    if (length === 0) return;
    const unitX = dx / length;
    const unitY = dy / length;
    const backX =
      to.x - unitX * DUNGEON_CHARGE_PREVIEW_ARROW_LENGTH_PX;
    const backY =
      to.y - unitY * DUNGEON_CHARGE_PREVIEW_ARROW_LENGTH_PX;
    const sideX = -unitY * DUNGEON_CHARGE_PREVIEW_ARROW_HALF_WIDTH_PX;
    const sideY = unitX * DUNGEON_CHARGE_PREVIEW_ARROW_HALF_WIDTH_PX;
    this.dungeonChargePreview
      .moveTo(from.x, from.y)
      .lineTo(backX, backY)
      .stroke({
        width: DUNGEON_CHARGE_PREVIEW_LINE_WIDTH_PX,
        color: DUNGEON_CHARGE_PREVIEW_COLOR,
        alpha: DUNGEON_CHARGE_PREVIEW_LINE_ALPHA,
      })
      .poly([
        to.x,
        to.y,
        backX + sideX,
        backY + sideY,
        backX - sideX,
        backY - sideY,
      ])
      .fill({
        color: DUNGEON_CHARGE_PREVIEW_COLOR,
        alpha: 0.9,
      });
  }

  /** Hide the Charge preview after commit, cancellation, or run teardown. */
  clearDungeonChargePreview(): void {
    this.dungeonChargePreview.clear();
  }

  /**
   * Play a Blackthorn's Storm spawn-telegraph sparkle on ground tile `(x, y)`
   * (Slice E, ADR-0152), the client half of the {@link
   * ../net/protocol.StormTelegraphMessage} broadcast: a ~2s electric cue warning that
   * a vortex is about to materialize there. `ticksUntil` (server 50ms steps until the
   * summon) drives the sparkle's lifetime so it winks out as the vortex lands; a
   * non-positive/absent countdown falls back to the overlay's default. The telegraphed
   * summons land on the bank floor (z 0), so the sparkle projects at ground elevation.
   */
  showStormTelegraph(x: number, y: number, ticksUntil?: number): void {
    const ttlMs = ticksUntil && ticksUntil > 0 ? ticksUntil * SERVER_TICK_MS : undefined;
    this.telegraphOverlay.telegraph({ x, y }, ttlMs);
  }

  /**
   * Draw one announced area from a telegraphed dungeon cast (PRD #793), the
   * client half of the {@link ../net/protocol.DungeonTelegraphMessage} push.
   *
   * The footprint is derived from the declared shape the message carries, so
   * what is drawn is what will be selected, and it lives exactly as long as the
   * warning the server declared. A shape with no ground area, such as a shot in
   * flight, draws nothing.
   */
  /**
   * Whether this dungeon monster should be showing its health bar right now.
   *
   * The rule is "hurt recently": a drop in replicated health arms the bar for
   * {@link DUNGEON_HEALTH_BAR_MS}, a further hit re-arms it, and a monster back
   * at full health drops it immediately. The budget caps how many are lit at
   * once, so a large fight cannot turn into a wall of bars; the ones closest to
   * expiring lose theirs first, which is also the ones you hit longest ago.
   *
   * Ageing happens here rather than on a clock of its own because every entity
   * is visited exactly once per frame, so one frame of time passes per entry.
   */
  private ageDungeonHealthBars(frameDtMs: number): void {
    const elapsed = Math.max(0, frameDtMs);
    if (elapsed === 0) return;
    for (const entry of this.dungeonHealthBars.values()) {
      if (entry.remainingMs > 0) entry.remainingMs = Math.max(0, entry.remainingMs - elapsed);
    }
  }

  private trackDungeonHealthBar(
    id: EntityId,
    health: { current: number; max: number },
  ): boolean {
    const entry = this.dungeonHealthBars.get(id);
    const dropped =
      entry === undefined
        ? health.current < health.max
        : health.current < entry.current;
    const armed = dropped && health.current > 0;
    const next = entry ?? { current: health.current, max: health.max, remainingMs: 0 };
    next.current = health.current;
    next.max = health.max;
    if (armed) next.remainingMs = DUNGEON_HEALTH_BAR_MS;
    if (health.current >= health.max || health.current <= 0) next.remainingMs = 0;
    this.dungeonHealthBars.set(id, next);
    if (armed) this.evictOldestDungeonHealthBar(id);
    return next.remainingMs > 0;
  }

  /**
   * Keep at most {@link DUNGEON_HEALTH_BAR_BUDGET} bars lit, dropping the one
   * hit longest ago when a fresh hit needs the slot.
   *
   * The eviction reaches the evicted sprite directly rather than waiting for the
   * next frame to notice, because entities are drawn in an order this layer does
   * not choose: a bar taken away after its own sprite was already drawn would
   * otherwise stay lit for a frame, and in a big fight there is a fresh hit
   * every frame.
   */
  private evictOldestDungeonHealthBar(newest: EntityId): void {
    let lit = 0;
    let oldest: EntityId | undefined;
    let oldestRemaining = Number.POSITIVE_INFINITY;
    for (const [id, entry] of this.dungeonHealthBars) {
      if (entry.remainingMs <= 0) continue;
      lit += 1;
      if (id === newest) continue;
      if (
        entry.remainingMs < oldestRemaining ||
        (entry.remainingMs === oldestRemaining && oldest !== undefined && id < oldest)
      ) {
        oldest = id;
        oldestRemaining = entry.remainingMs;
      }
    }
    if (lit <= DUNGEON_HEALTH_BAR_BUDGET || oldest === undefined) return;
    const evicted = this.dungeonHealthBars.get(oldest)!;
    evicted.remainingMs = 0;
    this.sprites.get(oldest)?.setHealthBar(evicted.current, evicted.max, false);
  }

  showDungeonTelegraph(
    shape: AbilityShape,
    anchor: FootprintAnchor,
    ticksUntil: number,
  ): void {
    this.dungeonTelegraphOverlay.announce(shape, anchor, ticksUntil);
  }

  /**
   * Float a spoken line above the given entity's sprite in `color` (Slice 5).
   * Routed to the pooled {@link PlayerSprite} for that entity; the sprite owns the
   * fade. A line for an entity with no sprite yet (a speaker the local client has
   * not seen join) is harmlessly dropped - speech is ephemeral, so there is
   * nothing to catch up. The journal keeps the durable record on its own surface.
   *
   * The dice-cup is also a valid target (dice-and-cup, ADR-0083): the server floats
   * the `*Name rolls d1,d2*` action emote over the **dice** entity, so a line whose
   * entity is a {@link DiceSprite} routes to it instead of a character. An entity is
   * one or the other, never both, so checking each pool in turn is unambiguous.
   */
  showSpeech(entityId: EntityId, text: string, color: number): void {
    this.sprites.get(entityId)?.say(text, color);
    this.diceSprites.get(entityId)?.say(text, color);
  }

  /**
   * Play a body clip **once** on an entity's pooled sprite in the given facing -
   * the client half of the one-shot action-animation broadcast (Rat Swarm Slice
   * B, ADR-0116): the stomper's attack swing, the rat's death clip. Routed to the
   * pooled {@link PlayerSprite}, which owns the playback (plays through once,
   * returns to standing, keeps its overhead labels). An entity with no sprite
   * here (already despawned, or never seen by this client) is a harmless no-op -
   * the animation is ephemeral, so there is nothing to catch up - and the sprite
   * itself no-ops on a clip its body has no baked frames for.
   *
   * `repeatOrActionId` accepts the broadcast's optional repeat count (ADR-0387) or
   * the historical fourth-argument prediction id (ADR-0417). A fifth argument lets
   * a broadcast carry both. The die-clip corpse path ignores both values.
   */
  playActionAnimation(
    entityId: EntityId,
    clip: string,
    facing: Direction,
    repeatOrActionId: number | string = 1,
    actionId?: string,
  ): void {
    const repeat = typeof repeatOrActionId === 'number' ? repeatOrActionId : 1;
    const resolvedActionId =
      typeof repeatOrActionId === 'string' ? repeatOrActionId : actionId;
    // A duel death raises a SEPARATE corpse (corpse/ghost split, ADR-0140): the
    // player entity itself becomes the standing grey shroud ghost (the body-swap
    // look), so the die clip plays on a fallen corpse built from the body + clothes
    // it wore in life - NOT on the player sprite, which must stand as the ghost.
    if (clip === 'die-forward' || clip === 'die-back') {
      // EXCEPT the King's Justice thief (PRD #361, ADR-0194): his entity never
      // ghost-swaps - it IS the corpse, staying in the world grey and hit-testable
      // for the double-click loot - so no separate corpse figure is raised. His
      // prone pose is deliberately NOT driven from this ephemeral broadcast
      // either: it derives from the replicated `thiefCorpse` marker in
      // {@link reconcile} (the broadcast can race the pair's very first snapshot,
      // and a late joiner never hears it at all), so this cue is dropped here -
      // its audible half (the death cry) already fired in main.ts's handler.
      if (this.justiceThieves.has(entityId)) return;
      const livingLook = this.livingLooks.get(entityId);
      if (this.atlasStore && livingLook) {
        void this.raiseLoadedCorpse(entityId, livingLook, clip, facing);
      } else {
        this.raiseCorpse(entityId, clip, facing);
      }
      return;
    }
    if (!this.sprites.has(entityId)) return;
    if (resolvedActionId !== undefined) {
      const key = `${entityId}:${resolvedActionId}`;
      const now = performance.now();
      const presentedAt = this.presentedActionIds.get(key);
      if (presentedAt !== undefined && now - presentedAt < 5_000) return;
      this.presentedActionIds.set(key, now);
      if (this.presentedActionIds.size > 256) {
        for (const [candidate, timestamp] of this.presentedActionIds) {
          if (now - timestamp >= 5_000) this.presentedActionIds.delete(candidate);
        }
        while (this.presentedActionIds.size > 256) {
          const oldest = this.presentedActionIds.keys().next().value;
          if (oldest === undefined) break;
          this.presentedActionIds.delete(oldest);
        }
      }
    }
    const look = this.spriteFrameLooks.get(entityId);
    if (this.atlasStore && look) {
      void this.playLoadedAction(entityId, look, clip, () => {
        this.sprites.get(entityId)?.playAction(clip, facing, repeat);
      });
    } else {
      this.sprites.get(entityId)?.playAction(clip, facing, repeat);
    }
  }

  /**
   * Warm one action clip for the entity's current look.
   * Repeated status pushes share one promise and never rebuild the same layers.
   */
  prepareActionAnimation(entityId: EntityId, clip: string): Promise<void> {
    const store = this.atlasStore;
    const look = this.spriteFrameLooks.get(entityId);
    if (!store || !look || !this.sprites.has(entityId)) {
      return Promise.resolve();
    }
    const lookKey = this.lookKey({ renderable: look });
    const key = `${entityId}:${lookKey}:${clip}`;
    const existing = this.actionPreloads.get(key);
    if (existing) return existing;
    const preload = store.loadClip(look, clip).then(() => {
      if (!this.sprites.has(entityId)) return;
      const currentLook = this.spriteFrameLooks.get(entityId);
      if (!currentLook || this.lookKey({ renderable: currentLook }) !== lookKey) {
        return;
      }
      this.sprites
        .get(entityId)
        ?.setLayers(this.layersFor({ renderable: currentLook }));
    });
    this.actionPreloads.set(key, preload);
    while (this.actionPreloads.size > 256) {
      const oldest = this.actionPreloads.keys().next().value;
      if (oldest === undefined) break;
      this.actionPreloads.delete(oldest);
    }
    return preload;
  }

  private async playLoadedAction(
    entityId: EntityId,
    look: PlayerLook,
    clip: string,
    play: () => void,
  ): Promise<void> {
    const generation = (this.actionLoadGeneration.get(entityId) ?? 0) + 1;
    this.actionLoadGeneration.set(entityId, generation);
    const lookKey = this.lookKey({ renderable: look });
    await this.atlasStore!.loadClip(look, clip);
    if (this.actionLoadGeneration.get(entityId) !== generation) return;
    if (!this.sprites.has(entityId)) return;
    const currentLook = this.spriteFrameLooks.get(entityId) ?? look;
    if (this.lookKey({ renderable: currentLook }) !== lookKey) return;
    this.sprites.get(entityId)?.setLayers(this.layersFor({ renderable: currentLook }));
    play();
  }

  private async raiseLoadedCorpse(
    entityId: EntityId,
    livingLook: PlayerLook,
    clip: string,
    facing: Direction,
  ): Promise<void> {
    // Pin the living look for the corpse BEFORE awaiting the die-clip load (issue
    // #625 review): the entity's own sprite pin has already moved living→ghost by
    // now, so without an early pin the living equipment bundles could be evicted
    // under budget pressure during the await, raising a corpse missing clothing
    // layers. pinLook's pending-pin mechanism protects still-streaming atlases too.
    // Retain-new-then-release-old so a same-key refcount never dips to zero.
    const earlyPin = this.atlasStore?.pinLook(livingLook);
    if (earlyPin) {
      const previous = this.corpsePins.get(entityId);
      this.corpsePins.set(entityId, earlyPin);
      previous?.();
    }
    // Death supersedes any punch/hit/emote still waiting on art. Keep corpse loads
    // on their own token, but preserve the action path's last-event-wins contract.
    this.actionLoadGeneration.set(entityId, (this.actionLoadGeneration.get(entityId) ?? 0) + 1);
    const generation = (this.corpseLoadGeneration.get(entityId) ?? 0) + 1;
    this.corpseLoadGeneration.set(entityId, generation);
    await this.atlasStore!.loadClip(livingLook, clip);
    if (this.corpseLoadGeneration.get(entityId) !== generation) return;
    if (!this.sprites.has(entityId)) return;
    // The replicated player may already be wearing its ghost look by the time the
    // living atlases arrive. The separate corpse deliberately uses the cached living
    // look, so unlike an ordinary action it must not reject that expected look change.
    this.raiseCorpse(entityId, clip, facing);
  }

  private prepareCombatFrames(entityId: EntityId, look: PlayerLook): void {
    const store = this.atlasStore;
    if (!store) return;
    const key = this.lookKey({ renderable: look });
    if (this.combatPrefetchLooks.get(entityId) === key) return;
    this.combatPrefetchLooks.set(entityId, key);
    void Promise.all(COMBAT_STANCE_CLIP_IDS.map((clip) => store.loadClip(look, clip))).then(() => {
      if (this.combatPrefetchLooks.get(entityId) !== key) return;
      const currentLook = this.spriteFrameLooks.get(entityId);
      if (!currentLook || this.lookKey({ renderable: currentLook }) !== key) return;
      this.sprites.get(entityId)?.setLayers(this.layersFor({ renderable: currentLook }));
    });
  }

  /**
   * Settle every deferred visual dependency used by a Sphere opening exchange.
   * Missing clips/effects deliberately resolve through their established fallback;
   * SpriteAtlasStore and the effect loader dedupe concurrent/rematch requests.
   */
  async prepareSphereAssets(entityIds: readonly EntityId[]): Promise<void> {
    const tasks: Promise<unknown>[] = [];
    const effects = this.onEffectArtNeeded?.();
    if (effects) tasks.push(effects);
    if (this.atlasStore) {
      for (const entityId of entityIds) {
        const look = this.spriteFrameLooks.get(entityId);
        if (!look) continue;
        for (const clip of ['cast', 'hit', 'die-forward']) {
          tasks.push(this.atlasStore.loadClip(look, clip));
        }
      }
    }
    await Promise.allSettled(tasks);
    this.effectArtLoadFinished = true;
    for (const entityId of entityIds) {
      const look = this.spriteFrameLooks.get(entityId);
      if (look) this.sprites.get(entityId)?.setLayers(this.layersFor({ renderable: look }));
    }
  }

  /**
   * Play the King's Justice smoke poof at a tile/level (PRD #361, ADR-0194): the
   * baked `poof` effect strip as a one-shot impact on that square, lifted to body
   * height like every tile-anchored FX. Degrades to the particle burst when the
   * effect is unbaked (the ArenaFx graceful-absence rule). `z` matters: the
   * execution spot may be the bank roof plate, and the projection drops a
   * roof-level poof onto the roof's screen height.
   */
  playPoof(x: number, y: number, z: number): void {
    this.onEffectArtNeeded?.();
    const screen = tileToScreen(x, y, z);
    this.arenaFx.impact({ x: screen.x, y: screen.y - TILE_HALF }, 'poof', POOF_RADIUS_TILES, POOF_COLOR);
  }

  /**
   * The keystone-unlock flourish (PRD #378 story 15): a golden sparkle loop +
   * particle burst on the runner as the transcendence lands - the sparkle strip
   * is the "special moment" grammar, degrading to the flicker diamond/burst
   * like every arena effect. A no-op for an entity with no sprite.
   */
  playKeystoneFanfare(entityId: EntityId): void {
    this.onEffectArtNeeded?.();
    const at = this.fxPoint(entityId);
    if (!at) return;
    this.arenaFx.field(at, 'sparkle', KEYSTONE_FANFARE_MS, KEYSTONE_FANFARE_COLOR);
    this.arenaFx.burst(at.x, at.y, KEYSTONE_FANFARE_COLOR, BOSS_BURST_PARTICLES);
  }

  /**
   * The level-up flourish (ADR-0218): a gold particle burst on the runner plus
   * staggered expanding pulse rings, timed with the plink-arpeggio fanfare -
   * the XP orbs' gathering grammar cresting on the body that gathered them.
   * Distinct from the keystone fanfare (no sparkle field: a level-up is a beat,
   * a transcendence is a savour). Each ring re-resolves the runner's live FX
   * point at fire time; a despawned sprite degrades that ring to a no-op.
   */
  playLevelUpFlourish(entityId: EntityId): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    this.arenaFx.burst(at.x, at.y, LEVEL_UP_FLOURISH_COLOR, BOSS_BURST_PARTICLES);
    for (let i = 0; i < LEVEL_UP_RING_COUNT; i++) {
      this.arenaFx.schedule(i * LEVEL_UP_RING_STAGGER_MS, () => {
        const live = this.fxPoint(entityId);
        if (live) this.arenaFx.pulse(live.x, live.y, LEVEL_UP_RING_RADIUS_TILES, LEVEL_UP_FLOURISH_COLOR);
      });
    }
  }

  /**
   * Raise the fallen corpse of a duelist who just died (corpse/ghost split,
   * ADR-0140): a fresh sprite wearing the body + clothes it wore in life ({@link
   * livingLooks}), dropped at the tile the player stood on, playing its die clip
   * once and freezing on the prone last frame (the sprite's own death-hold). The
   * player entity's own sprite is left untouched so the body-swap turns it into the
   * standing shroud ghost. Idempotent per death (one corpse), and a **no-op when the
   * die art is a placeholder stub** (no baked die frames) so a clientless bake shows
   * just the standing ghost rather than a standing duplicate - never a square.
   */
  private raiseCorpse(entityId: EntityId, clip: string, facing: Direction): void {
    if (this.corpses.has(entityId)) return; // one corpse per death
    const look = this.livingLooks.get(entityId);
    const source = this.sprites.get(entityId);
    if (!look || !source) return; // never seen alive: nothing to raise a corpse from
    // Only raise a corpse when the die pose is real, baked art. On a clientless stub
    // bake the die clip is a placeholder (dropped by playerArt), so there is no prone
    // pose to freeze - degrade to just the standing ghost rather than a standing double.
    const bodyClips = this.bodyFrames[look.body ?? DEFAULT_BODY] ?? this.bodyFrames[DEFAULT_BODY];
    const dieFrames = (bodyClips as unknown as Record<string, Record<number, unknown[]>> | undefined)?.[
      clip
    ]?.[facing];
    if (!dieFrames || dieFrames.length === 0) return;

    const corpse = new PlayerSprite(
      buildPlayerLayers(look, this.bodyFrames, this.equipFrames),
      facing,
    );
    corpse.eventMode = 'none'; // inert: never intercept taps meant for entities behind it, nor reveal its empty name label
    corpse.cursor = 'inherit';
    corpse.position.copyFrom(source.position); // falls where the player stood
    corpse.zIndex = source.zIndex - 1; // lies just under the standing ghost
    corpse.playAction(clip, facing); // plays the die once, then holds the prone frame
    this.world.addChild(corpse);
    this.corpses.set(entityId, corpse);
    // The corpse renders the cached living look for as long as it lies there, so pin
    // those atlases for the corpse's lifetime (released by removeCorpse; issue #625).
    const pin = this.atlasStore?.pinLook(look);
    if (pin) {
      this.corpsePins.get(entityId)?.();
      this.corpsePins.set(entityId, pin);
    }
  }

  /**
   * Remove and destroy a dead entity's fallen corpse (corpse/ghost split, ADR-0140):
   * the counterpart to {@link raiseCorpse}, called on resurrection ({@link clearDeath})
   * and on despawn ({@link reconcile}). A no-op when no corpse is standing.
   */
  private removeCorpse(entityId: EntityId): void {
    // The corpse's residency pin releases with it (issue #625).
    this.corpsePins.get(entityId)?.();
    this.corpsePins.delete(entityId);
    const corpse = this.corpses.get(entityId);
    if (!corpse) return;
    this.retireSprite(corpse);
    this.corpses.delete(entityId);
    this.corpseTtl.delete(entityId);
  }

  /**
   * Take a finished character sprite out of the scene: pooled when it is a horde
   * sprite the next spawn can wear (PRD #826), destroyed otherwise.
   *
   * Only horde sprites pool. A player or NPC sprite carries name, title, and bar
   * surfaces built for that one character, and they leave the scene rarely
   * enough that recycling them would buy nothing.
   */
  private retireSprite(sprite: PlayerSprite): void {
    this.world.removeChild(sprite);
    if (sprite instanceof HordeSprite && this.freeHordeSprites.length < HORDE_SPRITE_POOL_MAX) {
      sprite.visible = false;
      this.freeHordeSprites.push(sprite);
      return;
    }
    sprite.destroy({ children: true });
  }

  /**
   * Wipe every fallen corpse this client is holding (corpse/ghost split, ADR-0140): the
   * bulk counterpart to {@link removeCorpse}, driven by the server's broadcast `ClearCorpses`
   * signal at storm end and from the admin "Clear all bodies" control. A Blackthorn's Storm
   * can down many players at once, and each corpse is a pooled sprite ticked every frame - so
   * a lingering mass is a real render cost this clears in one call. Idempotent (a no-op when
   * no corpse stands) and safe: corpses are inert, so nothing depends on them surviving.
   */
  clearAllCorpses(): void {
    for (const entityId of this.corpseLoadGeneration.keys()) this.invalidateCorpseLoad(entityId);
    for (const entityId of [...this.corpses.keys()]) this.removeCorpse(entityId);
  }

  private invalidateCorpseLoad(entityId: EntityId): void {
    this.corpseLoadGeneration.set(entityId, (this.corpseLoadGeneration.get(entityId) ?? 0) + 1);
  }

  /**
   * Float one combat number over an entity's pooled sprite (Player duels, PRD
   * #252, ADR-0131): a landed hit's red damage number over a struck duelist (a
   * miss floats nothing) - the client half of the {@link CombatTextMessage}
   * broadcast, only ever driven for a `damage` outcome. A
   * no-op for an entity with no sprite here (never seen / already despawned); the
   * float is ephemeral, so there is nothing to catch up.
   *
   * `punch` is the arena damage-number punch (PRD #430 Slice B, ADR-0223):
   * magnitude + deterministic scatter seed. Town/duel callers pass nothing and
   * render exactly as before.
   */
  showCombatText(entityId: EntityId, text: string, color: number, punch?: CombatFloatPunch): void {
    this.sprites.get(entityId)?.spawnCombatText(text, color, punch);
  }

  /**
   * Play one baked target effect centered on an entity.
   * This is the shared fixed-effect seam for spells such as Poison, Lightning,
   * Greater Heal, and Cure.
   */
  playImpactFx(entityId: EntityId, effect: ImpactEffectId, color: number): void {
    const play = (): void => {
      const sprite = this.sprites.get(entityId);
      const at = effect === 'lightning' && sprite
        ? { x: sprite.position.x, y: sprite.position.y }
        : this.fxPoint(entityId);
      if (at) this.arenaFx.impact(at, effect, 0, color);
    };
    if (this.effectArt[effect]) play();
    else this.afterEffectArtLoad(play);
  }

  /**
   * The world-local point Arena FX aim at for an entity: its pooled sprite's
   * position lifted half a tile, so effects land on the body art rather than at
   * the feet. `undefined` when the entity has no sprite here (never seen /
   * already despawned) - every FX pass-through below no-ops on that, the same
   * graceful-absence rule as {@link showCombatText}.
   */
  private fxPoint(entityId: EntityId): { x: number; y: number } | undefined {
    const sprite = this.sprites.get(entityId);
    if (!sprite) return undefined;
    return { x: sprite.position.x, y: sprite.position.y - TILE_HALF };
  }

  /**
   * Kick the screen shake (Arena Slice F, ADR-0178): `strength` in `[0, 1]`
   * trauma (see {@link ScreenShake}); repeated kicks cap, the offset caps at
   * {@link SHAKE_MAX_PX}, and the decay runs on the render clock in {@link sync}.
   */
  kickShake(strength: number): void {
    this.shake.kick(strength);
  }

  /** The live shake offset this frame (headless e2e observability). */
  shakeOffset(): { x: number; y: number } {
    return this.shake.offset();
  }

  /**
   * A pooled particle burst over an entity (Arena Slice F, ADR-0178): the
   * enemy-death pop (or the bigger boss-death shower). No sprite = no-op.
   */
  burstAtEntity(entityId: EntityId, color: number, count: number): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    const budget = presentationBudgetFor(this.presentationQuality);
    const bossMultiplier = isArenaBossBody(this.entityBodies.get(entityId)) ? 3 : 1;
    this.arenaFx.burst(
      at.x,
      at.y,
      color,
      Math.min(count, budget.deathParticles * bossMultiplier),
    );
  }

  /**
   * Present the warrior auto-attack as one cohesive local action: a broad
   * crescent over the regular swing and a short visual-only forward lunge.
   */
  playDungeonSweep(
    entityId: EntityId,
    aim: { x: number; y: number },
    radiusTiles: number,
    arcDegrees: number,
  ): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    const screenDelta = tileToScreen(aim.x, aim.y);
    const length = Math.hypot(screenDelta.x, screenDelta.y);
    if (length === 0) return;
    const unitX = screenDelta.x / length;
    const unitY = screenDelta.y / length;
    this.arenaFx.sweep(
      at.x,
      at.y,
      Math.atan2(unitY, unitX),
      radiusTiles,
      arcDegrees,
      0xffd995,
    );
    this.dungeonLunges.set(entityId, { unitX, unitY, ageMs: 0 });
  }

  playDungeonWhirlwind(
    entityId: EntityId,
    facing: Direction,
    radiusTiles: number,
  ): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    this.arenaFx.whirlwind(
      at.x,
      at.y,
      radiusTiles,
      0xffd995,
      () => this.fxPoint(entityId),
    );
    this.dungeonWhirlwinds.set(entityId, {
      startFacing: facing,
      ageMs: 0,
    });
  }

  /** Present the Warrior's timed defensive sparkle. */
  playDungeonAegis(entityId: EntityId, durationMs: number, key?: string): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    this.arenaFx.aegis(at, durationMs, () => this.fxPoint(entityId), key);
  }

  /** Cancel an optimistic Aegis presentation rejected by the server. */
  cancelDungeonAegis(key: string): void {
    this.arenaFx.cancelAegis(key);
  }

  /** Show Piercing Thrust's gameplay length and width along the cursor aim. */
  playDungeonPierce(
    entityId: EntityId,
    aim: { x: number; y: number },
    rangeTiles: number,
    widthTiles: number,
  ): void {
    const at = this.fxPoint(entityId);
    if (!at) return;
    const aimLength = Math.hypot(aim.x, aim.y);
    if (aimLength === 0) return;
    const end = tileToScreen(
      (aim.x / aimLength) * rangeTiles,
      (aim.y / aimLength) * rangeTiles,
    );
    this.arenaFx.pierce(
      at.x,
      at.y,
      end.x,
      end.y,
      widthTiles * TILE_HALF * Math.SQRT2,
      0xd8c9a4,
    );
  }

  /**
   * Start the local Charge speed show.
   * Movement itself comes from the prediction walker; this layer snapshots the
   * live run frame, kicks dust, and shoves contacted monster animators.
   */
  playDungeonCharge(
    entityId: EntityId,
    start: { x: number; y: number },
    end: { x: number; y: number },
    contacts: readonly DungeonChargeContact[],
    durationMs: number,
  ): void {
    const sprite = this.sprites.get(entityId);
    if (!sprite) return;
    const travelX = end.x - start.x;
    const travelY = end.y - start.y;
    const travelSquared = travelX * travelX + travelY * travelY;
    this.dungeonCharges.set(entityId, {
      ageMs: 0,
      durationMs: Math.max(1, durationMs),
      nextAfterimageMs: 0,
      nextDustMs: 0,
      afterimagesSpawned: 0,
      contacts: contacts.map((contact) => ({
        contact,
        progress:
          travelSquared === 0
            ? 0
            : Math.max(
                0,
                Math.min(
                  1,
                  ((contact.from.x - start.x) * travelX +
                    (contact.from.y - start.y) * travelY) /
                    travelSquared,
                ),
              ),
        presented: false,
      })),
    });
    this.kickShake(DUNGEON_CHARGE_LAUNCH_SHAKE);
  }

  /**
   * A projectile tracer between two entities (Arena Slice F, ADR-0178): the
   * runner-to-target weapon-hit flash. Either sprite missing = no-op.
   */
  tracerBetween(fromId: EntityId, toId: EntityId, color: number): void {
    const from = this.fxPoint(fromId);
    const to = this.fxPoint(toId);
    if (from && to) this.arenaFx.tracer(from, to, color);
  }

  /**
   * An expanding aura-pulse ring around an entity (Arena Slice F, ADR-0178): the
   * aura weapon's firing cadence made visible. No sprite = no-op.
   */
  pulseAtEntity(entityId: EntityId, radiusTiles: number, color: number): void {
    const at = this.fxPoint(entityId);
    if (at) this.arenaFx.pulse(at.x, at.y, radiusTiles, color);
  }

  /**
   * Late-bind the baked effect strips into the FX overlay (fire-mage rework,
   * ADR-0181) - they load async at boot; until then (or without a bake) every
   * sprite effect degrades to the Slice F primitives.
   */
  setEffectArt(art: EffectArt): void {
    this.arenaFx.setEffectArt(art);
    // The moongate sprites read the gate/summon-flash strips from the same load
    // (PRD #362 Slice A); until it lands they degrade to the Graphics fallback.
    this.effectArt = art;
    // A first-use request may have created a primitive fallback while the atlas was
    // in flight. Recycle those short-lived pools so the next sync adopts baked art.
    for (const sprite of this.projectileSprites.values()) {
      this.world.removeChild(sprite);
      sprite.dispose();
    }
    this.projectileSprites.clear();
    for (const sprite of this.moongateSprites.values()) {
      this.world.removeChild(sprite);
      sprite.dispose();
    }
    this.moongateSprites.clear();
  }

  /**
   * Do not spend the first use of a deferred effect on its primitive fallback.
   * If a load is available, wait for it and then play the canonical strip.
   * A failed or absent bake still resolves through the overlay's normal fallback.
   */
  private afterEffectArtLoad(play: () => void): void {
    if (this.effectArtLoadFinished || !this.onEffectArtNeeded) {
      play();
      return;
    }
    const loading = this.onEffectArtNeeded();
    if (!loading) {
      this.effectArtLoadFinished = true;
      play();
      return;
    }
    const settle = (): void => {
      this.effectArtLoadFinished = true;
      play();
    };
    void loading.then(settle, settle);
  }

  /** Late-bind the baked blood atlas into the decal layer (loads async at boot).
   * The arena FX overlay shares the same load for the molten ground look
   * (PRD #419 slice B) - one slice of the atlas, two consumers. */
  setBloodArt(textures: BloodTextures): void {
    this.bloodLayer.setTextures(textures);
    this.arenaFx.setBloodArt(textures);
    // Hand the FX overlay the blaze hue value for the molten splat.
    // Without a hue table, the splat degrades to its old flat tint.
    if (this.hueTable) this.arenaFx.setMoltenHue(BLAZE_HUE);
  }

  /**
   * Spray blood decals around victim `tile` from an already-rolled scatter list
   * (Blood splatter, PRD #372, Slice A). The plain client-side primitive the wire
   * message (Slice B) and arena cascade (Slice C) will drive; Slice A's dev hook
   * rolls the list locally. A no-op while the "Show blood" setting is off.
   */
  splatBlood(tile: { x: number; y: number }, splats: readonly SplatRoll[], hue?: number): void {
    this.bloodLayer.splat(tile, splats, hue);
  }

  /** Toggle the per-viewer "Show blood" setting (one early-return in the layer). */
  setBloodEnabled(on: boolean): void {
    this.bloodLayer.setEnabled(on);
  }

  /**
   * The world-local point tile-anchored FX aim at: the tile's ground projection
   * lifted half a tile, matching {@link fxPoint}'s body-height lift for
   * entities so a tile-anchored impact and an entity-anchored one land at the
   * same height on the same square.
   */
  private fxPointAtTile(tile: { x: number; y: number }): { x: number; y: number } {
    const screen = tileToScreen(tile.x, tile.y, 0);
    return { x: screen.x, y: screen.y - TILE_HALF };
  }

  /**
   * Play one fire-mage volley's whole FX cascade (ADR-0181): plan the timed
   * show from the owner-only `ArenaFx` events (staggered flights, impacts a
   * flight later, chains off their parent's landing - {@link planVolleyFx}),
   * then stage every op on the overlay's scheduler. Anchors prefer the live
   * sprite and fall back to the event's resolution-time tile, so a struck
   * enemy that despawned mid-show still anchors its FX; damage floats land at
   * their hit's impact instant through the existing combat-text path (a gone
   * sprite no-ops). The optional `hooks` fire at the show's own instants
   * (ADR-0187): `onEffect` per flight launch / impact landing with the effect
   * id, `onHit` per struck entity at its damage float - main.ts turns them
   * into the throttled spell-FX and monster-hurt cues. The render layer
   * schedules the *moments*; the sound decisions and their throttles stay
   * with the caller.
   */
  playVolleyFx(events: readonly ArenaFxEvent[], dealtColor: number, hooks?: VolleyFxHooks): void {
    if (!this.effectArtLoadFinished && this.onEffectArtNeeded) {
      this.afterEffectArtLoad(() => this.playVolleyFx(events, dealtColor, hooks));
      return;
    }
    const budget = presentationBudgetFor(this.presentationQuality);
    const buildPlan = () => {
      const admitted = admitArenaFxEvents(events, this.presentationQuality);
      return aggregateDamageFloats(
        planVolleyFx(admitted.events, (tile, entity) =>
          (entity !== undefined ? this.fxPoint(entity) : undefined) ?? this.fxPointAtTile(tile),
        ),
        budget.damageTextWindowMs,
      );
    };
    const plan = this.performanceMonitor
      ? this.performanceMonitor.measure('effect-planning', buildPlan)
      : buildPlan();
    this.effectOperationsPlanned += plan.length;
    if (plan.length >= 16) this.largeEffectCascades += 1;
    // The damage-number punch's scatter seed (ADR-0223): deterministic per push
    // (target x per-plan float index - no Math.random on the render path), so two
    // hits on one target in the same volley still fan apart.
    const schedulePlan = () => {
      let floatIndex = 0;
      for (const op of prioritizePlannedFx(plan)) {
        const schedule = (fire: () => void): void => {
          this.effectOperationsScheduled += 1;
          this.arenaFx.schedule(op.atMs, fire, op.priority);
        };
        switch (op.op) {
          case 'flight': {
            // Home the tracer onto the struck enemy's live position (ADR-0216) so it
            // lands on a mover; falls back to the fixed endpoint when the flight
            // locks no single target (a pierced serpent / nova ray).
            const targetId = op.target;
            const liveTarget =
              targetId !== undefined ? () => this.fxPoint(targetId) : undefined;
            schedule(() => {
              this.arenaFx.projectile(
                op.from,
                op.to,
                op.effect,
                op.sizeTier,
                dealtColor,
                liveTarget,
              );
              hooks?.onEffect?.(op.effect);
            });
            break;
          }
          case 'impact':
            schedule(() => {
              this.arenaFx.impact(op.at, op.effect, op.radiusTiles, dealtColor);
              hooks?.onEffect?.(op.effect);
            });
            break;
          case 'float': {
            // Seed fixed at plan time (the schedule closure must not race the
            // counter); magnitude is the raw damage - the pure layer derives the
            // log-curve scale, pop, scatter, and white-hot grade (ADR-0223).
            const punch: CombatFloatPunch = {
              magnitude: op.damage,
              seed: combatFloatSeed(op.target, floatIndex++),
            };
            const viaEffect = op.viaEffect;
            schedule(() => {
              this.showCombatText(
                op.target,
                String(op.damage),
                op.ground ? 0xff8a24 : dealtColor,
                op.ground ? undefined : punch,
              );
              // The struck enemy reacts at the same instant its number floats
              // (PRD #430 Slice A): white hit flash + stagger hold, renderer-
              // internal since both act on renderer-owned objects (the pooled
              // sprite and its step animator). The onHit hook beside it stays
              // the caller's seam for sound/blood decisions.
              if (!op.ground) this.flashHit(op.target);
              hooks?.onHit?.(op.target, viaEffect);
            });
            break;
          }
          case 'ground': {
            // `op.art` is the molten splat's deterministic blood-static pick (PRD
            // #419 slice B); strip/sparkle ops carry none. The archer's poison /
            // caltrop grounds (ADR-0237) tint their unbaked Graphics degrade apart
            // from fire (a baked strip ignores the colour).
            const groundColor = ARENA_GROUND_FX_COLOR[op.effect] ?? dealtColor;
            const effect = op.effect;
            const durationMs = op.durationMs;
            const at = op.at;
            const art = op.art;
            schedule(() => {
              this.arenaFx.field(at, effect, durationMs, groundColor, art);
            });
            break;
          }
          case 'hurl':
            // Comet knockback (PRD #419 slice E): slide the struck enemy's own
            // sprite strike tile -> landing tile at the impact instant, via its
            // step animator (a 3-tile relocation would otherwise snap - the
            // multi-tile replay budget is 2). The animator's hurl also absorbs
            // the matching snapshot observation, whichever side arrives first,
            // so the slide and the authoritative stream never fight. A gone
            // sprite (the enemy despawned mid-show) no-ops.
            schedule(() => {
              this.animators.get(op.target)?.hurl(op.fromTile, op.toTile, op.durationMs);
            });
            break;
          case 'status': {
            // Archer DoT tick blips (ADR-0237): a tiny status-coloured spurt over
            // each marked tile - purely cosmetic (the damage already floated/landed).
            const marks = op.marks;
            schedule(() => {
              for (let index = 0; index < marks.length; index += budget.decorativeStride) {
                const mark = marks[index]!;
                this.arenaFx.burst(
                  mark.at.x,
                  mark.at.y,
                  ARENA_STATUS_MARK_COLOR[mark.status],
                  3,
                );
              }
            });
            break;
          }
        }
      }
    };
    if (this.performanceMonitor) {
      this.performanceMonitor.measure('effect-scheduling', schedulePlan);
    } else {
      schedulePlan();
    }
  }

  /**
   * Lift a downed duelist's held death pose (Player duels, PRD #252, ADR-0131):
   * the client half of resurrection, called when a duelist's replicated health
   * jumps back to full. Hands the sprite back to its ordinary idle/gait machines.
   * A no-op for an entity with no sprite, or one not holding a death frame.
   */
  clearDeath(entityId: EntityId): void {
    // Resurrection clears the fallen corpse (corpse/ghost split, ADR-0140) and lifts
    // any held pose on the player sprite (defensive - the player stands as the ghost
    // now, so it holds no die frame, but the call stays safe and idempotent).
    this.actionLoadGeneration.set(entityId, (this.actionLoadGeneration.get(entityId) ?? 0) + 1);
    this.invalidateCorpseLoad(entityId);
    this.removeCorpse(entityId);
    this.sprites.get(entityId)?.clearDeath();
  }

  /**
   * Wipe every character's overhead speech stack immediately. Called on tab refocus
   * (main.ts's `visibilitychange` handler): overhead lifetime rides the rAF-backed
   * Pixi ticker, which the browser pauses on a hidden tab, while the socket keeps
   * delivering barks - so without this the whole hidden-period backlog appears at once
   * on return and fades together (the "chat flood"). Only the transient overhead
   * surface is cleared; the journal (main.ts) keeps the durable player-speech record.
   */
  clearAllSpeech(): void {
    for (const sprite of this.sprites.values()) sprite.clearSpeech();
  }

  /**
   * Reveal (or top up) the overhead name of every pooled character at once - the
   * "show all names" hotkey (issue #184). Reuses {@link PlayerSprite.revealName}, so
   * each name inherits the same per-viewer line-of-sight gating a single reveal
   * does: a character across the bank wall stays dark until the viewer can see it.
   * "Nearby" falls out of the sprite pool + viewport cull naturally - only on-screen,
   * same-region characters end up with a visible label.
   */
  revealAllNames(): void {
    for (const sprite of this.sprites.values()) sprite.revealName();
  }

  /**
   * Hold the "show all names" reveal on or off (issue #184). While on, {@link sync}
   * re-reveals every name each frame so none fade; turning it on reveals them
   * immediately so the hotkey feels instant even before the next frame. Turning it
   * off lets the existing 4s fade timers run out on their own.
   */
  setForceNamesVisible(on: boolean): void {
    this.forceNamesVisible = on;
    if (on) this.revealAllNames();
  }

  /**
   * Turn the war-mode interaction gate on/off (Rat Swarm Slice E, ADR-0120).
   * Purely client-local (no stance replication, per the PRD): it arms the rat
   * hover-highlight and click-to-pursue wiring installed in {@link reconcile}.
   * Turning it off sweeps any live highlight so no rat stays grey; the cursor
   * swap is `main.ts`'s side of the same toggle (see `render/gameCursor.ts`).
   */
  setWarMode(on: boolean): void {
    if (this.warMode === on) return;
    this.warMode = on;
    if (!on) {
      for (const id of this.ratIds) this.sprites.get(id)?.setHighlight(null);
    }
  }

  /** The grey highlight hue carried in each sprite's batch data. */
  private highlightHue(): number {
    return GREY_NOTORIETY_HUE;
  }

  /**
   * Arm/disarm the duel target cursor (Player duels, PRD #252, ADR-0130) - the
   * duel twin of {@link setWarMode}. While armed it enables the blue player/NPC
   * hover-highlight and click-to-select wiring installed in {@link reconcile};
   * disarming sweeps any live blue highlight so no target stays tinted. The cursor
   * tint swap is `main.ts`'s side of the same toggle (see `render/gameCursor.ts`).
   */
  setDuelTargeting(on: boolean): void {
    if (this.duelMode === on) return;
    this.duelMode = on;
    if (!on) {
      for (const id of this.duelTargetIds) this.sprites.get(id)?.setHighlight(null);
      for (const id of this.ignoreWiredIds) this.sprites.get(id)?.setHighlight(null);
    }
  }

  /** The blue duel highlight hue carried in each sprite's batch data. */
  private duelHighlight(): number {
    return BLUE_DUEL_HUE;
  }

  /**
   * React one struck enemy to a landed skill hit (Arena hit reactions, PRD #430
   * Slice A, ADR-0225): restart its pooled {@link HitFlash} (full white, then
   * the ~170ms decay) and hold its {@link StepAnimator} glide for
   * {@link STAGGER_MS} (halved for a boss - shrugging through fire is part of
   * its read). Fired by {@link playVolleyFx} at each hit's damage-float
   * instant, the same staged moment the `onHit` hook (blood + hurt yelps in
   * `main.ts`) rides. A target despawned by render time has no sprite and is a
   * harmless no-op - the yelp path's dead-by-render-time guard. Purely
   * presentation: position authority and the sim are untouched.
   */
  private flashHit(target: EntityId): void {
    if (!this.sprites.has(target)) return;
    let flash = this.hitFlashes.get(target);
    if (!flash) {
      flash = new HitFlash();
      this.hitFlashes.set(target, flash);
    }
    flash.trigger();
    const boss = isArenaBossBody(this.entityBodies.get(target));
    this.animators.get(target)?.stagger(boss ? STAGGER_MS / 2 : STAGGER_MS);
  }

  /**
   * The authoritative pose + timings a remote mobile's {@link StepAnimator}
   * observes, read off the entity's serialized movement state: the resting tile
   * and facing (what the step queue diffs), the level, and the per-tile/turn
   * durations its synthesized steps animate at (with `stepRunning` picking the
   * gait, so a runner replays at run pace).
   *
   * `z` is the entity's **level** (`GameMap.levelAt`), never the raw mirrored
   * terrain z (terraced land, PRD #536 Slice C): on a heighted map a ground
   * walker's `position.z` changes with every slope step, and the animator
   * treats any z change as a level change - a teleport, snapped rather than
   * replayed - which would jerk every remote walker up a ramp. Normalizing to
   * the level keeps ground steps gliding; a real plate/teleporter change still
   * snaps. On a flat map `levelAt` is exactly the old `z ?? 0`.
   */
  private observedPose(
    map: { levelAt: (x: number, y: number, z: number | undefined) => number },
    entity: {
      mobile?: { toState: () => MobileState };
      position?: { z?: number };
    },
  ): ObservedPose {
    const s = entity.mobile!.toState();
    return {
      x: s.x,
      y: s.y,
      z: map.levelAt(Math.round(s.x), Math.round(s.y), entity.position?.z),
      facing: s.dir,
      msPerTile: s.msPerTile,
      msPerTileRun: s.msPerTileRun,
      turnDelayMs: s.turnDelayMs,
      running: s.stepRunning,
    };
  }

  /**
   * Reflect the world's renderable entities into the scene and depth-sort them.
   * `frameDtMs` drives the walk-cycle animation and the step-queue playback
   * (pass 0 for a static refresh). Grid-stepped remote mobiles draw from their
   * per-entity {@link StepAnimator}; marked dungeon enemies follow collision-
   * constrained fractional observations; the local player draws from the
   * prediction pose set via {@link setLocalPose} when one is present.
   */
  sync(world: World, frameDtMs: number): void {
    if (!this.replicaDrivenReconciliation) {
      this.rebuildPresentationIndex(world);
      if (this.performanceMonitor) {
        this.performanceMonitor.measure('scene-reconciliation', () => this.reconcile());
      } else {
        this.reconcile();
      }
    }
    const animationStartedAt = this.performanceMonitor ? performance.now() : 0;
    // Age every hurt-monster bar once, before any entity is drawn, so each one
    // ranks against the same instant rather than against whichever entries the
    // entity loop happened to reach first (PRD #793).
    this.ageDungeonHealthBars(frameDtMs);

    // Advance each fallen corpse's die clip to its frozen prone frame (corpse/ghost
    // split, ADR-0140): a corpse is not an entity, so it is ticked here rather than in
    // the entity loop. It never moves or gaits - the update only carries the one-shot
    // die clip to its held last frame, where the sprite's death-hold pins it.
    // An arena horde corpse (ADR-0304) additionally ages against its client TTL,
    // fading over its last second; a duel corpse (no TTL entry) persists until
    // resurrection/despawn/ClearCorpses exactly as before.
    for (const [id, corpse] of this.corpses) {
      corpse.update(frameDtMs, false, false);
      const ttl = this.corpseTtl.get(id);
      if (ttl === undefined) continue;
      const remaining = ttl - frameDtMs;
      if (remaining <= 0) {
        this.removeCorpse(id);
      } else {
        this.corpseTtl.set(id, remaining);
        if (remaining < ARENA_CORPSE_FADE_MS) corpse.alpha = remaining / ARENA_CORPSE_FADE_MS;
      }
    }

    // Ease the applied zoom toward its target with frame-rate-independent
    // exponential smoothing, then scale the world container about its origin. A
    // static refresh (frameDtMs 0, e.g. tests) holds the zoom steady. Pixi scales
    // every child's local point about that origin, which is why the camera centring
    // below must factor the zoom in - otherwise the player drifts off-centre.
    if (frameDtMs > 0 && this.zoom !== this.targetZoom) {
      const k = 1 - Math.exp(-ZOOM_SMOOTH_PER_MS * frameDtMs);
      this.zoom += (this.targetZoom - this.zoom) * k;
      if (Math.abs(this.targetZoom - this.zoom) < ZOOM_SNAP_EPSILON) this.zoom = this.targetZoom;
    }
    this.world.scale.set(this.zoom);

    // Statics are added to the sort below, after the camera tile is known, so the
    // viewport-cull window can prune off-screen ones. Mobiles (and then doors) are
    // pushed as they are processed; sort order among them is irrelevant since the
    // depth rule fully orders the combined set.
    const sortables: Sortable[] = [];

    // Walk-on surface depth fix (ADR-0168): the tiles each mobile currently
    // straddles, mapped to that mobile's *own* fractional sort position (x, y, z).
    // A flat surface a mobile stands on (roof cap at BANK_ROOF_Z, first-floor
    // carpet at z 0) is a `Layer.Static` on the mobile's tile; standing, it ties
    // the mobile on `x + y` and `z` and loses the layer tie-break, so the mobile
    // draws on top. Walking, the mobile's fractional `x + y` no longer matches the
    // tile it is stepping onto, so that tile's surface wins the primary `x + y`
    // comparison and clips through the body. Re-anchoring such a surface to the
    // straddling mobile's own sort key (see the static re-push below) restores the
    // resting tie so the mobile stays on top for the whole step, without touching
    // the mobile's own key (its occlusion against walls one tile away is unchanged).
    const surfaceAnchorByTile = new Map<string, { x: number; y: number; z: number }>();

    let cameraScreen: { x: number; y: number } | undefined;
    let cameraTile: { x: number; y: number } | undefined;
    let cameraSightTile: { x: number; y: number } | undefined;
    // Each drawn mobile with the discrete tile (and level) that places it in an
    // inside/outside region, for the per-viewer overhead line-of-sight pass
    // below (ADR-0017).
    const overhead: { id: EntityId; sprite: PlayerSprite; tile: Tile; elevation: number }[] = [];
    // The camera target's level: a viewer standing on a roof plate keeps that
    // roof visible (they are *on* it, not under it) and counts as outside for
    // the overhead line-of-sight pass.
    let cameraElevation = 0;

    for (const entity of this.mobileEntities) {
      const mobile = entity.mobile;
      if (!mobile) continue;
      const sprite = this.sprites.get(entity.id)!;
      // The local player's prediction-driven pose wins over its snapshot mobile:
      // the walker owns its motion, and the snapshot lags it by RTT.
      const local = entity.id === this.cameraTarget ? this.localPose : undefined;

      let tile: Tile;
      // The discrete tile the entity occupies places it in/out of a building,
      // driving both roof-hiding (camera target) and overhead line-of-sight.
      let entityTile: Tile;
      let elevation: number;
      // Replicated duel combat stance (ADR-0131): a flag on the renderable, so
      // both duelists and every bystander see the combat pose. Pushed each frame
      // (not via lookKey - it swaps clip selection, not the layer stack).
      const horde = entity.horde === true;
      const combatStance = !horde && entity.renderable?.combatStance === true;
      if (!horde) {
        sprite.setCombatStance(combatStance);
        if (combatStance) {
          this.prepareCombatFrames(entity.id, this.cloneLook(entity.renderable));
        } else {
          this.combatPrefetchLooks.delete(entity.id);
        }
      }
      // The overhead duel health bar (Player duels, PRD #252): surface the fighter's
      // replicated health while they carry the combat stance, hidden otherwise. Both
      // are replicated, so every client draws the same bar for the same fighter.
      const health = entity.health;
      const arenaHealth = this.arenaRunnerHealth?.id === entity.id ? this.arenaRunnerHealth : undefined;
      const dungeonHurt =
        entity.dungeonEnemy === true && health !== undefined
          ? this.trackDungeonHealthBar(entity.id, health)
          : false;
      const sphereVitals = this.sphereVitals.get(entity.id);
      if (!horde) {
        sprite.setHealthBar(
          sphereVitals?.health ?? arenaHealth?.current ?? health?.current ?? 0,
          sphereVitals ? 100 : arenaHealth?.max ?? health?.max ?? 0,
          sphereVitals !== undefined ||
            arenaHealth !== undefined ||
            dungeonHurt ||
            (entity.renderable?.combatStance === true && health !== undefined),
          sphereVitals !== undefined || arenaHealth !== undefined,
          sphereVitals?.poisoned === true,
        );
        sprite.setManaBar(sphereVitals?.mana ?? 0, 100, sphereVitals?.mana !== undefined);
      }
      // The banishment overhead countdown (PRD #362 Slice D): the replicated
      // `banish.remainingTicks` refreshes ~1/s; anchor a wall-clock deadline on
      // each push and tick the mm:ss down between pushes on the render clock -
      // the storm-board extrapolation pattern, per entity.
      if (!horde) this.syncBanishCountdown(entity.id, sprite, entity.banish?.remainingTicks);
      if (local) {
        // Prediction supplies a ready fractional tile, level, facing, and gait.
        sprite.setFacing(local.facing);
        sprite.update(frameDtMs, local.isMoving, local.isRunning);
        tile = { x: local.x, y: local.y };
        entityTile = { x: Math.round(local.x), y: Math.round(local.y) };
        // The walker's z is a LEVEL selector (ADR-0269); a ground-level pose
        // grounds onto the terrain under its tile (terraced land, PRD #536
        // Slice C). Flat map: landZ is 0, exactly the old `local.z`.
        elevation = world.map.groundedZ(entityTile.x, entityTile.y, local.z);
      } else if (entity.dungeonEnemy === true && entity.position) {
        const mobileState = mobile.toState();
        const pose = {
          x: entity.position.x,
          y: entity.position.y,
          z: world.map.levelAt(
            Math.round(entity.position.x),
            Math.round(entity.position.y),
            entity.position.z,
          ),
          facing: entity.position.facing,
          speedTilesPerSecond: 1000 / Math.max(1, mobileState.msPerTile),
        };
        let animator = this.continuousAnimators.get(entity.id);
        if (!animator) {
          animator = new ContinuousPositionAnimator(pose);
          this.continuousAnimators.set(entity.id, animator);
        } else if (!sprite.deathHeld) {
          animator.observe(pose);
        }
        if (!sprite.deathHeld) {
          const obstacles = world.all().flatMap((other) => {
            if (other.id === entity.id) return [];
            if (other.id === this.cameraTarget) {
              const playerPoint = this.localPose ?? other.position;
              if (!playerPoint) return [];
              const playerLevel =
                this.localPose?.z ??
                world.map.levelAt(
                  Math.round(other.position!.x),
                  Math.round(other.position!.y),
                  other.position!.z,
                );
              return playerLevel === pose.z
                ? [{
                    x: playerPoint.x,
                    y: playerPoint.y,
                    radius: DUNGEON_PLAYER_COLLISION_RADIUS_TILES,
                  }]
                : [];
            }
            if (other.dungeonEnemy !== true || !other.position) return [];
            const otherAnimator = this.continuousAnimators.get(other.id);
            const otherPoint =
              otherAnimator?.renderPosition() ?? other.position;
            const otherLevel =
              otherAnimator?.z ??
              world.map.levelAt(
                Math.round(other.position.x),
                Math.round(other.position.y),
                other.position.z,
              );
            return otherLevel === pose.z
              ? [{
                  x: otherPoint.x,
                  y: otherPoint.y,
                  radius: DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
                }]
              : [];
          });
          animator.update(
            frameDtMs,
            (start, displacement, level) =>
              sweepDungeonDisplacement(world, start, displacement, {
                level,
                radius: DUNGEON_ENEMY_COLLISION_RADIUS_TILES,
                subdivision: DUNGEON_ENEMY_COLLISION_SUBDIVISION_TILES,
                obstacles,
              }),
          );
        }
        sprite.setFacing(animator.facing);
        sprite.update(
          frameDtMs,
          !sprite.deathHeld && animator.isMoving,
          false,
        );
        tile = animator.renderPosition();
        entityTile = { x: Math.round(tile.x), y: Math.round(tile.y) };
        elevation = world.map.groundedZ(
          entityTile.x,
          entityTile.y,
          animator.z,
        );
      } else {
        // Remote: feed the step animator this frame's observed pose (a no-op
        // between snapshots - the pose only changes when a snapshot lands),
        // play its queue forward, and draw the replayed step.
        let animator = this.animators.get(entity.id);
        const pose = this.observedPose(world.map, entity);
        if (!animator) {
          animator = new StepAnimator(pose);
          this.animators.set(entity.id, animator);
        } else if (!sprite.deathHeld) {
          // A death-held sprite is a corpse-in-waiting and a corpse does not walk
          // (ADR-0304's sliding-corpse fix): the die beat can land while the mob
          // still has an in-flight/queued step (its parked sim mobile commits that
          // last step a tick after death), so the animator stops observing AND
          // stops replaying the moment the die clip pins - the body lies exactly
          // where the viewer saw it fall. The stale pose is dropped with the
          // animator at despawn; only players resurrect, and a player sprite never
          // death-holds (duel deaths raise a separate ADR-0140 corpse figure).
          animator.observe(pose);
        }
        if (!sprite.deathHeld) animator.update(frameDtMs);
        sprite.setFacing(animator.facing);
        sprite.update(frameDtMs, !sprite.deathHeld && animator.isMoving, animator.isRunning);
        const p = animator.renderPosition();
        tile = p;
        entityTile = { x: Math.round(p.x), y: Math.round(p.y) };
        // The animator's z is the LEVEL (see observedPose); a ground-level
        // entity stands on the terrain under its *replayed* tile, so a walker
        // climbing a terraced ramp rises step by step with the ground it is
        // drawn on (terraced land, PRD #536 Slice C). Flat map: landZ is 0
        // and a plate level passes through - exactly the old `animator.z`.
        elevation = world.map.groundedZ(entityTile.x, entityTile.y, animator.z);
      }
      const whirlwind = this.dungeonWhirlwinds.get(entity.id);
      if (whirlwind) {
        whirlwind.ageMs += frameDtMs;
        if (whirlwind.ageMs >= DUNGEON_WHIRLWIND_TTL_MS) {
          this.dungeonWhirlwinds.delete(entity.id);
        } else {
          const facingStep = Math.min(
            7,
            Math.floor(whirlwind.ageMs / DUNGEON_WHIRLWIND_FACING_MS),
          );
          sprite.setActionFacing(
            ((whirlwind.startFacing + facingStep) % 8) as Direction,
          );
        }
      }
      // Age this entity's hit flash and write its quantised whiten mix into the
      // sprite batch data (PRD #430 Slice A, ADR-0225). An idle machine clears
      // the mix and costs nothing further; entities
      // never struck have no machine at all.
      const flash = this.hitFlashes.get(entity.id);
      if (flash) {
        flash.update(frameDtMs);
        const level = flash.idle ? 0 : hitFlashLevel(flash.intensity());
        sprite.setFlashMix(level / HIT_FLASH_LEVELS);
      }
      // The entity's level lifts it up the screen exactly as an authored static
      // at that z is lifted, so a rooftop walker stands on the roof plate.
      const screen = tileToScreen(tile.x, tile.y, elevation);
      sprite.position.set(screen.x, screen.y);
      // Per-entity visual scale (larger enemies): multiplies rendered size only,
      // never the sim. `setVisualScale` keeps the feet planted on the tile at any
      // scale (Pixi scales about the origin, but the ground point sits below it),
      // so it also owns the sprite's y - pass the just-set `screen.y` through.
      sprite.updateVisualScale(entity.renderable?.scale ?? 1, screen.y, frameDtMs);
      const lunge = this.dungeonLunges.get(entity.id);
      if (lunge) {
        lunge.ageMs += frameDtMs;
        if (lunge.ageMs >= DUNGEON_LUNGE_TTL_MS) {
          this.dungeonLunges.delete(entity.id);
        } else {
          const progress = lunge.ageMs / DUNGEON_LUNGE_TTL_MS;
          const distance = Math.sin(progress * Math.PI) * DUNGEON_LUNGE_PX;
          sprite.position.set(
            sprite.position.x + lunge.unitX * distance,
            sprite.position.y + lunge.unitY * distance,
          );
        }
      }
      const charge = this.dungeonCharges.get(entity.id);
      if (charge) {
        charge.ageMs += frameDtMs;
        const travelProgress = dungeonRushProgress(
          charge.ageMs / charge.durationMs,
        );
        for (const pending of charge.contacts) {
          if (pending.presented || travelProgress < pending.progress) continue;
          pending.presented = true;
          const contact = pending.contact;
          const at = tileToScreen(contact.from.x, contact.from.y, 0);
          this.arenaFx.burst(
            at.x,
            at.y + TILE_HALF,
            0xc9ad78,
            DUNGEON_CHARGE_IMPACT_DUST_PARTICLES,
          );
          this.kickShake(DUNGEON_CHARGE_CONTACT_SHAKE);
          if (contact.to) {
            this.animators
              .get(contact.target)
              ?.hurl(contact.from, contact.to, DUNGEON_CHARGE_KNOCK_SLIDE_MS);
            this.continuousAnimators
              .get(contact.target)
              ?.hurl(contact.from, contact.to, DUNGEON_CHARGE_KNOCK_SLIDE_MS);
          }
        }
        const afterimageInterval =
          charge.durationMs / DUNGEON_CHARGE_AFTERIMAGE_COUNT;
        while (
          charge.afterimagesSpawned < DUNGEON_CHARGE_AFTERIMAGE_COUNT &&
          charge.ageMs >= charge.nextAfterimageMs
        ) {
          const afterimage = sprite.createAfterimage(0.34);
          this.world.addChild(afterimage);
          this.dungeonAfterimages.push({ sprite: afterimage, ageMs: 0 });
          charge.afterimagesSpawned += 1;
          charge.nextAfterimageMs += afterimageInterval;
        }
        while (
          charge.nextDustMs < charge.durationMs &&
          charge.ageMs >= charge.nextDustMs
        ) {
          this.arenaFx.burst(
            sprite.position.x,
            sprite.position.y + TILE_HALF,
            0xbfa36f,
            DUNGEON_CHARGE_TRAVEL_DUST_PARTICLES,
          );
          charge.nextDustMs += DUNGEON_CHARGE_DUST_INTERVAL_MS;
        }
        if (charge.ageMs >= charge.durationMs) {
          this.dungeonCharges.delete(entity.id);
        }
      }
      if (!horde) overhead.push({ id: entity.id, sprite, tile: entityTile, elevation });
      if (entity.id === this.cameraTarget) {
        cameraScreen = screen;
        cameraTile = entityTile;
        cameraSightTile = tile;
        cameraElevation = elevation;
        this.lastPlayerTile = cameraTile;
      }

      sortables.push({
        x: tile.x,
        y: tile.y,
        z: elevation,
        layer: Layer.Mobile,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });

      // Record the integer tiles this mobile's foot straddles this frame, each
      // anchored to the mobile's own fractional sort key, so a surface on any of
      // them re-anchors and draws behind the mobile (see `surfaceAnchorByTile`).
      // At rest the position is integral, so this is just the mobile's own tile -
      // where the depth rule already draws it on top, leaving standing unchanged.
      const anchor = { x: tile.x, y: tile.y, z: elevation };
      for (const [ix, iy] of straddledTiles(tile.x, tile.y)) {
        const key = `${ix},${iy}`;
        const prior = surfaceAnchorByTile.get(key);
        // When two mobiles straddle one tile, the higher one owns the surface
        // (a rooftop walker over a ground walker, say); ties keep the first.
        if (!prior || elevation > prior.z) surfaceAnchorByTile.set(key, anchor);
      }
    }

    // Spectator backdrop: with no entity target but a fixed camera tile set, centre
    // the view on that tile (the player spawn tile) so the login screen frames the alive
    // world. The entity loop above leaves `cameraScreen`/`cameraTile` undefined when
    // no target entity was drawn; fill them from the fixed tile so culling, roof-
    // hiding, overhead line-of-sight, and the camera offset below all behave exactly
    // as they do for a followed player - just anchored to a tile, not an entity.
    if (!cameraScreen && this.fixedCameraTile) {
      cameraTile = {
        x: Math.round(this.fixedCameraTile.x),
        y: Math.round(this.fixedCameraTile.y),
      };
      // Anchor the fixed camera on the terrain under its tile (terraced land,
      // PRD #536 Slice C) - landZ is 0 on a flat map, today's exact framing.
      cameraScreen = tileToScreen(
        this.fixedCameraTile.x,
        this.fixedCameraTile.y,
        world.map.landZ(cameraTile.x, cameraTile.y),
      );
      this.lastPlayerTile = cameraTile;
    }

    // Viewport culling: only statics the camera can see take part in the depth
    // sort, the sortable world container Pixi re-sorts each frame, and the GPU draw
    // set. With no camera target (the depth-integration tests, the pre-Welcome
    // state) `cameraTile` is undefined and we cull nothing, rendering every static
    // exactly as before. The window is recomputed each frame from the camera tile,
    // the live screen size, and the applied zoom (not the target) so it matches
    // what is actually painted this frame.
    const bounds: TileBounds | undefined = cameraTile
      ? visibleTileBounds(
          cameraTile,
          this.app.screen.width,
          this.app.screen.height,
          this.zoom,
          this.cullMargin,
        )
      : undefined;

    // Re-cull only when the camera window actually moved. The two loops below sweep
    // the *whole* map (~29.5k statics, ~33.3k terrain tiles); their entire output -
    // which statics are world-container children, which static sortables exist, and
    // every terrain sprite's `.visible` - is a pure function of `bounds`. So when
    // `bounds` is byte-identical to last frame's (a still camera, or the player
    // moving fractionally within one tile - the common case) they would recompute
    // the exact same state, and we skip both entirely, reusing the cached static
    // sortables. Zoom easing changes `bounds` each frame until it settles, so a
    // moving or zooming camera still re-culls every frame; the win is the steady
    // state. Output is pixel-identical either way.
    if (!this.hasCulled || !sameBounds(bounds, this.lastCullBounds)) {
      this.cullRecomputes += 1;
      const staticSortables: Sortable[] = [];
      let activeStatics: readonly StaticBillboard[];
      if (this.staticScene) {
        const update = this.staticScene.update(bounds);
        for (const billboard of update.entered) {
          this.world.addChild(billboard.sprite);
          for (const configure of this.staticActivators.get(billboard.name ?? '') ?? []) {
            configure(billboard.sprite);
          }
        }
        activeStatics = this.staticScene.activeBillboards;
      } else {
        const legacyStatics = this.statics as readonly StaticBillboard[];
        for (const s of legacyStatics) {
          const onScreen = !bounds || inBounds(bounds, s.x, s.y);
          if (bounds) {
            const added = this.addedStatics.has(s);
            if (onScreen && !added) {
              this.world.addChild(s.sprite);
              this.addedStatics.add(s);
            } else if (!onScreen && added) {
              this.world.removeChild(s.sprite);
              this.addedStatics.delete(s);
            }
          }
        }
        activeStatics = legacyStatics.filter((s) => !bounds || inBounds(bounds, s.x, s.y));
      }
      for (const s of activeStatics) {
        staticSortables.push({
          x: s.x,
          y: s.y,
          z: s.z,
          layer: s.layer,
          roof: s.roof,
          foliage: s.foliage,
          apply: (z: number) => {
            this.stampDepth(s.sprite, z);
          },
        });
      }
      this.cachedStaticSortables = staticSortables;

      // Terrain culling: the ground sprites never sort (terrain is one sub-container
      // at TERRAIN_Z), but ~33k of them still cost GPU, so toggle each one's
      // visibility by the same window. No camera target -> leave them all visible.
      if (bounds) {
        for (const t of this.terrainSprites) t.sprite.visible = inBounds(bounds, t.x, t.y);
      }

      // Sloped-mesh ground (PRD #536 Slice E): same window, chunk-granular.
      this.meshTerrain?.cull(bounds);

      // Open-plain and bounded flat ground follow the same camera window.
      // A single-tile plain updates two repeated planes; varied plains and bounded
      // maps update only the sprites entering and leaving the window.
      const updateTerrainWindow = (): void => {
        this.proceduralTerrain?.update(bounds);
        this.windowedTerrain?.update(bounds);
      };
      if (this.performanceMonitor) {
        this.performanceMonitor.measure('terrain-window', updateTerrainWindow);
      } else {
        updateTerrainWindow();
      }

      this.lastCullBounds = bounds;
      this.hasCulled = true;
    }

    // Statics resort against the live mobiles every frame, so the cached on-screen
    // set is re-pushed into this frame's sortables even when the cull was skipped.
    // A surface static (roof cap, carpet) on a tile a mobile is stepping across, at
    // or below the mobile's foot (`anchor.z >= s.z`), is re-anchored to the mobile's
    // own fractional x/y (keeping its own z) so the x+y primary tie plus the layer
    // tie-break draws it *behind* the mobile for the whole step, matching the resting
    // frame (ADR-0168). A taller straddled static (z above
    // the foot - e.g. an overhanging fixture) fails the `>= s.z` guard and keeps its
    // own key, so legitimate occlusion is untouched. A mobile *under* a roof (foot z
    // 0, cap z 20) also fails the guard, so roof-hiding and roof occlusion are intact.
    for (const s of this.cachedStaticSortables) {
      const anchor = surfaceAnchorByTile.get(`${s.x},${s.y}`);
      if (anchor && anchor.z >= s.z) {
        sortables.push({ x: anchor.x, y: anchor.y, z: s.z, layer: s.layer, roof: s.roof, apply: s.apply });
      } else {
        sortables.push(s);
      }
    }

    // Draw the stateful doors from their entities, after mobiles so they join the
    // same depth sort (a door is a Layer.Static billboard at its swung cell).
    this.syncDoors(sortables);

    // Draw the dice-and-cup statics the same way (dice-and-cup, ADR-0083): fixed
    // billboards at their tile, joined to the same depth sort. `frameDtMs` ages
    // their overhead roll emote on the render clock.
    this.syncDice(sortables, frameDtMs);

    // Draw the moongates (PRD #362 Slice A): animated billboards at their cell,
    // joined to the same depth sort; `frameDtMs` advances the gate loop / summon
    // flash on the render clock.
    this.syncMoongates(sortables, frameDtMs);

    // Draw the treasure chests (PRD #362 Slice B): fixed billboards exactly like
    // the dice cup, in their own pool routed to OpenChest.
    this.syncChests(sortables, frameDtMs);
    this.syncDungeonLoot(sortables, frameDtMs);
    this.syncDungeonGlobes(sortables, frameDtMs);

    // Draw the flying arena projectiles (PRD #399 Slice A): centered billboards
    // pinned to their replicated position with no gait smoothing, joined to the
    // same depth sort; `frameDtMs` loops each bolt's strip on the render clock.
    this.syncProjectiles(sortables, frameDtMs);

    // Draw the Vacuum pickups (PRD #431) BEFORE the gems, so a vacuum claimed
    // this frame flags the whole-field inrush the gem removal sweep reads: every
    // gem removed in the same sweep then flies regardless of distance.
    const vacuumGrab = this.syncPickups(sortables, frameDtMs);

    // Draw Arena XP gems through the stable ground particle batch. A gem
    // removed near the runner transfers into the absorb batch stepped below.
    this.syncXpGems(frameDtMs, vacuumGrab);
    this.stepAbsorbingOrbs(frameDtMs);

    // The caller owns `sortables` (a throwaway per-frame buffer), so sort it in
    // place - identical order to sortByDepth, minus a full-scene array copy.
    if (this.performanceMonitor) {
      this.performanceMonitor.record('animation', performance.now() - animationStartedAt);
      this.performanceMonitor.measure('depth-sorting', () => {
        sortByDepthInPlace(sortables).forEach((item, i) => item.apply(i));
      });
    } else {
      sortByDepthInPlace(sortables).forEach((item, i) => item.apply(i));
    }

    // The connected roof structures double as the inside/outside *regions* both
    // per-viewer effects key off (ADR-0003 roof-hiding, ADR-0017 line-of-sight):
    // a tile inside a structure's footprint is inside that building, a tile in
    // none is the open plaza. The statics never move, so this flood-fill is the
    // same every frame: compute it once (lazily) and reuse the cache (see
    // {@link structures}). Region membership uses ALL statics, not just the visible
    // window, so the viewer's inside/outside region stays correct even if a wall
    // of their building is currently culled.
    const structures = (this.structures ??= roofStructures(this.statics));
    // A viewer standing on a roof plate (elevation above the ground under their
    // tile) is *on* the building, not inside it: their region is the open plaza,
    // so names/speech resolve against the outside and the roof-hiding gate below
    // leaves every roof up. "Ground" is height-aware (PRD #536 Slice B): a
    // viewer on a hill carries the mirrored terrain z, which equals landZ and so
    // still counts as ground - on a flat map this is exactly `elevation === 0`.
    const landZ = (x: number, y: number): number => world.map.landZ(x, y);
    const viewerRegion =
      cameraTile && cameraElevation <= landZ(cameraTile.x, cameraTile.y)
        ? regionIndex(structures, cameraTile)
        : OUTSIDE;

    // Per-viewer roof-hiding: cull the roof of the structure the LOCAL player is
    // under, identified purely from its tile. A render-time decision per client -
    // the shared world is untouched. This is the *followed player's* effect only:
    // the spectator backdrop (the login screen) has a fixed camera tile but no
    // target entity, and that tile (the player spawn) lands inside the bank, so
    // without this gate the backdrop would render the bank roofless. With no entity to follow
    // (spectator, or the pre-Welcome state), every roof shows.
    const hiddenStructure =
      this.cameraTarget === undefined || viewerRegion === OUTSIDE
        ? null
        : structures[viewerRegion]!;
    const hiddenRoof = hiddenStructure ? new Set(hiddenStructure.members) : null;
    const activeRoofStatics =
      this.staticScene?.activeBillboards ?? (this.statics as readonly StaticBillboard[]);
    for (const s of activeRoofStatics) {
      if (s.roof) {
        const placement = s.placement ?? s;
        s.sprite.visible = !hiddenRoof?.has(placement);
      }
    }

    // Per-viewer roof-*entity*-hiding (ADR-0155): an entity standing ON the hidden
    // structure's roof would otherwise float over the open interior the viewer can
    // now see down into, so it is culled with the roof tiles - driven by the SAME
    // `hiddenStructure` so the tiles and the roof-top occupants can never disagree
    // (never hide the cap but show the person, or vice-versa). A ground-level
    // occupant under the open roof (elevation 0) stays visible - seeing into the
    // building is the whole point; only roof/cap-level entities on the hidden
    // structure hide. The local player is skipped explicitly so no pose ever culls
    // the viewer's own sprite: while under a roof they stand at ground level so they
    // already fail the roof-level test, and a viewer ON a roof leaves `hiddenStructure`
    // null (their region is OUTSIDE), but the guard makes the invariant unmissable.
    for (const o of overhead) {
      const culled = o.id !== this.cameraTarget && isEntityOnHiddenRoof(hiddenStructure, o, landZ);
      o.sprite.visible = !culled;
    }
    // A moongate standing ON the hidden roof culls with it (PRD #362), driven by
    // the same `hiddenStructure` decision so gates and roof tiles never disagree.
    for (const m of this.moongateOverhead) {
      m.sprite.visible = !isEntityOnHiddenRoof(hiddenStructure, m, landZ);
    }

    // "Show all names" hotkey held (issue #184): keep every character's name lit by
    // topping its reveal timer back up each frame, so the 4s fade never runs out
    // while held. The line-of-sight pass below still hides names across the bank
    // wall; releasing the hotkey stops the re-reveal and the names fade on their own.
    if (this.forceNamesVisible) this.revealAllNames();

    // Per-viewer overhead line-of-sight: render an entity's name + speech only when
    // it shares the viewer's region (both inside the same building, or both in the
    // plaza), so chat and names don't bleed across the bank wall (ADR-0017). A pure
    // render toggle. With no camera target the viewer's region is unknown, so leave
    // every overhead showing rather than guessing.
    if (cameraTile) {
      for (const o of overhead) {
        // A rooftop character (elevation > 0) is outside its building's region,
        // matching how the viewer's own region resolves above.
        const region = o.elevation === 0 ? regionIndex(structures, o.tile) : OUTSIDE;
        o.sprite.setOverheadVisible(region === viewerRegion);
        // Reveal the name on first appearance in the viewport (issue #85): a
        // character scrolling into the camera window gets the same brief reveal as
        // one that spawns in sight. Tracked per entity so it fires once on the
        // entering frame, not every frame it stays on screen; leaving the window
        // clears the flag so a later re-entry reveals it again.
        const inView = !bounds || inBounds(bounds, o.tile.x, o.tile.y);
        if (inView) {
          if (!this.inViewport.has(o.id)) {
            this.inViewport.add(o.id);
            o.sprite.revealName();
          }
        } else {
          this.inViewport.delete(o.id);
        }
      }
    }

    // Camera: keep the target entity centred on screen by offsetting the world.
    // The world is scaled by `zoom` about its origin, so the target's local screen
    // point lands at `zoom * cameraScreen` before translation; subtract that
    // (not the unscaled point) so the player stays dead-centre at any zoom. This is
    // what keeps `playerCenter()` a constant screen centre, so the right-click
    // movement angle math is unaffected by zoom (issue #67).
    // Screen shake (Slice F, ADR-0178): a capped, decaying kick added to the same
    // camera translation. Because both Pixi's event-boundary hit test and
    // screenToGroundTile read `world.position` live, the visuals and every pick
    // stay in lockstep while shaken - a click always hits exactly what is drawn
    // under the cursor - and sim/tile coordinates are untouched.
    this.shake.update(frameDtMs);
    if (cameraScreen) {
      const kick = this.shake.offset();
      this.world.position.set(
        this.app.screen.width / 2 - this.zoom * cameraScreen.x + kick.x,
        this.app.screen.height / 2 - this.zoom * cameraScreen.y + kick.y,
      );
    }

    // Age the tap-to-walk feedback on the render clock: expand + fade the pings,
    // breathe the persistent marker, cull spent transients (issue #163).
    this.autowalkOverlay.update(frameDtMs);
    // Age the storm spawn-telegraph sparkles the same way: contract the warning ring,
    // twinkle the star, cull spent cues (ADR-0152).
    this.telegraphOverlay.update(frameDtMs);
    // Age the announced dungeon areas: brighten toward the impact tick, pulse,
    // and cull each one exactly as its damage lands (PRD #793).
    this.dungeonTelegraphOverlay.update(frameDtMs);
    // Age the blood decals: pop in, hold, fade out, cull spent splats (PRD #372).
      this.bloodLayer.update(frameDtMs);
      this.dungeonAfterimages = this.dungeonAfterimages.filter((afterimage) => {
        afterimage.ageMs += frameDtMs;
        const progress = afterimage.ageMs / DUNGEON_CHARGE_AFTERIMAGE_TTL_MS;
        if (progress < 1) {
          afterimage.sprite.alpha = 0.34 * (1 - progress);
          return true;
        }
        this.world.removeChild(afterimage.sprite);
        afterimage.sprite.destroy({ children: true });
        return false;
      });
      // Age the Arena juice FX (Slice F, ADR-0178): fly the burst flecks, glide the
    // tracers, expand the aura pulses, return the spent to their pools.
    this.arenaFx.update(frameDtMs);

    // Dungeon lighting is screen-space but follows the camera target's exact
    // transformed position, including zoom and shake. Its filter is appended to
    // the world only, so the sibling overhead RenderLayer and DOM chrome stay lit.
    if (this.dungeonLighting) {
      const playerScreen = cameraScreen
        ? {
            x: this.world.position.x + this.zoom * cameraScreen.x,
            y: this.world.position.y + this.zoom * cameraScreen.y,
          }
        : { x: this.app.screen.width / 2, y: this.app.screen.height / 2 };
      try {
        const lightPlacements = this.staticScene
          ? this.staticScene.lightsIn(
              bounds,
              LIGHT_CULL_MARGIN,
              hiddenRoof as ReadonlySet<StaticPlacement> | null,
            )
          : this.statics;
        let lightCount = 0;
        for (const source of lightPlacements) {
          if ('sprite' in source && !source.sprite.visible) continue;
          const placement = 'placement' in source && source.placement
            ? source.placement
            : source;
          if (!('light' in placement) || !placement.light) continue;
          let screen = this.staticLightPool[lightCount];
          if (!screen) {
            screen = { x: 0, y: 0, profile: 0, color: 0xffffff };
            this.staticLightPool.push(screen);
          }
          this.staticLightScreen[lightCount] = screen;
          positionStaticLight(screen, placement.x, placement.y, placement.z);
          screen.x = this.world.position.x + this.zoom * screen.x;
          screen.y = this.world.position.y + this.zoom * screen.y;
          screen.profile = placement.light.profile;
          screen.color = placement.light.color;
          screen.shader = placement.light.shader;
          lightCount += 1;
        }
        this.staticLightScreen.length = lightCount;
        this.dungeonLighting.update(
          this.dungeonLightingEnabled,
          frameDtMs,
          playerScreen,
          this.zoom,
          this.dungeonLightingEnabled && this.dungeonSightRunKey && cameraSightTile
            ? {
                runKey: this.dungeonSightRunKey,
                mapWidth: world.map.width,
                mapHeight: world.map.height,
                playerTile: cameraSightTile,
              }
            : undefined,
          this.staticLightScreen,
        );
      } catch {
        const dungeonLighting = this.dungeonLighting;
        this.dungeonLighting = undefined;
        this.dungeonLightingUnavailable = true;
        try {
          dungeonLighting.destroy();
        } catch {
          // Rendering failure degrades to the ordinary unlit world.
        }
      }
    }
    this.presentationIndexDirty = false;
  }

  /**
   * Stamp a resolved depth rank onto one display object, writing only when the
   * rank actually moved (PRD #826).
   *
   * The depth pass resolves a rank for every sortable in the scene every frame,
   * so most stamps re-assert a rank the object already has. A `zIndex` write is
   * not free: it marks the parent render group `structureDidChange` (a full
   * instruction rebuild and quad re-pack) and flags `world.children` for a
   * re-sort. Reading first keeps a settled scene write-free, and keeps a scene
   * where two characters cross to the two ranks that genuinely swapped.
   */
  private stampDepth(target: { zIndex: number }, z: number): void {
    if (target.zIndex === z) return;
    target.zIndex = z;
    this.depthStampWrites += 1;
  }

  /**
   * Reflect the world's door entities into the scene and queue them for the depth
   * sort. A door's art id and draw cell both derive from its live
   * {@link DoorComponent} via the pure door model (`base + 2*facing + open`, plus
   * the per-facing open-offset swing), so a state toggle re-textures and re-places
   * the sprite with no other wiring - this is the door analogue of the mobile
   * path. The sprite pool is keyed by entity id and reconciled here: a new door
   * gets a sprite, a vanished one is destroyed. Door sprites are clickable so the
   * local player can open/close them (the click sends a `UseDoor`; the renderer
   * never mutates the door). Pushed as {@link Layer.Static}, non-roof, at the
   * swung cell so they depth-sort against walls/roof/mobiles correctly.
   */
  private syncDoors(sortables: (Renderable & { apply: (z: number) => void })[]): void {
    const live = this.doorEntityIds;
    for (const entity of this.doorEntities) {
      const door = entity.door;
      const pos = entity.position;
      if (!door || !pos) continue;

      const name = staticTileName(doorArtId(door.base, door.facing, door.open));
      const texture = this.staticTextures?.get(name);
      if (!texture) {
        if (!this.missingDoorArt.has(name)) {
          console.warn(`no baked static texture for door "${name}" - skipped`);
          this.missingDoorArt.add(name);
        }
        continue;
      }
      live.add(entity.id);

      const pooled = this.ensureDoorSprite(entity.id, texture, name);

      // The swung draw cell: anchor when closed, anchor + open-offset when open.
      const cell = doorDrawCell(pos.x, pos.y, 0, door.facing, door.open);
      const screen = tileToScreen(cell.x, cell.y, cell.z);
      // A billboard hangs bottom-centre, dropped to the tile's near corner - the
      // same anchoring `createStatics` uses, so a door sits exactly where the
      // cosmetic leaf it replaced did.
      pooled.sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);

      sortables.push({
        x: cell.x,
        y: cell.y,
        z: cell.z,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(pooled.sprite, z);
        },
      });
    }

    // Drop sprites for doors that have left the world.
    if (this.presentationIndexDirty) for (const [id, pooled] of this.doorSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(pooled.sprite);
      pooled.sprite.destroy();
      this.doorSprites.delete(id);
    }
  }

  /**
   * Get (or create) the pooled sprite for a door, re-texturing it when its art
   * name changed (an open/close toggle swaps the closed leaf for the open one).
   * A freshly created sprite is bottom-centre anchored like every static and made
   * clickable so a tap routes a {@link onDoorUse} for this entity.
   */
  private ensureDoorSprite(
    id: EntityId,
    texture: Texture,
    name: string,
  ): { sprite: Sprite; name: string } {
    let pooled = this.doorSprites.get(id);
    if (!pooled) {
      const sprite = new UoHueSprite();
      sprite.texture = texture;
      sprite.anchor.set(0.5, 1);
      sprite.eventMode = 'static';
      // Keep the UO gauntlet over doors rather than swapping to the system hand:
      // `inherit` falls through to the `#app` host's gauntlet cursor (see
      // `applyGameCursor`), so a door is still clickable but never breaks the
      // viewport's gauntlet affordance.
      sprite.cursor = 'inherit';
      sprite.on('pointertap', () => this.onDoorUse?.(id));
      this.world.addChild(sprite);
      pooled = { sprite, name };
      this.doorSprites.set(id, pooled);
    } else if (pooled.name !== name) {
      pooled.sprite.texture = texture;
      pooled.name = name;
    }
    return pooled;
  }

  /**
   * Reflect the world's dice-cup entities into the scene and queue them for the
   * depth sort (dice-and-cup, ADR-0083). A dice prop is a fixed billboard at its
   * tile - it never moves and never re-textures - so this is the door path stripped
   * to its essentials: ensure a pooled {@link DiceSprite}, place it at the entity's
   * cell (bottom-centre on the near corner, exactly as a static/door), age its
   * overhead roll emote, and push it as a non-roof {@link Layer.Static}. The sprite
   * is double-clickable; the double-click routes a {@link onDiceUse} for this
   * entity (the renderer never rolls locally - the server is authoritative).
   */
  private syncDice(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    const live = this.diceEntityIds;
    for (const entity of this.diceEntities) {
      const dice = entity.dice;
      const pos = entity.position;
      if (!dice || !pos) continue;

      const name = staticTileName(dice.artId);
      const texture = this.staticTextures?.get(name);
      if (!texture) {
        if (!this.missingDiceArt.has(name)) {
          console.warn(`no baked static texture for dice "${name}" - skipped`);
          this.missingDiceArt.add(name);
        }
        continue;
      }
      live.add(entity.id);

      const sprite = this.ensureDiceSprite(entity.id, texture);
      sprite.update(frameDtMs);

      // `pos.z` grounds the cup on raised terrain (terraced land, PRD #536
      // Slice C); absent/0 on the flat bank, exactly the old placement.
      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      // Bottom-centre on the tile's near corner - the same anchoring `createStatics`
      // and the door path use, so the cup sits exactly on its tile.
      sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);

      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }

    // Drop sprites for dice that have left the world (none today, but symmetric
    // with the door pool and robust if the roster ever changes at runtime).
    if (this.presentationIndexDirty) for (const [id, sprite] of this.diceSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(sprite);
      sprite.dispose();
      this.diceSprites.delete(id);
    }
  }

  /**
   * Drive one character's overhead banishment mm:ss (PRD #362 Slice D). The
   * replicated `remainingTicks` arrives in coarse ~1/s pushes; each *change*
   * re-anchors a wall-clock deadline (`remainingTicks x 50ms` from now) and every
   * frame renders the extrapolated remainder - so the label ticks smoothly
   * between pushes and snaps to the authority on each one. `undefined` clears
   * both the label and the anchor.
   */
  private syncBanishCountdown(
    id: EntityId,
    sprite: PlayerSprite,
    remainingTicks: number | undefined,
  ): void {
    if (remainingTicks === undefined) {
      this.banishAnchors.delete(id);
      sprite.setBanishCountdown(null); // no-op when no label is up
      return;
    }
    const now = performance.now();
    let anchor = this.banishAnchors.get(id);
    if (!anchor || anchor.ticks !== remainingTicks) {
      anchor = { ticks: remainingTicks, atMs: now };
      this.banishAnchors.set(id, anchor);
    }
    const remainingMs = Math.max(0, anchor.ticks * 50 - (now - anchor.atMs));
    const totalSeconds = Math.ceil(remainingMs / 1000);
    const mm = Math.floor(totalSeconds / 60);
    const ss = totalSeconds % 60;
    sprite.setBanishCountdown(`${mm}:${String(ss).padStart(2, '0')}`);
  }

  /**
   * Reflect the world's moongate entities into the scene and queue them for the
   * depth sort (PRD #362 Slice A). The dice path with two twists: the billboard is
   * *animated* (the baked `moongate` effect strip loops; a newly-spawned gate
   * first plays the `moongate-flash` summon one-shot), and a gate may stand on
   * the roof plate, so its screen position and depth key use its `z`. The removal
   * sweep is live - the event consumes gates at runtime.
   */
  private syncMoongates(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    this.moongateOverhead.length = 0;
    const live = this.moongateEntityIds;
    for (const entity of this.moongateEntities) {
      const moongate = entity.moongate;
      const pos = entity.position;
      if (!moongate || !pos) continue;
      live.add(entity.id);

      const sprite = this.ensureMoongateSprite(entity.id);
      sprite.update(frameDtMs);

      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      // Bottom-centre on the tile's near corner - the static/door/dice anchoring.
      sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);
      // Default visible; the per-viewer roof-hiding pass may cull it this frame.
      sprite.visible = true;
      this.moongateOverhead.push({
        sprite,
        tile: { x: pos.x, y: pos.y },
        elevation,
      });

      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }

    // Drop sprites for gates that left the world (entered / fizzled / despawned).
    if (this.presentationIndexDirty) for (const [id, sprite] of this.moongateSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(sprite);
      sprite.dispose();
      this.moongateSprites.delete(id);
    }
  }

  /**
   * Reflect the world's treasure-chest entities into the scene and queue them
   * for the depth sort (PRD #362 Slice B) - the {@link syncDice} path over the
   * chest pool: ensure a pooled {@link DiceSprite}, place it bottom-centre at the
   * entity's cell, and push it as a non-roof static. Chests are event-spawned
   * and consumed at runtime, so the removal sweep is live.
   */
  private syncChests(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    const live = this.chestEntityIds;
    for (const entity of this.chestEntities) {
      const chest = entity.chest;
      const pos = entity.position;
      if (!chest || !pos) continue;

      const name = staticTileName(chest.artId);
      const texture = this.staticTextures?.get(name);
      if (!texture && !this.missingChestArt.has(name)) {
        // Unlike the dice cup (whose art is in the committed atlas) a chest can
        // outrun the last statics bake; warn once and degrade to the sprite's
        // flat-box fallback rather than an invisible, unclickable chest.
        console.warn(`no baked static texture for chest "${name}" - flat-box fallback`);
        this.missingChestArt.add(name);
      }
      live.add(entity.id);

      const sprite = this.ensureChestSprite(entity.id, texture);
      sprite.update(frameDtMs);

      // `pos.z` grounds a chest on raised terrain (terraced land, PRD #536
      // Slice C); absent/0 on flat ground, exactly the old placement.
      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);

      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }

    if (this.presentationIndexDirty) for (const [id, sprite] of this.chestSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(sprite);
      sprite.dispose();
      this.chestSprites.delete(id);
    }
  }

  /** Reflect private dungeon loot into a readable, double-clickable static pool. */
  private syncDungeonLoot(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    const live = this.dungeonLootEntityIds;
    for (const entity of this.dungeonLootEntities) {
      const loot = entity.dungeonLoot;
      const pos = entity.position;
      if (!loot || !pos) continue;
      const textureName = staticTileName(loot.artId);
      const texture = this.staticTextures?.get(textureName);
      if (!texture && !this.missingDungeonLootArt.has(textureName)) {
        console.warn(
          `no baked static texture for dungeon loot "${textureName}" - flat-box fallback`,
        );
        this.missingDungeonLootArt.add(textureName);
      }
      live.add(entity.id);
      const sprite = this.ensureDungeonLootSprite(entity.id, texture);
      // Rarity is readable from across the room (PRD #795, user story two): the
      // label the drop already carried is tinted by what it rolled, so deciding
      // whether to walk over costs no click and no tooltip.
      sprite.setName(loot.name, DUNGEON_LOOT_RARITY_COLORS[loot.rarity ?? 'common']);
      sprite.update(frameDtMs);
      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      sprite.position.set(screen.x, screen.y + 2 * TILE_HALF);
      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }
    if (this.presentationIndexDirty) for (const [id, sprite] of this.dungeonLootSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(sprite);
      sprite.dispose();
      this.dungeonLootSprites.delete(id);
    }
  }

  /**
   * Reflect dungeon health globes into the scene (PRD #794).
   *
   * A globe is a ground orb with no click surface and no label: the player
   * collects it by walking over it, and the server is what decides that. The
   * renderer's whole job is to make it visible from across a room.
   */
  private syncDungeonGlobes(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    const live = this.dungeonGlobeEntityIds;
    for (const entity of this.dungeonGlobeEntities) {
      const pos = entity.position;
      if (!entity.dungeonGlobe || !pos) continue;
      live.add(entity.id);
      let sprite = this.dungeonGlobeSprites.get(entity.id);
      if (!sprite) {
        sprite = new HealthGlobeSprite(entity.id);
        this.world.addChild(sprite);
        this.dungeonGlobeSprites.set(entity.id, sprite);
      }
      sprite.update(frameDtMs);
      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      sprite.position.set(screen.x, screen.y + TILE_HALF);
      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Static,
        roof: false,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }
    if (this.presentationIndexDirty) for (const [id, sprite] of this.dungeonGlobeSprites) {
      if (live.has(id)) continue;
      this.world.removeChild(sprite);
      sprite.dispose();
      this.dungeonGlobeSprites.delete(id);
    }
  }

  /**
   * Reflect the world's projectile entities into the scene and queue them for the
   * depth sort (bullet-hell rework, PRD #399 Slice A). Unlike the mobile loop
   * this uses **no {@link StepAnimator}**: a bolt is drawn straight at its
   * replicated `position` each frame (it lerps by its own velocity, not a walk
   * gait), lifted a little off the ground so it reads as flying. Projectiles are
   * spawned and reaped by the run, so the removal sweep is live. `frameDtMs` loops
   * each bolt's look strip on the render clock.
   */
  private syncProjectiles(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): void {
    const live = this.projectileEntityIds;
    for (const entity of this.projectileEntities) {
      const projectile = entity.projectile;
      const pos = entity.position;
      if (!projectile || !pos) continue;
      live.add(entity.id);

      const sprite = this.ensureProjectileSprite(entity.id, projectile.look);
      sprite.update(frameDtMs);

      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      // Lift the centered bolt off the ground corner so it flies at ~torso height.
      const impactPoint = { x: screen.x, y: screen.y - TILE_HALF };
      sprite.position.set(impactPoint.x, impactPoint.y);
      // Rotate the art along its flight (the resolved tracer already does this):
      // the traveling bolt would otherwise draw in its baked orientation regardless
      // of direction ("the arrow going the wrong way"). See projectileArtRotation.
      sprite.setFacing(projectileArtRotation(projectile.vx, projectile.vy, projectile.look));
      sprite.visible = true;
      // Remember where the bolt is and how much life it had left, so its impact
      // flash lands at its last-drawn point and only fires on a contact removal
      // (PRD #399 Slice D). Overwrites each frame, so the sweep below reads the
      // most recent snapshot state.
      this.projectileImpactState.set(entity.id, {
        ttlTicks: projectile.ttlTicks,
        screen: impactPoint,
      });

      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Mobile,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }

    if (this.presentationIndexDirty) for (const [id, sprite] of this.projectileSprites) {
      if (live.has(id)) continue;
      // The bolt left the world (stepProjectiles removed it): flash its impact
      // strip at its last-drawn point IFF it was cut short by a contact - a
      // max-range expiry dissipates with no flash. The pooled impact machinery is
      // reused verbatim (the resolved-cascade path's `impact`); the sound half of
      // this cue is derived independently from the same removal by the SoundObserver.
      const state = this.projectileImpactState.get(id);
      if (state && projectileRemovalWasContact(state.ttlTicks)) {
        this.arenaFx.impact(
          state.screen,
          PROJECTILE_IMPACT_EFFECT,
          PROJECTILE_IMPACT_RADIUS_TILES,
          PROJECTILE_IMPACT_COLOR,
        );
      }
      this.projectileImpactState.delete(id);
      this.world.removeChild(sprite);
      sprite.dispose();
      this.projectileSprites.delete(id);
    }
  }

  /**
   * Sync the XP orb clusters to the replicated `xpGem` entities (arena VS-style
   * orbs): one lightweight {@link XpOrbSprite} handle per gem writes into the
   * stable ground particle batch.
   * The juice lives in the removal sweep: a gem the sim collected (it last drew
   * within the magnet's screen reach of the runner) transfers into the absorb
   * batch and flies into the player; a far removal was a cap-merge and quietly
   * fades where it stood.
   */
  private syncXpGems(
    frameDtMs: number,
    vacuumGrab: boolean,
  ): void {
    const live = this.xpGemEntityIds;
    for (const entity of this.xpGemEntities) {
      const gem = entity.xpGem;
      const pos = entity.position;
      if (!gem || !pos) continue;
      live.add(entity.id);

      let sprite = this.xpGemSprites.get(entity.id);
      if (!sprite) {
        sprite = this.xpGemLayer.create(gem.value, entity.id);
        sprite.setCadence(xpOrbCadenceFor(this.presentationQuality));
        this.xpGemSprites.set(entity.id, sprite);
      }
      sprite.update(frameDtMs);

      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      sprite.setPosition(screen.x, screen.y);
      this.xpGemLastScreen.set(entity.id, { x: screen.x, y: screen.y });
    }

    const target = this.cameraTarget !== undefined ? this.fxPoint(this.cameraTarget) : undefined;
    const maxPx = this.xpAbsorbMaxPx();
    let staggerIndex = 0;
    if (this.presentationIndexDirty) for (const [id, sprite] of this.xpGemSprites) {
      if (live.has(id)) continue;
      // The gem left the world. Collected near the runner -> suck-in flight;
      // anywhere else (a cap-merge) -> fade in place. A **Vacuum grab** this
      // frame is the one sanctioned exception (PRD #431 Slice C): every gem
      // removed in the same sweep flies regardless of distance. Either way the
      // sprite leaves the pool now and the transient list owns its teardown.
      this.xpGemSprites.delete(id);
      const from = this.xpGemLastScreen.get(id) ?? { x: sprite.position.x, y: sprite.position.y };
      this.xpGemLastScreen.delete(id);
      const dist = target !== undefined ? Math.hypot(from.x - target.x, from.y - target.y) : Infinity;
      const near = target !== undefined && dist <= maxPx;
      if (vacuumGrab && target !== undefined) {
        // Whole-field inrush: launch staggered by distance (nearest first) and
        // lengthen the flight with distance so the far edge pours in as a
        // sustained spiral rather than one frame of chaos.
        const t = Math.min(1, dist / XP_VACUUM_DISTANCE_REF_PX);
        const flightSprite = sprite.transferTo(this.xpAbsorbLayer);
        this.absorbingOrbs.push({
          sprite: flightSprite,
          from,
          ageMs: 0,
          delayMs: t * XP_VACUUM_STAGGER_MAX_MS,
          durationMs: XP_ABSORB_MS + t * (XP_VACUUM_ABSORB_MAX_MS - XP_ABSORB_MS),
          mode: 'absorb',
        });
        continue;
      }
      const flightSprite = near ? sprite.transferTo(this.xpAbsorbLayer) : sprite;
      this.absorbingOrbs.push({
        sprite: flightSprite,
        from,
        ageMs: 0,
        delayMs: near ? Math.min(XP_ABSORB_STAGGER_MAX_MS, staggerIndex++ * XP_ABSORB_STAGGER_MS) : 0,
        durationMs: XP_ABSORB_MS,
        mode: near ? 'absorb' : 'fade',
      });
    }
  }

  /**
   * The screen-px reach the removal sweep classifies a gem's suck-in against -
   * the client mirror of the sim magnet, derived from the runner's **effective**
   * radius ({@link ../sim/xpGem.arenaMagnetRadius} for the observed
   * {@link magnetLevel}) plus {@link XP_ABSORB_SLACK_TILES} of slack, in
   * isometric px. Presentation only (ADR-0215): a wrong guess mislabels a fade
   * as a suck-in, never the XP.
   */
  private xpAbsorbMaxPx(): number {
    return (arenaMagnetRadius(this.magnetLevel) + XP_ABSORB_SLACK_TILES) * 2 * TILE_HALF;
  }

  /**
   * Adopt the client's mirror of the runner's `magnet` stat level (PRD #431
   * Slice A), counted from its own accepted draft picks. Grows the suck-in
   * classification reach; reset to 0 on a fresh run. Clamped defensively.
   */
  setMagnetLevel(level: number): void {
    this.magnetLevel = Math.max(0, level);
  }

  /**
   * Sync the Vacuum pickup sprites to the replicated `pickup` entities (PRD
   * #431): one pooled {@link VacuumSprite} per pickup, pinned to its tile and
   * joined to the depth sort with a slow distinct pulse. Returns whether a
   * vacuum was **claimed** this frame (its entity left the world within grab
   * reach of the runner) - the caller flags the gem sweep so the whole field
   * flies. A claim also fires {@link onVacuumGrab} (the whoosh) and bursts at
   * the runner.
   */
  private syncPickups(
    sortables: (Renderable & { apply: (z: number) => void })[],
    frameDtMs: number,
  ): boolean {
    const live = this.pickupEntityIds;
    for (const entity of this.pickupEntities) {
      const pickup = entity.pickup;
      const pos = entity.position;
      if (!pickup || !pos) continue;
      live.add(entity.id);

      let sprite = this.pickupSprites.get(entity.id);
      if (!sprite) {
        sprite = new VacuumSprite();
        this.world.addChild(sprite);
        this.pickupSprites.set(entity.id, sprite);
      }
      sprite.update(frameDtMs);

      const elevation = pos.z ?? 0;
      const screen = tileToScreen(pos.x, pos.y, elevation);
      sprite.position.set(screen.x, screen.y);
      sprite.visible = true;
      this.pickupLastScreen.set(entity.id, { x: screen.x, y: screen.y });

      sortables.push({
        x: pos.x,
        y: pos.y,
        z: elevation,
        layer: Layer.Mobile,
        apply: (z: number) => {
          this.stampDepth(sprite, z);
        },
      });
    }

    let grabbed = false;
    const target = this.cameraTarget !== undefined ? this.fxPoint(this.cameraTarget) : undefined;
    const maxPx = this.xpAbsorbMaxPx();
    if (this.presentationIndexDirty) for (const [id, sprite] of this.pickupSprites) {
      if (live.has(id)) continue;
      // The pickup left the world - a claim (the sim only removes a vacuum when
      // the runner walks onto it). If it went near the runner, it's the grab:
      // burst, whoosh, and flag the whole-field inrush.
      this.pickupSprites.delete(id);
      const from = this.pickupLastScreen.get(id) ?? { x: sprite.position.x, y: sprite.position.y };
      this.pickupLastScreen.delete(id);
      const near =
        target !== undefined && Math.hypot(from.x - target.x, from.y - target.y) <= maxPx;
      if (near) {
        grabbed = true;
        this.arenaFx.burst(target!.x, target!.y, sprite.color, XP_ABSORB_BURST_PARTICLES * 3);
      }
      this.world.removeChild(sprite);
      sprite.dispose();
    }
    if (grabbed) this.onVacuumGrab?.();
    return grabbed;
  }

  /**
   * Step the orb clusters in flight to the runner (and the fading cap-merges).
   * The flight chases the player sprite's **live** point each frame - the
   * runner keeps walking, and the orbs curve after them - on an ease-in cubic:
   * a slow attracted drift that accelerates into the suck. Arrivals sparkle,
   * and {@link onXpAbsorb} fires once per frame with the arrival count.
   */
  private stepAbsorbingOrbs(frameDtMs: number): void {
    if (this.absorbingOrbs.length === 0) return;
    const target = this.cameraTarget !== undefined ? this.fxPoint(this.cameraTarget) : undefined;
    let arrivals = 0;
    this.absorbingOrbs = this.absorbingOrbs.filter((orb) => {
      orb.ageMs += frameDtMs;
      const age = orb.ageMs - orb.delayMs;
      if (age <= 0) return true; // still holding its pour slot
      orb.sprite.update(frameDtMs);

      if (orb.mode === 'fade') {
        const p = Math.min(1, age / XP_FADE_MS);
        orb.sprite.setAlpha(1 - p);
        if (p < 1) return true;
      } else {
        const p = Math.min(1, age / orb.durationMs);
        const eased = p * p * p;
        const to = target ?? orb.from;
        orb.sprite.setPosition(
          orb.from.x + (to.x - orb.from.x) * eased,
          orb.from.y + (to.y - orb.from.y) * eased,
        );
        orb.sprite.setAbsorb(p);
        if (p < 1) return true;
        this.arenaFx.burst(to.x, to.y, orb.sprite.color, XP_ABSORB_BURST_PARTICLES);
        arrivals += 1;
      }
      orb.sprite.dispose();
      return false;
    });
    if (arrivals > 0) this.onXpAbsorb?.(arrivals);
  }

  /** Get (or create) the pooled {@link ProjectileSprite} for a projectile entity. */
  private ensureProjectileSprite(id: EntityId, look: ProjectileEffectId): ProjectileSprite {
    let sprite = this.projectileSprites.get(id);
    if (!sprite) {
      this.onEffectArtNeeded?.();
      sprite = new ProjectileSprite(this.effectArt[look]);
      this.world.addChild(sprite);
      this.projectileSprites.set(id, sprite);
    }
    return sprite;
  }

  /**
   * Get (or create) the pooled chest sprite (a reused {@link DiceSprite}) for a
   * chest entity, routed to {@link onChestUse} on double-click.
   */
  private ensureChestSprite(id: EntityId, texture: Texture | undefined): DiceSprite {
    let sprite = this.chestSprites.get(id);
    if (!sprite) {
      sprite = new DiceSprite(texture, this.overheadLayer);
      sprite.onDoubleClick = () => this.onChestUse?.(id);
      sprite.suppressClick = () => this.suppressClick?.() ?? false;
      this.world.addChild(sprite);
      this.chestSprites.set(id, sprite);
    }
    return sprite;
  }

  private ensureDungeonLootSprite(
    id: EntityId,
    texture: Texture | undefined,
  ): DiceSprite {
    let sprite = this.dungeonLootSprites.get(id);
    if (!sprite) {
      sprite = new DiceSprite(texture, this.overheadLayer);
      sprite.onDoubleClick = () => this.onDungeonLootUse?.(id);
      sprite.suppressClick = () => this.suppressClick?.() ?? false;
      this.world.addChild(sprite);
      this.dungeonLootSprites.set(id, sprite);
    }
    return sprite;
  }

  /**
   * Get (or create) the pooled {@link MoongateSprite} for a moongate entity. A
   * freshly created sprite plays the summon flash first (when baked), then loops
   * the gate strip; its double-click routes {@link onMoongateUse} for this entity
   * and it shares the long-press suppression latch with the other clickables.
   */
  private ensureMoongateSprite(id: EntityId): MoongateSprite {
    let sprite = this.moongateSprites.get(id);
    if (!sprite) {
      this.onEffectArtNeeded?.();
      sprite = new MoongateSprite(this.effectArt['moongate'], this.effectArt['moongate-flash']);
      sprite.onDoubleClick = () => this.onMoongateUse?.(id);
      sprite.suppressClick = () => this.suppressClick?.() ?? false;
      this.world.addChild(sprite);
      this.moongateSprites.set(id, sprite);
    }
    return sprite;
  }

  /**
   * Get (or create) the pooled {@link DiceSprite} for a dice entity. A freshly
   * created sprite is wired so a double-click routes a {@link onDiceUse} for this
   * entity, and its overhead emote attaches to the always-on-top overhead layer.
   */
  private ensureDiceSprite(id: EntityId, texture: Texture): DiceSprite {
    let sprite = this.diceSprites.get(id);
    if (!sprite) {
      sprite = new DiceSprite(texture, this.overheadLayer);
      sprite.onDoubleClick = () => this.onDiceUse?.(id);
      sprite.suppressClick = () => this.suppressClick?.() ?? false;
      this.world.addChild(sprite);
      this.diceSprites.set(id, sprite);
    }
    return sprite;
  }

  /**
   * Bring the sprite pool into line with the world's renderable mobiles: create
   * a sprite for any newly appeared one and destroy (and drop the interpolation
   * state of) any that has left. Every entity carrying a {@link Entity.mobile}
   * gets an animated {@link PlayerSprite}, so a player joining or leaving is
   * reflected in the scene automatically.
   */
  private reconcile(): void {
    const liveMobiles = new Set<EntityId>();
    for (const entity of this.mobileEntities) {
      if (!entity.mobile) continue;
      liveMobiles.add(entity.id);
      const existing = this.sprites.get(entity.id);
      if (!existing) {
        const sprite = this.createSprite(entity);
        // A downed duelist's ghost body renders as the ClassicUO death shroud - solid,
        // grayscale, robed - built entirely by buildPlayerLayers off the ghost body id
        // (Duel deathfix, ADR-0142); there is no per-sprite translucency any more.
        // Double-clicking a player character opens the paperdoll (PRD #629): self
        // (the live cameraTarget - re-evaluated at click time, since sprites are
        // pooled before the camera target is set) or any entity classified as a
        // player by its replicated interaction role ('duel-target'/'dueling',
        // both of which join duelTargetIds below - stable across the mid-match
        // role flip). Rats, NPC scenery, and props never qualify, and the
        // marker-keyed vendor overrides further down replace this handler
        // entirely (their precedence is unchanged). The long-press latch
        // (ADR-0169) applies: a hold that opened the context menu must not also
        // read as a paperdoll double-click.
        if (entity.horde !== true) {
          const id = entity.id;
          sprite.onDoubleClick = () => {
          if (this.suppressClick?.()) return;
          if (id === this.cameraTarget || this.duelTargetIds.has(id)) {
            this.onCharacterDoubleClick?.(id);
          }
        };
        // Targeting classifies by the **replicated interaction role** (PRD #534
        // D5, ADR-0266) - the six-branch body-id chain is gone, and the default
        // is **fail-safe scenery**: an entity the server did not classify gets
        // NO hover/click targeting (it can never silently become a duel target).
        const role = entity.interaction?.role ?? 'scenery';
        if (role === 'war-target') {
          // The grey war-mode target (Rat Swarm Slice E, ADR-0120): hover
          // highlight + click-to-pursue. The PlayerSprite is already a hit-test
          // target (eventMode 'static', for the name-reveal tap), so this adds
          // handlers, not interactivity; every handler is gated on the live
          // war-mode flag and the corpse predicate at event time. `click` is the
          // left-button tap (Pixi v8), so right-click stays free for movement.
          this.ratIds.add(id);
          sprite.on('pointerover', () => {
            if (this.warMode && (this.ratTargetable?.(id) ?? true)) {
              sprite.setHighlight(this.highlightHue());
            }
          });
          sprite.on('pointerout', () => sprite.setHighlight(null));
          sprite.on('click', () => {
            // A long-press that opened the context menu (ADR-0169, replacing the
            // shift+left-click of ADR-0161) must never *also* start a rat pursuit; a plain
            // short left-click pursues exactly as before.
            if (this.suppressClick?.()) return;
            if (this.warMode && (this.ratTargetable?.(id) ?? true)) this.onRatClick?.(id);
          });
        } else if (role === 'duel-target' || role === 'dueling') {
          // The blue duel target (Player duels, PRD #252, ADR-0130): players
          // (including a downed player's ghost). A player toggles duel-target
          // <-> 'dueling' (the card-duel frozen marker, PRD #493 Slice 5,
          // ADR-0286) live during a match, but these handlers are wired once at
          // creation and `lookKey` excludes the role, so both roles are wired
          // IDENTICALLY here - stable across the flip. Wire the blue-hover
          // highlight and the click-to-select, mirroring the war-target wiring
          // but gated on the armed duel cursor (`duelMode`) and the duel-target
          // predicate. `duelTargetable` is busy-aware off the LIVE role, so a
          // player who is actively 'dueling' is suppressed at event time (no
          // highlight, no pick) and reverts cleanly when the role flips back.
          // The server still rules authoritatively (self / ghost / range /
          // busy); this is only the local hover feedback and pick, so a pick the
          // server refuses simply fizzles. The battle gump is the match indicator,
          // while the world sprite keeps its ordinary appearance.
          this.duelTargetIds.add(id);
          sprite.on('pointerover', () => {
            if (this.duelMode && (this.duelTargetable?.(id) ?? true)) {
              sprite.setHighlight(this.duelHighlight());
            }
          });
          sprite.on('pointerout', () => sprite.setHighlight(null));
          sprite.on('click', () => {
            // A long-press that opened the context menu (ADR-0169) must never *also* pick
            // this sprite as a duel target; a plain short left-click under the armed duel
            // cursor selects exactly as before.
            if (this.suppressClick?.()) return;
            if (this.duelMode && (this.duelTargetable?.(id) ?? true)) this.onDuelTargetClick?.(id);
          });
        } else {
          // 'scenery' (and 'usable', whose props have their own sprites) get no
          // war/duel targeting wiring: a war- or duel-cursor hover passes over a
          // storm body, an arena monster, a roster NPC, or an event NPC (the
          // ADR-0152 "hovering reads as scenery" acceptance). They still animate
          // and reveal names like any sprite - they just cannot be picked as a
          // war or duel target. The ONE cursor that may still pick a scenery
          // mobile is `/ignore` (ADR-0185): roster-NPC bark squelching is that
          // command's whole point, so these handlers exist but are gated at
          // event time on {@link ignoreTargetable}, which is FALSE unless
          // `main.ts` arms the ignore purpose - under a duel/dice cursor (or
          // unwired, e.g. headless tests) scenery stays completely inert.
          this.ignoreWiredIds.add(id);
          sprite.on('pointerover', () => {
            if (this.duelMode && (this.ignoreTargetable?.(id) ?? false)) {
              sprite.setHighlight(this.duelHighlight());
            }
          });
          sprite.on('pointerout', () => sprite.setHighlight(null));
          sprite.on('click', () => {
            // The long-press suppression latch applies here too (ADR-0169).
            if (this.suppressClick?.()) return;
            if (this.duelMode && (this.ignoreTargetable?.(id) ?? false)) this.onDuelTargetClick?.(id);
          });
        }

        // Marker-keyed affordances ride their own replicated components,
        // orthogonal to the interaction role:
          if (entity.justice === 'thief') {
          // The King's Justice thief (PRD #361, ADR-0194): once he falls he IS
          // the lootable corpse (a real hit-testable entity, not the inert
          // raiseCorpse visual), so wire the double-click loot; the server
          // validates phase/marker/range - a premature double-click fizzles.
          this.justiceThieves.add(id);
          sprite.onDoubleClick = () => this.onCorpseLoot?.(id);
          // A thief is a CRIMINAL: his overhead name renders the classic UO
          // notoriety grey, never the innocent blue - alive (his one instant)
          // and as the corpse alike (ADR-0194).
          sprite.setNameColor(NAME_COLOR_CRIMINAL);
          } else if (entity.escort !== undefined) {
          // The Bank Escort (PRD #371): double-click = "Ask destination" (the
          // long-press context menu in `main.ts` carries Accept/Abandon); the
          // server rules on range and phase, so a premature double-click fizzles.
          sprite.onDoubleClick = () => this.onEscortAsk?.(id);
          } else if (entity.arenaMaster === true) {
          // The Arena Master (PRD #334, ADR-0209/0245): double-click opens the
          // Arena Master gump (the long-press "Join the arena" menu lives in
          // `main.ts`), the Bank Escort precedent.
          sprite.onDoubleClick = () => this.onArenaMasterUse?.(id);
          } else if (entity.soulstoneKeeper === true) {
          // The Bone Knight Wall's Soulstone keeper (PRD #837 slice 3):
          // double-click opens the Soulstone dialog, the same marker-keyed
          // pattern as the escort and the Arena Master.
          sprite.onDoubleClick = () => this.onSoulstoneKeeperUse?.(id);
          }
        }
        this.sprites.set(entity.id, sprite);
        this.spriteLooks.set(entity.id, this.lookKey(entity));
        this.spriteFrameLooks.set(entity.id, this.cloneLook(entity.renderable));
        // Pin this visible look's atlases against byte-budget eviction (issue #625).
        this.pinEntityLook(entity.id);
        this.world.addChild(sprite);
        // A freshly spawned-in-sight character (and the local player on login)
        // reveals its name on appearance, then fades (issue #85). Scrolling an
        // existing character into the viewport is the other entry trigger, handled
        // in sync() against the camera window.
        sprite.revealName();
        // Stream this newcomer's body/equipment atlases if they aren't loaded yet
        // (ADR-0247): the sprite was just built from whatever is present - the grounded
        // default-body/undressed fallback for anything still loading - and swaps to the
        // real frames as each atlas arrives.
        this.streamFrames(entity.id, entity, entity.renderable ?? {}, true);
      } else {
        // A pooled sprite whose worn outfit (or body/hue) changed on the snapshot
        // stream: rebuild its layer stack in place so a dressing change shows live on
        // every client, no reconnect (PRD #101). The key compare keeps the 10Hz
        // snapshot stream (ADR-0163) from rebuilding an unchanged stack every frame.
        const key = this.lookKey(entity);
        const lookChanged = this.spriteLooks.get(entity.id) !== key;
        if (lookChanged) {
          this.spriteLooks.set(entity.id, key);
          this.spriteFrameLooks.set(entity.id, this.cloneLook(entity.renderable));
          // Re-pin the new look; the released outfit becomes evictable (issue #625).
          this.pinEntityLook(entity.id);
          // The death/resurrection body swap (ghost <-> living) rides this same
          // look-change path (the body id is part of the look key): setLayers rebuilds
          // the stack, which for a ghost body is the solid grayscale death shroud and
          // for a living body the normal hued stack (Duel deathfix, ADR-0142).
          existing.setLayers(this.layersFor(entity));
        }
        // Keep streaming: swap in a newly-arrived atlas for a still-loading look, and (on
        // a live outfit change to a not-yet-loaded item) begin tracking it (ADR-0247).
        this.streamFrames(entity.id, entity, entity.renderable ?? {}, lookChanged);
      }
      // Remember the living look so a death can raise its corpse from the body +
      // clothes it wore in life (corpse/ghost split, ADR-0140); once the body has
      // swapped to the ghost the living outfit is gone from the snapshot, so the last
      // living look cached here is what the corpse is built from. The capture is
      // admitted by the **replicated `raisesCorpse` flag** (PRD #534 D5, ADR-0266) -
      // replacing the hardcoded rat/storm/monster body-id exclusion list; creatures
      // die on the monster `die` clip, never die-forward/die-back, so they carry no
      // flag (and a packed 150-enemy arena field pays no per-enemy clone per frame).
      // A **ghost body** is still skipped dynamically: the flag is per-entity and a
      // downed player keeps it, but the shroud is not a living look to remember.
      const body = entity.renderable?.body;
      // The replicated body id, for the hit-stagger's boss check at the hit
      // instant (PRD #430 Slice A) - flashHit sees only the entity id.
      this.entityBodies.set(entity.id, body);
      // The replicated horde marker, for the despawn pass's corpse conversion
      // (ADR-0304) - by despawn time only the id is at hand, not the entity.
      if (entity.horde === true) this.hordeIds.add(entity.id);
      if (entity.interaction?.raisesCorpse === true && !isGhostBody(body)) {
        this.livingLooks.set(entity.id, this.cloneLook(entity.renderable));
      }
      // The King's Justice corpse pose is **snapshot-driven**, not broadcast-driven
      // (ADR-0194): the replicated `thiefCorpse` marker - not the ephemeral
      // `die-forward` ActionAnimation - is what drops the thief's own sprite into
      // its held prone frame. The one-shot broadcast can race the pair's very
      // first snapshot (a client cannot animate an entity it has never seen) and
      // is missed entirely by a late joiner, so deriving the pose from replicated
      // state is the only way every viewer converges on a prone corpse. Latched
      // once per entity; on a clientless stub bake the die clip is a dropped
      // placeholder and playAction no-ops (he lies where he stands, grey - the
      // raiseCorpse degrade).
      if (entity.thiefCorpse === true && !this.justiceCorpsePosed.has(entity.id)) {
        this.justiceCorpsePosed.add(entity.id);
        const facing = entity.position?.facing ?? Direction.S;
        const look = this.spriteFrameLooks.get(entity.id);
        if (this.atlasStore && look) {
          void this.playLoadedAction(entity.id, look, 'die-forward', () => {
            this.sprites.get(entity.id)?.playAction('die-forward', facing);
          });
        } else {
          this.sprites.get(entity.id)?.playAction('die-forward', facing);
        }
      }
      // The Bone Knight Wall training line (PRD #837 slice 3): a looping
      // ambient pose driven from the replicated `trainingLoop` component. Each
      // pass checks the per-entity schedule; when a replay is due the clip
      // plays once and the next replay is booked a period later, so the line
      // drills at its authored cadence with the authored stagger. Snapshot-
      // driven like the justice corpse above, for the same reason: a one-shot
      // broadcast at spawn is dropped before the sprite exists.
      const trainingLoop = entity.trainingLoop;
      if (trainingLoop) {
        const { play, nextAt } = trainingLoopSchedule(
          this.trainingLoopNextAt.get(entity.id),
          trainingLoop,
          performance.now(),
        );
        this.trainingLoopNextAt.set(entity.id, nextAt);
        if (play) {
          const facing = entity.position?.facing ?? Direction.S;
          const look = this.spriteFrameLooks.get(entity.id);
          if (this.atlasStore && look) {
            void this.playLoadedAction(entity.id, look, trainingLoop.clip, () => {
              this.sprites.get(entity.id)?.playAction(trainingLoop.clip, facing);
            });
          } else {
            this.sprites.get(entity.id)?.playAction(trainingLoop.clip, facing);
          }
        }
      }
      // The thief's replicated name flips to `a corpse of <name>` at the wack
      // (ADR-0194) - the one mid-life rename in the game - so his label syncs
      // from the snapshot each pass (setName is an idempotent string compare;
      // scoped to justice thieves so the rest of the crowd pays nothing).
      if (this.justiceThieves.has(entity.id)) {
        this.sprites.get(entity.id)?.setName(entity.renderable?.name);
      }
      // Achievement-title selection is live account presentation: reconcile the
      // resolved text every snapshot so nearby viewers update without a respawn.
      if (entity.horde !== true) {
        this.sprites.get(entity.id)?.setTitle(entity.renderable?.title);
      }
    }

    for (const [id, sprite] of this.sprites) {
      if (liveMobiles.has(id)) continue;
      if (this.hordeIds.has(id) && sprite.deathHeld) {
        // A slain horde enemy's despawn leaves its die-held sprite behind as a
        // client-side corpse (ADR-0304): the server removes the entity right
        // after the die beat (ARENA_ENEMY_DESPAWN_TICKS), and the frozen sprite
        // - already the exact corpse look - lies where it fell for
        // ARENA_CORPSE_TTL_MS, inert to input, under the live crowd.
        // Hit flash is live-entity feedback. The corpse no longer participates
        // in the entity loop that ages the flash machine, so clear its batched
        // whiten mix before transferring ownership or it can stay all white.
        sprite.setFlashMix(0);
        sprite.eventMode = 'none';
        sprite.cursor = 'inherit';
        sprite.zIndex -= 1;
        this.corpses.set(id, sprite);
        this.corpseTtl.set(id, ARENA_CORPSE_TTL_MS);
        this.corpseCreations += 1;
        this.enforceCorpseBudget();
        // The frozen sprite outlives its entity as a client-side corpse, so its look
        // pin transfers to the corpse lifetime (released by removeCorpse; issue #625).
        const pin = this.lookPins.get(id);
        if (pin) {
          this.corpsePins.get(id)?.();
          this.corpsePins.set(id, pin);
        }
      } else {
        this.retireSprite(sprite);
        // A despawned dead duelist takes its fallen corpse with it (ADR-0140).
        this.removeCorpse(id);
        // The departed look's atlases become evictable again (issue #625).
        this.lookPins.get(id)?.();
      }
      this.lookPins.delete(id);
      this.dungeonHealthBars.delete(id);
      this.sprites.delete(id);
      this.spriteLooks.delete(id);
      this.spriteFrameLooks.delete(id);
      this.actionLoadGeneration.delete(id);
      this.corpseLoadGeneration.delete(id);
      this.combatPrefetchLooks.delete(id);
      this.animators.delete(id);
      this.continuousAnimators.delete(id);
      // A despawned enemy takes its pooled hit-flash machine and body record
      // with it.
      this.hitFlashes.delete(id);
      this.entityBodies.delete(id);
      this.trainingLoopNextAt.delete(id);
      this.hordeIds.delete(id);
      this.banishAnchors.delete(id);
      this.livingLooks.delete(id);
      // A despawned rat leaves the war-mode target set.
      this.ratIds.delete(id);
      // A looted (or poofed-out) King's Justice thief leaves the loot set (ADR-0194).
      this.justiceThieves.delete(id);
      this.justiceCorpsePosed.delete(id);
      // A despawned player/NPC leaves the duel-target set the same way (ADR-0130).
      this.duelTargetIds.delete(id);
      this.ignoreWiredIds.delete(id);
      // Forget its viewport membership so a later re-entry re-reveals the name.
      this.inViewport.delete(id);
      // A despawn mid-stream drops the entity from the pending-swap set; a swap that
      // resolves after this is harmless (the store mutates its own records, and this
      // reconcile never looks the dead id up again). (ADR-0247)
      this.clearPending(id);
    }
  }

  /**
   * A stable signature of the render-look an entity's {@link createSprite} stack was
   * built from - its body, body hue, and the ordered worn `{id, hue}` equipment.
   * {@link reconcile} compares it frame-to-frame to detect a live outfit change and
   * rebuild the pooled sprite's layers, without rebuilding an unchanged stack.
   */
  private lookKey(entity: {
    renderable?: {
      body?: number;
      bodyHue?: number;
      equipment?: { id: string; hue?: number }[];
      scale?: number;
      mount?: number;
    };
  }): string {
    const r = entity.renderable;
    const equip = (r?.equipment ?? []).map((e) => `${e.id}:${e.hue ?? 0}`).join(',');
    // The mount is part of the look key (ADR-0442): mounting and dismounting changes
    // the layer stack (a mount layer appears/disappears) AND which locomotion clips
    // every other layer plays, so it must trigger the same in-place rebuild a body or
    // outfit change does. Without it, an admin toggling the camel would keep the old
    // stack until some unrelated look change happened to invalidate the key.
    return `${r?.body ?? ''}|${r?.bodyHue ?? ''}|${equip}|${r?.scale ?? ''}|${r?.mount ?? ''}`;
  }

  /**
   * A deep-enough copy of an entity's render-look (body + hue + worn equipment) for
   * {@link livingLooks}: the equipment array (and its `{id, hue}` entries) is cloned
   * so a later in-place mutation of the entity's renderable can't rewrite a corpse's
   * remembered outfit (corpse/ghost split, ADR-0140).
   */
  private cloneLook(renderable?: {
    body?: number;
    bodyHue?: number;
    equipment?: { id: string; hue?: number }[];
    mount?: number;
  }): PlayerLook {
    return {
      body: renderable?.body,
      bodyHue: renderable?.bodyHue,
      equipment: (renderable?.equipment ?? []).map((e) => ({ id: e.id, hue: e.hue })),
      mount: renderable?.mount,
    };
  }

  /**
   * (Re)pin the atlases an entity's current cloned look renders from (issue #625):
   * releases any previous pin for the id, then takes a fresh one on the look recorded
   * in {@link spriteFrameLooks}. A no-op without an atlas store (pre-populated
   * frame-map tests/tools).
   */
  private pinEntityLook(id: EntityId): void {
    // Retain the NEW look before releasing the old (issue #625 review): the old
    // pin's release can synchronously evict bundles the incoming look shares (a hat
    // swap keeps the shirt), which would pop the sprite undressed for a beat.
    const look = this.spriteFrameLooks.get(id);
    const pin = look ? this.atlasStore?.pinLook(look) : undefined;
    const previous = this.lookPins.get(id);
    if (pin) this.lookPins.set(id, pin);
    else this.lookPins.delete(id);
    previous?.();
  }

  /** Forget a sprite's streaming-in state (its look's atlases have all resolved). */
  private clearPending(id: EntityId): void {
    this.pendingFrames.delete(id);
    this.pendingFrameGen.delete(id);
  }

  /**
   * Drive lazy atlas streaming for one reconciled sprite (ADR-0247 / issue #474): kick
   * off any not-yet-loaded body/equipment atlas the look needs, and swap the grounded
   * fallback for the real frames as each arrives. `justRebuilt` is true when the caller
   * already (re)built this sprite's layers this pass (a fresh spawn, or a live outfit
   * change) - so the swap here only re-runs `setLayers` when a *newly arrived* atlas is
   * available, at most once per load event (guarded by the load generation), never every
   * frame. A no-op when no store is wired (tests/tools with pre-populated frame maps),
   * and it early-returns in the steady state (look fully loaded, nothing pending) so a
   * settled crowd pays nothing.
   */
  private streamFrames(
    id: EntityId,
    entity: {
      renderable?: {
        bodyHue?: number;
        body?: number;
        equipment?: { id: string; hue?: number }[];
        mount?: number;
      };
    },
    look: PlayerLook,
    justRebuilt: boolean,
  ): void {
    const store = this.atlasStore;
    if (!store) return;
    // Steady state: a settled sprite whose look was already fully loaded does no work.
    if (!justRebuilt && !this.pendingFrames.has(id)) return;
    store.requestLook(look);
    if (store.isLookLoaded(look)) {
      // Everything this look needs is now present. If it had been streaming, do a final
      // swap to the real frames (unless the caller just rebuilt it) and stop tracking it.
      if (this.pendingFrames.has(id)) {
        if (!justRebuilt) this.sprites.get(id)?.setLayers(this.layersFor(entity));
        this.clearPending(id);
      }
      return;
    }
    // Still streaming an atlas in. Track it, and on a later pass swap in whatever has
    // arrived since this sprite was last built (guarded so it rebuilds once per load).
    if (justRebuilt) {
      this.pendingFrames.add(id);
      this.pendingFrameGen.set(id, this.frameLoadGen);
    } else if ((this.pendingFrameGen.get(id) ?? -1) < this.frameLoadGen) {
      this.sprites.get(id)?.setLayers(this.layersFor(entity));
      this.pendingFrameGen.set(id, this.frameLoadGen);
    }
  }

  /**
   * Apply an admin's temporary absolute rider lifts to every visible instance of a
   * mount body.
   * This is a local visual calibration seam, not replicated gameplay state.
   */
  setMountRiderHeightOverride(
    body: number,
    override: RiderHeightOverride,
  ): void {
    this.mountRiderHeightOverrides.set(body, override);
    this.redressMountBody(body);
  }

  /** Remove an admin calibration and redraw from the registry's exact values. */
  clearMountRiderHeightOverride(body: number): void {
    this.mountRiderHeightOverrides.delete(body);
    this.redressMountBody(body);
  }

  private redressMountBody(body: number): void {
    for (const [entityId, look] of this.spriteFrameLooks) {
      if (look.mount !== body) continue;
      this.sprites
        .get(entityId)
        ?.setLayers(this.layersFor({ renderable: look }));
    }
  }

  /** Assemble the {@link SpriteLayer} stack for an entity's current render-look. */
  private layersFor(entity: {
    renderable?: {
      bodyHue?: number;
      body?: number;
      equipment?: { id: string; hue?: number }[];
      mount?: number;
    };
  }): SpriteLayer[] {
    return buildPlayerLayers(
      entity.renderable ?? {},
      this.bodyFrames,
      this.equipFrames,
      entity.renderable?.mount === undefined
        ? undefined
        : this.mountRiderHeightOverrides.get(entity.renderable.mount),
    );
  }

  /**
   * Build a mobile sprite for an entity from its renderable data: the chosen body
   * is the base layer, each equipped cosmetic (hair, …) is stacked over it, every
   * layer recoloured by its own hue, and the name floats above - so each character
   * is drawn in its own body/hue/equipment/name and the crowd is varied. An
   * unknown or absent body falls back to the default body; equipment with no baked
   * clips (an id the client never loaded) is skipped rather than crashing.
   */
  private createSprite(entity: {
    mobile?: { facing: number };
    horde?: boolean;
    renderable?: {
      bodyHue?: number;
      body?: number;
      name?: string;
      title?: string;
      equipment?: { id: string; hue?: number }[];
    };
  }): PlayerSprite {
    const facing = entity.mobile!.facing;
    // The body is layer 0 and equipment stacks over it; the shared assembly seam
    // (also used by the splash preview) owns the registry lookup, per-layer hue, and
    // gender gating, so the preview can never drift from the live result.
    const layers = this.layersFor(entity);
    if (entity.horde === true) {
      const pooled = this.freeHordeSprites.pop();
      if (pooled) {
        pooled.recycle(layers, facing);
        this.hordeSpriteReuses += 1;
        return pooled;
      }
      return new HordeSprite(layers, facing);
    }
    return new PlayerSprite(
      layers,
      facing,
      entity.renderable?.name,
      this.overheadLayer,
      entity.renderable?.title,
    );
  }
}
